import { useState, createContext, useContext, useEffect } from "react";

// ── Global navigate function — used by all components ───────────────
let _navigateFn = null;
const globalNavigate = (page, opts = {}) => { if (_navigateFn) _navigateFn(page, opts); };

// ── Mobile detection hook ────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// API CONFIG
const API = "/api";
const RAZORPAY_KEY = "rzp_live_SSEcPeupPHIz6E";

// ─── Product URL slug helper ──────────────────────────────────────────────────
const toSlug = (name) => name?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "product";
const productPath = (product) => `/products/${toSlug(product.name)}-${(product._id || product.id)?.slice(-6)}`;

// ─── Google Analytics 4 tracking ─────────────────────────────────────────────
const trackEvent = (name, params) => {
  // Google Analytics
  if (window.gtag) window.gtag("event", name, params);
  // Facebook Pixel
  if (window.fbq) {
    if (name === "add_to_cart") {
      window.fbq("track", "AddToCart", {
        content_ids: [params.items?.[0]?.item_id],
        content_name: params.items?.[0]?.item_name,
        value: params.value,
        currency: "INR",
      });
    } else if (name === "purchase") {
      window.fbq("track", "Purchase", {
        value: params.value,
        currency: "INR",
        content_ids: params.items?.map(i => i.item_id),
      });
    } else if (name === "view_item") {
      window.fbq("track", "ViewContent", {
        content_ids: [params.items?.[0]?.item_id],
        content_name: params.items?.[0]?.item_name,
        value: params.value,
        currency: "INR",
      });
    }
  }
};


// ─── API CONFIG ───────────────────────────────────────────────────────────────

// ─── PRODUCTS — always loaded from MongoDB via API ───────────────────────────
const PRODUCTS = []; // No hardcoded products — all data comes from MongoDB
const BRANDS = ["All", "Panasonic"]; // default, overridden by API
const WHATSAPP = "919361076530";

// ─── CART CONTEXT ─────────────────────────────────────────────────────────────
const CartContext = createContext();
function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("axxess_cart") || "[]"); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("axxess_wishlist") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("axxess_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem("axxess_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);
  const [toast, setToast] = useState(null);
  const [apiProducts, setApiProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [apiBrands, setApiBrands] = useState(["All", "Panasonic"]);

  useEffect(() => {
    const loadProducts = () => {
      fetch(`${API}/products`)
        .then(r => { if (!r.ok) throw new Error("API error"); return r.json(); })
        .then(data => {
          const normalized = data.map(p => ({ ...p, id: p._id }));
          setApiProducts(normalized);
          setProductsLoading(false);
        })
        .catch(() => {
          // Retry once after 2 seconds if API fails
          setTimeout(() => {
            fetch(`${API}/products`)
              .then(r => r.json())
              .then(data => {
                const normalized = data.map(p => ({ ...p, id: p._id }));
                setApiProducts(normalized);
                setProductsLoading(false);
              })
              .catch(() => {
                setApiProducts([]);
                setProductsLoading(false);
              });
          }, 2000);
        });
    };
    loadProducts();
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const addToCart = (product, qty = 1) => {
    setCart(prev => {
      const ex = prev.find(i => i.id === product.id);
      if (ex) return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { ...product, qty }];
    });
    showToast("Added to cart!");
    trackEvent("add_to_cart", { currency: "INR", value: product.price * qty, items: [{ item_id: product.id, item_name: product.name, item_brand: product.brand, price: product.price, quantity: qty }] });
  };

  const removeFromCart = (id) => setCart(prev => prev.filter(i => i.id !== id));
  const updateQty = (id, qty) => { if (qty < 1) { removeFromCart(id); return; } setCart(prev => prev.map(i => i.id === id ? { ...i, qty } : i)); };

  const toggleWishlist = (product) => {
    setWishlist(prev => {
      const ex = prev.find(i => i.id === product.id);
      showToast(ex ? "Removed from wishlist" : "Saved to wishlist!", ex ? "info" : "success");
      return ex ? prev.filter(i => i.id !== product.id) : [...prev, product];
    });
  };

  const isInWishlist = (id) => wishlist.some(i => i.id === id);
  const isInCart = (id) => cart.some(i => i.id === id);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  useEffect(() => {
    fetch(`${API}/brands`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setApiBrands(["All", ...data.filter(b => b.active).map(b => b.name)]);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <CartContext.Provider value={{ cart, wishlist, toast, addToCart, removeFromCart, updateQty, toggleWishlist, isInWishlist, isInCart, cartTotal, cartCount, apiProducts, productsLoading, apiBrands }}>
      {children}
    </CartContext.Provider>
  );
}
const useCart = () => useContext(CartContext);


// ─── AUTH CONTEXT ─────────────────────────────────────────────────────────────
const AuthContext = createContext();
const SESSION_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in ms
const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes inactivity auto logout

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("axxess_user") || "null");
      const loginTime = parseInt(localStorage.getItem("axxess_login_time") || "0");
      const lastActive = parseInt(localStorage.getItem("axxess_last_active") || "0");
      const now = Date.now();
      // Check session expiry (30 days)
      if (u && now - loginTime > SESSION_DURATION) {
        localStorage.removeItem("axxess_user");
        localStorage.removeItem("axxess_token");
        localStorage.removeItem("axxess_login_time");
        localStorage.removeItem("axxess_last_active");
        return null;
      }
      // Check inactivity (2 hours)
      if (u && lastActive && now - lastActive > INACTIVITY_LIMIT) {
        localStorage.removeItem("axxess_user");
        localStorage.removeItem("axxess_token");
        localStorage.removeItem("axxess_login_time");
        localStorage.removeItem("axxess_last_active");
        return null;
      }
      return u;
    } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem("axxess_token") || null);

  // Update last active time on user interaction
  useEffect(() => {
    if (!user) return;
    const updateActive = () => localStorage.setItem("axxess_last_active", Date.now().toString());
    const events = ["click", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, updateActive, { passive: true }));

    // Check inactivity every 5 minutes
    const interval = setInterval(() => {
      const lastActive = parseInt(localStorage.getItem("axxess_last_active") || "0");
      if (lastActive && Date.now() - lastActive > INACTIVITY_LIMIT) {
        logout();
      }
    }, 5 * 60 * 1000);

    return () => {
      events.forEach(e => window.removeEventListener(e, updateActive));
      clearInterval(interval);
    };
  }, [user]);

  const login = (userData, authToken) => {
    const now = Date.now();
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("axxess_user", JSON.stringify(userData));
    localStorage.setItem("axxess_token", authToken);
    localStorage.setItem("axxess_login_time", now.toString());
    localStorage.setItem("axxess_last_active", now.toString());
  };

  const updateUser = (updatedData) => {
    const merged = { ...user, ...updatedData };
    setUser(merged);
    localStorage.setItem("axxess_user", JSON.stringify(merged));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("axxess_user");
    localStorage.removeItem("axxess_token");
    localStorage.removeItem("axxess_login_time");
    localStorage.removeItem("axxess_last_active");
    // Clear cart and wishlist on logout
    localStorage.removeItem("axxess_cart");
    localStorage.removeItem("axxess_wishlist");
    // Reload page to reset cart state
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}
const useAuth = () => useContext(AuthContext);

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt = (n) => `Rs. ${n.toLocaleString("en-IN")}`;
const pct = (o, p) => Math.round(((o - p) / o) * 100);
const Stars = ({ rating, size = 13 }) => (
  <span style={{ fontSize: size, color: "#d97706", letterSpacing: 1 }}>
    {"★".repeat(Math.floor(rating))}{"☆".repeat(5 - Math.floor(rating))}
  </span>
);

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  bg:           "#f5f7fa",
  bgCard:       "#ffffff",
  bgElevated:   "#eef1f6",
  bgHover:      "#e8ecf4",
  border:       "#dde3ee",
  borderLight:  "#eef1f6",
  text:         "#0d1b3e",
  textSub:      "#3d4f7c",
  textMuted:    "#8b96b0",
  navy:         "#0d2461",
  navyDark:     "#060f2e",
  navyMid:      "#0f2b7a",
  navyLight:    "#1840b0",
  saffron:      "#f97316",
  saffronDark:  "#ea6c0a",
  saffronBg:    "#fff7ed",
  saffronBorder:"#fed7aa",
  sage:         "#f97316",
  sageDark:     "#ea6c0a",
  sageLight:    "#fb923c",
  sageBg:       "rgba(249,115,22,0.08)",
  sageBorder:   "rgba(249,115,22,0.25)",
  olive:        "#f97316",
  green:        "#16a34a",
  greenBg:      "#f0fdf4",
  red:          "#dc2626",
  redBg:        "#fef2f2",
  white:        "#ffffff",
  shadow:       "0 2px 12px rgba(13,36,97,0.07)",
  shadowMd:     "0 6px 24px rgba(13,36,97,0.10)",
  shadowLg:     "0 16px 48px rgba(13,36,97,0.14)",
  shadowSage:   "0 8px 32px rgba(13,36,97,0.15)",
};
// ─── GLOBAL CSS ───────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  html { scroll-behavior:smooth; }
  body { font-family:'Plus Jakarta Sans',sans-serif; background:#f0f2f8; color:#0d1b3e; -webkit-font-smoothing:antialiased; }
  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-track { background:#f5f7fa; }
  ::-webkit-scrollbar-thumb { background:#1840b0; border-radius:4px; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:translateY(0);} }
  @keyframes toastIn { from{opacity:0;transform:translateX(-50%) translateY(10px);}to{opacity:1;transform:translateX(-50%) translateY(0);} }
  .fade-up  { animation:fadeUp 0.6s cubic-bezier(0.16,1,0.3,1) both; }
  .fade-up-2{ animation:fadeUp 0.6s 0.1s cubic-bezier(0.16,1,0.3,1) both; }
  .fade-up-3{ animation:fadeUp 0.6s 0.2s cubic-bezier(0.16,1,0.3,1) both; }
  .fade-up-4{ animation:fadeUp 0.6s 0.3s cubic-bezier(0.16,1,0.3,1) both; }
  .card-hover { transition:transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s ease, border-color 0.3s ease; }
  .card-hover:hover { transform:translateY(-6px); box-shadow:0 16px 48px rgba(13,36,97,0.14); border-color:rgba(13,36,97,0.25) !important; }
  .btn-primary { display:inline-flex;align-items:center;justify-content:center;gap:8px;background:#0d2461;color:white;border:none;padding:13px 28px;border-radius:12px;font-family:'Plus Jakarta Sans',sans-serif;font-weight:700;font-size:14px;cursor:pointer;letter-spacing:0.3px;transition:all 0.25s; }
  .btn-primary:hover { background:#0f2b7a;transform:translateY(-2px);box-shadow:0 8px 24px rgba(13,36,97,0.25); }
  .btn-primary:disabled { background:#b0bcd4;cursor:not-allowed;transform:none;box-shadow:none; }
  .btn-navy { display:inline-flex;align-items:center;justify-content:center;gap:8px;background:#0d2461;color:white;border:none;padding:13px 28px;border-radius:12px;font-family:'Plus Jakarta Sans',sans-serif;font-weight:700;font-size:14px;cursor:pointer;transition:all 0.25s; }
  .btn-navy:hover { background:#0f2b7a;transform:translateY(-2px);box-shadow:0 8px 24px rgba(13,36,97,0.25); }
  .btn-navy:disabled { background:#b0bcd4;cursor:not-allowed;transform:none; }
  .btn-saffron { display:inline-flex;align-items:center;justify-content:center;gap:8px;background:#f97316;color:white;border:none;padding:13px 28px;border-radius:12px;font-family:'Plus Jakarta Sans',sans-serif;font-weight:700;font-size:14px;cursor:pointer;transition:all 0.25s; }
  .btn-saffron:hover { background:#ea6c0a;transform:translateY(-2px);box-shadow:0 8px 24px rgba(249,115,22,0.25); }
  .btn-outline { display:inline-flex;align-items:center;justify-content:center;gap:8px;background:transparent;color:#0d2461;border:1.5px solid #0d2461;padding:12px 26px;border-radius:12px;font-family:'Plus Jakarta Sans',sans-serif;font-weight:700;font-size:14px;cursor:pointer;transition:all 0.25s; }
  .btn-outline:hover { background:#0d2461;color:white;transform:translateY(-1px); }
  .btn-ghost { background:none;border:none;cursor:pointer;font-family:'Plus Jakarta Sans',sans-serif; }
  input,select,textarea { font-family:'Plus Jakarta Sans',sans-serif;outline:none; }
  input:focus,select:focus,textarea:focus { border-color:#0d2461!important;box-shadow:0 0 0 3px rgba(13,36,97,0.1)!important; }
  .section-label { font-size:10px;font-weight:700;color:#f97316;text-transform:uppercase;letter-spacing:5px;margin-bottom:10px;display:block;font-family:'Plus Jakarta Sans',sans-serif; }

  /* ── MOBILE RESPONSIVE ─────────────────────────────────────────── */
  @media (max-width: 768px) {
    .nav-main { padding:0 16px !important; height:60px !important; gap:12px !important; }
    .nav-search { display:none !important; }
    .nav-links { display:none !important; }
    .nav-icons { gap:0 !important; }
    .nav-icons button { padding:6px 8px !important; }
    .nav-icons span { font-size:9px !important; }
    .nav-promo { font-size:10px !important; padding:6px 12px !important; line-height:1.5 !important; }
    .nav-main > div:first-child div:last-child > div:first-child { font-size:20px !important; }
    .hero-grid { grid-template-columns:1fr !important; gap:0 !important; padding:28px 16px 24px !important; }
    .hero-cards { display:none !important; }
    .hero-deco { display:none !important; }
    .hero-h1 { font-size:26px !important; letter-spacing:-0.5px !important; line-height:1.2 !important; }
    .hero-btns { flex-wrap:wrap !important; gap:8px !important; }
    .hero-stats { flex-wrap:wrap !important; gap:0 !important; margin-left:0 !important; padding-left:0 !important; border-left:none !important; padding-top:12px !important; border-top:1px solid rgba(255,255,255,0.1) !important; margin-top:4px !important; width:100% !important; justify-content:space-around !important; }
    .trust-grid { grid-template-columns:repeat(3,1fr) !important; }
    .trust-item { padding:8px 6px !important; border-right:none !important; border-bottom:1px solid #eef1f6 !important; }
    .products-grid { grid-template-columns:1fr 1fr !important; gap:10px !important; padding:0 !important; }
    .products-grid > div { font-size:12px !important; }
    .card-content { padding:8px 10px 10px !important; }
    .card-content h3 { font-size:12px !important; min-height:44px !important; }
    .page-container { padding:90px 12px 80px !important; }
    .brand-pills { overflow-x:auto !important; flex-wrap:nowrap !important; padding-bottom:6px !important; -webkit-overflow-scrolling:touch !important; scrollbar-width:none !important; }
    .brand-pills::-webkit-scrollbar { display:none !important; }
    .compat-filter { flex-direction:column !important; align-items:flex-start !important; gap:10px !important; }
    .compat-filter select { width:100% !important; min-width:unset !important; }
    .sort-row { flex-wrap:wrap !important; gap:8px !important; }
    .why-grid { grid-template-columns:1fr 1fr !important; gap:10px !important; }
    .footer-grid { grid-template-columns:1fr 1fr !important; gap:16px !important; }
    .footer-main { padding:32px 16px !important; }
    .cart-grid { grid-template-columns:1fr !important; gap:16px !important; }
    .checkout-grid { grid-template-columns:1fr !important; gap:16px !important; }
    .address-grid { grid-template-columns:1fr !important; gap:10px !important; }
    .payment-grid { grid-template-columns:1fr 1fr !important; gap:8px !important; }
    .detail-grid { grid-template-columns:1fr !important; gap:16px !important; padding:16px !important; }
    .detail-tabs { overflow-x:auto !important; flex-wrap:nowrap !important; -webkit-overflow-scrolling:touch !important; scrollbar-width:none !important; }
    .detail-tabs::-webkit-scrollbar { display:none !important; }
    .detail-tab-btn { white-space:nowrap !important; padding:8px 12px !important; font-size:12px !important; }
    .contact-grid { grid-template-columns:1fr !important; }
    .brands-strip { flex-wrap:wrap !important; gap:4px !important; padding:8px 16px !important; }
    .brand-pill { padding:6px 14px !important; font-size:12px !important; }
    .admin-sidebar { width:0 !important; overflow:hidden !important; }
    .admin-content { margin-left:0 !important; padding:16px !important; }
    .stats-grid { grid-template-columns:1fr 1fr !important; gap:10px !important; }
  }

  @media (max-width: 480px) {
    .products-grid { grid-template-columns:1fr 1fr !important; gap:8px !important; }
    .trust-grid { grid-template-columns:repeat(2,1fr) !important; }
    .hero-h1 { font-size:22px !important; }
    .footer-grid { grid-template-columns:1fr !important; }
    .nav-promo { font-size:9.5px !important; }
    .page-container { padding:85px 10px 60px !important; }
    .why-grid { grid-template-columns:1fr !important; }
    .payment-grid { grid-template-columns:1fr !important; }
  }
`;

// ─── NAV AUTH BUTTON ─────────────────────────────────────────────────────────
function NavAuthButton({ setPage }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (user) return (
    <div style={{ position: "relative" }}>
      <button className="btn-ghost" onClick={() => setOpen(!open)}
        style={{ padding: "8px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: 1, borderRadius: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "white" }}>
          {user.name ? user.name.charAt(0).toUpperCase() : user.phone.slice(-2)}
        </div>
        <span style={{ fontSize: 9, color: C.textMuted, fontWeight: 600 }}>Account</span>
      </button>
      {open && (
        <div style={{ position: "absolute", right: 0, top: "100%", background: "white", borderRadius: 10, boxShadow: C.shadowMd, border: `1px solid ${C.border}`, minWidth: 160, zIndex: 999, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{user.name || "Customer"}</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>+91 {user.phone}</div>
          </div>
          <button onClick={() => { setOpen(false); setPage("account"); }}
            style={{ width: "100%", padding: "10px 16px", textAlign: "left", background: "none", border: "none", fontSize: 13, fontWeight: 600, color: C.text, cursor: "pointer", fontFamily: "'Plus Jakarta Sans'" }}>
            📦 My Orders
          </button>
          <button onClick={() => { logout(); setOpen(false); }}
            style={{ width: "100%", padding: "10px 16px", textAlign: "left", background: "none", border: "none", fontSize: 13, fontWeight: 600, color: C.red, cursor: "pointer", fontFamily: "'Plus Jakarta Sans'" }}>
            🚪 Logout
          </button>
        </div>
      )}
    </div>
  );

  return (
    <button className="btn-ghost" onClick={() => setPage("login")}
      style={{ padding: "8px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: 1, borderRadius: 8 }}>
      <span style={{ fontSize: 18 }}>👤</span>
      <span style={{ fontSize: 9, color: C.textMuted, fontWeight: 600 }}>Login</span>
    </button>
  );
}

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
function Navbar({ page, setPage, searchQuery, setSearchQuery, activeBrand, setActiveBrand }) {
  const { cartCount, wishlist, apiBrands } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const isMobile = useIsMobile();
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:1000, boxShadow: scrolled ? C.shadowMd : "none", transition:"all 0.3s" }}>
      {/* Promo bar — sliding ticker */}
      <div className="nav-promo" style={{ background:"linear-gradient(90deg, #040d24 0%, #0d2461 50%, #040d24 100%)", borderBottom:"2px solid rgba(249,115,22,0.5)", overflow:"hidden", position:"relative", height:38 }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 50% 50%, rgba(249,115,22,0.1) 0%, transparent 70%)", pointerEvents:"none" }} />
        {/* Left fade */}
        <div style={{ position:"absolute", left:0, top:0, bottom:0, width:80, background:"linear-gradient(90deg, #040d24, transparent)", zIndex:2, pointerEvents:"none" }} />
        {/* Right fade */}
        <div style={{ position:"absolute", right:0, top:0, bottom:0, width:80, background:"linear-gradient(270deg, #040d24, transparent)", zIndex:2, pointerEvents:"none" }} />
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center" }}>
          <style>{`
            @keyframes ticker {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .ticker-track { display:flex; gap:0; animation: ticker 28s linear infinite; white-space:nowrap; will-change:transform; }
            .ticker-track:hover { animation-play-state: paused; }
          `}</style>
          <div className="ticker-track">
            {[
              { icon:"⚡", t:"Free & Fast Delivery on All Orders" },
              { icon:"🛡️", t:"1-Year Warranty on All Models" },
              { icon:"✅", t:"100% Genuine Products" },
              { icon:"🔁", t:"7-Day Easy Returns" },
              { icon:"🔒", t:"100% Secure Payments" },
              { icon:"📞", t:"+91 93610 76530" },
              { icon:"⚡", t:"Free & Fast Delivery on All Orders" },
              { icon:"🛡️", t:"1-Year Warranty on All Models" },
              { icon:"✅", t:"100% Genuine Products" },
              { icon:"🔁", t:"7-Day Easy Returns" },
              { icon:"🔒", t:"100% Secure Payments" },
              { icon:"📞", t:"+91 93610 76530" },
            ].map(({icon,t},i) => (
              <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:8, fontSize:11.5, fontWeight:700, color:"rgba(255,255,255,0.92)", letterSpacing:0.4, padding:"0 28px" }}>
                <span style={{ color:"#f97316", fontSize:13, filter:"drop-shadow(0 0 5px rgba(249,115,22,0.9))" }}>{icon}</span>
                {t}
                <span style={{ color:"rgba(249,115,22,0.6)", margin:"0 4px", fontSize:16, lineHeight:1 }}>·</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div style={{ background:"white", borderBottom:`1px solid rgba(13,36,97,0.08)`, boxShadow:"0 1px 0 rgba(13,36,97,0.04)" }}>
        <div className="nav-main" style={{ maxWidth:1280, margin:"0 auto", padding: isMobile ? "0 12px" : "0 32px", height: isMobile ? 60 : 68, display:"flex", alignItems:"center", gap: isMobile ? 8 : 24 }}>

          {/* Logo */}
          <div onClick={() => setPage("home")} style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
            {/* Icon box */}
            <div style={{ width:46, height:46, background:"linear-gradient(145deg, #060f2e 0%, #0d2461 100%)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 6px 20px rgba(13,36,97,0.5)", flexShrink:0 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <polygon points="15,2 5,13 11,13 9,22 19,11 13,11" fill="#f97316"/>
                <polygon points="15,2 11,13 13,11" fill="#fde68a"/>
              </svg>
            </div>
            {/* Wordmark */}
            <div style={{ position:"relative", display:"inline-flex", alignItems:"center", lineHeight:1 }}>
              {/* a — navy */}
              <span style={{ fontFamily:"'Plus Jakarta Sans'", fontWeight:900, fontSize:30, letterSpacing:"-1.5px", color:"#060f2e", lineHeight:1 }}>a</span>
              {/* xx — orange gradient with light streak */}
              <span style={{ position:"relative", display:"inline-block", lineHeight:1 }}>
                <span style={{ fontFamily:"'Plus Jakarta Sans'", fontWeight:900, fontSize:30, letterSpacing:"-1.5px", background:"linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #ea580c 100%)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", lineHeight:1 }}>xx</span>
                <svg style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", pointerEvents:"none", overflow:"hidden" }} viewBox="0 0 46 34">
                  <polygon points="6,2 14,2 28,32 20,32" fill="#fde68a" opacity="0.45"/>
                  <polygon points="18,2 22,2 32,32 28,32" fill="white" opacity="0.2"/>
                </svg>
              </span>
              {/* ess — navy, zero gap */}
              <span style={{ fontFamily:"'Plus Jakarta Sans'", fontWeight:900, fontSize:30, letterSpacing:"-1.5px", color:"#060f2e", lineHeight:1 }}>ess</span>
            </div>
          </div>

          {/* Search */}
          {!isMobile && <div className="nav-search" style={{ flex:1, maxWidth:500, position:"relative" }}>
            <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); globalNavigate("products"); }}
              placeholder="Search mixer grinders, brands..."
              style={{ width:"100%", padding:"11px 96px 11px 44px", background:"#f5f7fc", border:"1.5px solid #e2e8f4", borderRadius:50, fontSize:13.5, color:C.text, fontFamily:"'Plus Jakarta Sans'" }} />
            <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:C.textMuted, fontSize:14 }}>🔍</span>
            <button onClick={() => globalNavigate("products")} style={{ position:"absolute", right:4, top:4, bottom:4, background:C.navy, color:"white", border:"none", borderRadius:50, padding:"0 18px", fontFamily:"'Plus Jakarta Sans'", fontWeight:700, fontSize:12, cursor:"pointer", letterSpacing:"0.3px" }}>Search</button>
          </div>}

          {/* Nav links */}
          {!isMobile && <div className="nav-links" style={{ display:"flex", alignItems:"center", gap:2 }}>
            {[{l:"Home",k:"home"},{l:"Products",k:"products"},{l:"Contact",k:"contact"}].map(({l,k}) => (
              <button key={k} className="btn-ghost" onClick={() => setPage(k)} style={{ padding:"8px 14px", fontSize:13.5, fontWeight: page===k ? 700 : 500, color: page===k ? C.navy : C.textSub, borderBottom:`2px solid ${page===k ? C.saffron : "transparent"}`, borderRadius:0, transition:"all 0.2s" }}>{l}</button>
            ))}
          </div>}

          {/* Icons */}
          <div className="nav-icons" style={{ display:"flex", alignItems:"center", gap:2, flexShrink:0, marginLeft:"auto" }}>
            {isMobile && (
              <button className="btn-ghost" onClick={() => { globalNavigate("products"); }} style={{ padding:"8px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:1, borderRadius:8 }}>
                <span style={{ fontSize:18 }}>🔍</span>
                <span style={{ fontSize:9, color:C.textMuted, fontWeight:600 }}>Search</span>
              </button>
            )}
            <NavAuthButton setPage={setPage} />
            <button className="btn-ghost" onClick={() => setPage("wishlist")} style={{ position:"relative", padding:"8px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:1, borderRadius:8, transition:"background 0.2s" }}
              onMouseOver={e=>e.currentTarget.style.background=C.bgElevated}
              onMouseOut={e=>e.currentTarget.style.background="none"}>
              <span style={{ fontSize:18 }}>{wishlist.length > 0 ? "❤️" : "🤍"}</span>
              <span style={{ fontSize:9, color:C.textMuted, fontWeight:600 }}>Wishlist</span>
              {wishlist.length > 0 && <span style={{ position:"absolute", top:4, right:4, background:C.saffron, color:"white", fontSize:8, fontWeight:800, borderRadius:"50%", width:14, height:14, display:"flex", alignItems:"center", justifyContent:"center" }}>{wishlist.length}</span>}
            </button>
            <button className="btn-ghost" onClick={() => setPage("cart")} style={{ position:"relative", padding:"8px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:1, borderRadius:8, transition:"background 0.2s" }}
              onMouseOver={e=>e.currentTarget.style.background=C.bgElevated}
              onMouseOut={e=>e.currentTarget.style.background="none"}>
              <span style={{ fontSize:18 }}>🛒</span>
              <span style={{ fontSize:9, color:C.textMuted, fontWeight:600 }}>Cart</span>
              {cartCount > 0 && <span style={{ position:"absolute", top:4, right:4, background:C.saffron, color:"white", fontSize:8, fontWeight:800, borderRadius:"50%", width:14, height:14, display:"flex", alignItems:"center", justifyContent:"center" }}>{cartCount}</span>}
            </button>
          </div>

        </div>
      </div>

      {/* Brand strip */}
      <div style={{ background:"linear-gradient(90deg, #060f2e, #0d2461)", borderTop:"1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ maxWidth:1280, margin:"0 auto", padding: isMobile ? "0 12px" : "0 32px", display:"flex", alignItems:"center", overflowX: isMobile ? "auto" : "visible", flexWrap: isMobile ? "nowrap" : "wrap", scrollbarWidth:"none" }}>
          {apiBrands.map((label) => {
            const isActive = activeBrand === label;
            return (
              <button key={label} onClick={() => { setActiveBrand(label); setPage("home"); }} style={{ background:"none", border:"none", borderBottom:`2px solid ${isActive ? C.saffron : "transparent"}`, color: isActive ? "white" : "rgba(255,255,255,0.65)", fontFamily:"'Plus Jakarta Sans'", fontWeight: isActive ? 700 : 500, fontSize: isMobile ? 14 : 13, padding: isMobile ? "13px 18px" : "11px 20px", cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.2s", flexShrink:0 }}
                onMouseOver={e=>{e.currentTarget.style.color="white"; e.currentTarget.style.borderBottomColor=C.saffron;}}
                onMouseOut={e=>{ if(!isActive){e.currentTarget.style.color="rgba(255,255,255,0.65)"; e.currentTarget.style.borderBottomColor="transparent";}}}
              >{label}</button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
function Toast() {
  const { toast } = useCart();
  if (!toast) return null;
  return (
    <div style={{
      position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
      zIndex: 9999, background: toast.type === "success" ? C.green : C.navy,
      color: "white", padding: "12px 28px", borderRadius: 10,
      fontWeight: 600, fontSize: 14, animation: "toastIn 0.3s ease",
      boxShadow: C.shadowMd, whiteSpace: "nowrap",
    }}>
      {toast.type === "success" ? "✓ " : "ℹ "}{toast.msg}
    </div>
  );
}

// ─── PRODUCT CARD ─────────────────────────────────────────────────────────────
function ProductCard({ product, setPage, setSelectedProduct }) {
  const { addToCart, toggleWishlist, isInWishlist, isInCart } = useCart();
  const inW = isInWishlist(product.id);
  const inC = isInCart(product.id);
  const disc = product.originalPrice > product.price ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100) : 0;
  const [hovered, setHovered] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background:"white",
        borderRadius:16,
        overflow:"hidden",
        border:`1.5px solid ${hovered ? "#f97316" : C.border}`,
        position:"relative",
        display:"flex",
        flexDirection:"column",
        boxShadow: hovered ? "0 12px 40px rgba(249,115,22,0.18)" : "0 2px 12px rgba(13,36,97,0.07)",
        transition:"all 0.3s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
      }}>

      {/* Top accent line — shows on hover */}
      <div style={{ height:3, background: hovered ? "linear-gradient(90deg, #f97316, #fbbf24, #f97316)" : "transparent", transition:"all 0.3s" }} />

      {/* Discount badge */}
      {disc > 0 && (
        <div style={{ position:"absolute", top:14, left:14, zIndex:4, background:"linear-gradient(135deg, #f97316, #ea6c0a)", color:"white", fontSize:10, fontWeight:800, padding:"4px 10px", borderRadius:20, boxShadow:"0 3px 10px rgba(249,115,22,0.4)", letterSpacing:0.5 }}>
          {disc}% OFF
        </div>
      )}

      {/* Wishlist button */}
      <button onClick={() => toggleWishlist(product)} style={{ position:"absolute", top:12, right:12, zIndex:4, background:"white", border:`1.5px solid ${inW ? "#f97316" : C.border}`, borderRadius:"50%", width:32, height:32, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.1)", transition:"all 0.2s" }}>
        {inW ? "❤️" : "🤍"}
      </button>

      {/* Out of stock overlay */}
      {!product.inStock && (
        <div style={{ position:"absolute", inset:0, background:"rgba(255,255,255,0.88)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:3, borderRadius:16 }}>
          <span style={{ background:C.navy, color:"white", padding:"7px 18px", borderRadius:8, fontWeight:700, fontSize:12, letterSpacing:1 }}>Out of Stock</span>
        </div>
      )}

      {/* Image */}
      <div onClick={() => { globalNavigate("detail", { selected: product }); }}
        style={{ cursor:"pointer", height: isMobile ? 140 : 200, background:"linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)", overflow:"hidden", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", padding: isMobile ? "10px" : "16px" }}>
        <img src={product.image} alt={product.name}
          style={{ width:"100%", height:"100%", objectFit:"contain", transition:"transform 0.4s ease", transform: hovered ? "scale(1.08)" : "scale(1)" }} />
      </div>

      {/* Content */}
      <div style={{ padding: isMobile ? "10px 10px 12px" : "14px 16px 16px", display:"flex", flexDirection:"column", flex:1 }}>

        {/* Badge row */}
        <div style={{ height:20, display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          {product.badge
            ? <span style={{ fontSize:9, fontWeight:800, color:"#f97316", textTransform:"uppercase", letterSpacing:2, background:"rgba(249,115,22,0.08)", padding:"2px 8px", borderRadius:10, border:"1px solid rgba(249,115,22,0.2)" }}>{product.badge}</span>
            : <span/>}
          {disc > 0 && <span style={{ fontSize:9, fontWeight:800, color:"white", background:"linear-gradient(135deg,#f97316,#ea580c)", padding:"2px 8px", borderRadius:10 }}>{disc}% OFF</span>}
        </div>

        {/* Brand */}
        <span style={{ fontSize:9, color:C.textMuted, fontWeight:700, textTransform:"uppercase", letterSpacing:2, marginBottom:6 }}>{product.brand}</span>

        {/* Title — bigger, bolder, two-tone on hover */}
        <h3 onClick={() => { globalNavigate("detail", { selected: product }); }}
          style={{ fontSize:14.5, fontWeight:800, color:"#060f2e", cursor:"pointer", lineHeight:1.45, fontFamily:"'Plus Jakarta Sans'", height:64, overflow:"hidden", marginBottom:10, transition:"color 0.2s", letterSpacing:"-0.3px" }}
          onMouseOver={e=>e.currentTarget.style.color="#f97316"}
          onMouseOut={e=>e.currentTarget.style.color="#060f2e"}>
          {product.name}
        </h3>

        {/* Spec tags */}
        <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:12 }}>
          {[product.wattage&&`${product.wattage}W`, product.jars&&`${product.jars} Jars`, product.warranty].filter(Boolean).map(tag=>(
            <span key={tag} style={{ fontSize:10, background:"#f0f4ff", color:C.navy, padding:"3px 10px", borderRadius:20, fontWeight:600, border:"1px solid #dde6ff", whiteSpace:"nowrap" }}>{tag}</span>
          ))}
        </div>

        {/* Price — big punchy treatment */}
        <div style={{ marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
            <span style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:26, fontWeight:900, color:"#060f2e", letterSpacing:"-1px", lineHeight:1 }}>
              {fmt(product.price)}
            </span>
            {product.originalPrice > product.price && (
              <span style={{ fontSize:13, color:"#94a3b8", textDecoration:"line-through", fontWeight:500 }}>{fmt(product.originalPrice)}</span>
            )}
          </div>
          {disc > 0 && (
            <div style={{ fontSize:11, fontWeight:700, color:"#16a34a", marginTop:3 }}>
              You save {fmt(product.originalPrice - product.price)} 🎉
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display:"flex", gap:8, marginTop:"auto" }}>
          <button disabled={!product.inStock} onClick={() => product.inStock && addToCart(product)}
            style={{ flex:1, padding:"11px", borderRadius:10, background: inC ? "#16a34a" : "linear-gradient(135deg, #0d2461, #1535a0)", color:"white", border:"none", fontFamily:"'Plus Jakarta Sans'", fontWeight:700, fontSize:13, cursor: product.inStock ? "pointer" : "not-allowed", opacity: product.inStock ? 1 : 0.4, transition:"all 0.2s", boxShadow: inC ? "0 4px 12px rgba(22,163,74,0.3)" : "0 4px 12px rgba(13,36,97,0.25)" }}>
            {inC ? "✓ Added" : "Add to Cart"}
          </button>
          <button onClick={() => { globalNavigate("detail", { selected: product }); }}
            style={{ padding:"11px 16px", background:"white", border:`1.5px solid ${C.border}`, borderRadius:10, cursor:"pointer", fontSize:12, fontWeight:700, color:C.navy, fontFamily:"'Plus Jakarta Sans'", transition:"all 0.2s" }}
            onMouseOver={e=>{e.currentTarget.style.background="#f0f4ff"; e.currentTarget.style.borderColor=C.navy;}}
            onMouseOut={e=>{e.currentTarget.style.background="white"; e.currentTarget.style.borderColor=C.border;}}>
            View
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── HOME PAGE ────────────────────────────────────────────────────────────────
function HeroSlider() {
  const lines = [
    { icon:"⚡", text:"Premium Mixer Accessories", orange:"Accessories" },
    { icon:"✅", text:"100% Genuine Panasonic Products", orange:"Genuine" },
    { icon:"🏆", text:"Authorized Panasonic Accessories Store", orange:"Authorized" },
    { icon:"🛡️", text:"1-Year Warranty on All Models", orange:"1-Year Warranty" },
    { icon:"🚚", text:"Free & Fast Delivery on All Orders", orange:"Free & Fast Delivery" },
  ];
  const [current, setCurrent] = useState(0);
  const [phase, setPhase] = useState("visible"); // "slide-in" | "visible" | "slide-out"

  useEffect(() => {
    const cycle = () => {
      // Slide out to right
      setPhase("slide-out");
      setTimeout(() => {
        // Jump to next, start from left (hidden)
        setCurrent(prev => (prev + 1) % lines.length);
        setPhase("slide-in");
        setTimeout(() => {
          // Settle into view
          setPhase("visible");
        }, 50);
      }, 500);
    };
    const timer = setInterval(cycle, 2800);
    return () => clearInterval(timer);
  }, []);

  const getTransform = () => {
    if (phase === "slide-in")  return "translateX(100%)";
    if (phase === "slide-out") return "translateX(-100%)";
    return "translateX(0%)";
  };

  const getOpacity = () => {
    if (phase === "visible") return 1;
    return 0;
  };

  const line = lines[current];

  // Render text with orange highlight word
  const renderText = () => {
    const parts = line.text.split(line.orange);
    return (
      <span>
        {parts[0]}
        <span style={{ color:"#f97316" }}>{line.orange}</span>
        {parts[1]}
      </span>
    );
  };

  return (
    <div style={{ width:"100%", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", gap:0, padding:"0" }}>

      {/* LEFT DECORATION — hidden on mobile */}
      <div className="hero-deco" style={{ flexShrink:0, display:"flex", alignItems:"center", gap:14, padding:"0 36px", opacity:0.75 }}>
        <span style={{ fontSize:52, filter:"drop-shadow(0 0 16px rgba(249,115,22,1))" }}>⚡</span>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ width:52, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
          <div style={{ width:38, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
          <div style={{ width:24, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
        </div>
        <span style={{ fontSize:34, filter:"drop-shadow(0 0 12px rgba(249,115,22,1))" }}>⚡</span>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ width:52, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
          <div style={{ width:38, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
          <div style={{ width:24, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
        </div>
      </div>

      {/* SLIDING TEXT */}
      <div style={{ flex:1, overflow:"hidden", padding:"0 12px" }}>
        <div style={{
          display:"flex",
          alignItems:"center",
          justifyContent:"center",
          gap:12,
          transform: getTransform(),
          opacity: getOpacity(),
          transition: phase === "slide-in" ? "none" : "transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease",
        }}>
          <span style={{ fontSize:"clamp(28px, 5vw, 52px)", lineHeight:1, filter:"drop-shadow(0 0 12px rgba(249,115,22,0.7))", flexShrink:0 }}>
            {line.icon}
          </span>
          <h1 style={{
            fontFamily:"'Plus Jakarta Sans'",
            fontSize:"clamp(18px, 4vw, 52px)",
            fontWeight:800,
            color:"white",
            margin:0,
            lineHeight:1.2,
            letterSpacing:"-1px",
            whiteSpace:"normal",
            textAlign:"center",
          }}>
            {renderText()}
          </h1>
        </div>
      </div>

      {/* RIGHT DECORATION — hidden on mobile */}
      <div className="hero-deco" style={{ flexShrink:0, display:"flex", alignItems:"center", gap:14, padding:"0 36px", opacity:0.75, transform:"scaleX(-1)" }}>
        <span style={{ fontSize:52, filter:"drop-shadow(0 0 16px rgba(249,115,22,1))" }}>⚡</span>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ width:52, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
          <div style={{ width:38, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
          <div style={{ width:24, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
        </div>
        <span style={{ fontSize:34, filter:"drop-shadow(0 0 12px rgba(249,115,22,1))" }}>⚡</span>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ width:52, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
          <div style={{ width:38, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
          <div style={{ width:24, height:4, borderRadius:2, background:"linear-gradient(90deg, transparent, #f97316)" }}/>
        </div>
      </div>

    </div>
  );
}

function HomePage({ setPage, setSelectedProduct, activeBrand, setActiveBrand }) {
  const { apiProducts, productsLoading, apiBrands } = useCart();
  const isMobile = useIsMobile();
  const allProducts = apiProducts;
  const filteredByBrand = activeBrand === "All" ? allProducts : allProducts.filter(p => p.brand === activeBrand);

  return (
    <div style={{ paddingTop:152, background:C.bg }}>

      {/* HERO — full width big text slider */}
      <div style={{ background:"linear-gradient(135deg, #040d24 0%, #0a1f5e 40%, #0d2461 65%, #1535a0 100%)", position:"relative", overflow:"hidden" }}>

        {/* Dot grid background */}
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)", backgroundSize:"28px 28px", pointerEvents:"none" }} />

        {/* TOP — glowing orange accent line */}
        <div style={{ width:"100%", height:3, background:"linear-gradient(90deg, transparent 0%, #f97316 30%, #fbbf24 50%, #f97316 70%, transparent 100%)", boxShadow:"0 0 18px 4px rgba(249,115,22,0.5)" }} />

        {/* TOP BAR — India badge + tagline */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:32, padding:"18px 48px 0", position:"relative", zIndex:1 }}>
          <div style={{ height:1, flex:1, background:"linear-gradient(90deg, transparent, rgba(249,115,22,0.3))" }} />
          <div style={{ display:"inline-flex", alignItems:"center", gap:10, background:"rgba(249,115,22,0.12)", border:"1px solid rgba(249,115,22,0.4)", borderRadius:24, padding:"7px 20px" }}>
            <span style={{ width:7, height:7, borderRadius:"50%", background:"#f97316", display:"inline-block", boxShadow:"0 0 8px rgba(249,115,22,0.9)", animation:"pulse 1.5s infinite" }} />
            <span style={{ color:"#fdba74", fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:"uppercase", fontFamily:"'Plus Jakarta Sans'" }}>India's #1 Mixer Accessories Store</span>
          </div>
          <div style={{ height:1, flex:1, background:"linear-gradient(90deg, rgba(249,115,22,0.3), transparent)" }} />
        </div>

        {/* SLIDER */}
        <div style={{ position:"relative", zIndex:1, padding:"38px 0 100px" }}>
          <HeroSlider />
        </div>

        {/* BOTTOM — glowing orange accent line */}
        <div style={{ width:"100%", height:3, background:"linear-gradient(90deg, transparent 0%, #f97316 30%, #fbbf24 50%, #f97316 70%, transparent 100%)", boxShadow:"0 0 18px 4px rgba(249,115,22,0.5)" }} />

      </div>

      {/* TRUST BAR — premium dark style */}
      <div style={{ background:"#060f2e", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ maxWidth:1280, margin:"0 auto", padding:"0 32px" }}>
          <div className="trust-grid" style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)" }}>
            {[
              {icon:"🚚", t:"Free Delivery", s:"On all orders"},
              {icon:"🔒", t:"Secure Payment", s:"100% encrypted"},
              {icon:"🔁", t:"Easy Returns", s:"7-day hassle free"},
              {icon:"✅", t:"100% Genuine", s:"Authorized dealer"},
              {icon:"📞", t:"24/7 Support", s:"+91 93610 76530"},
              {icon:"⚡", t:"Fast Dispatch", s:"Ships in 24 hours"},
            ].map(({icon,t,s},i) => (
              <div key={t} className="trust-item" style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 12px", borderRight: i<5 ? "1px solid rgba(255,255,255,0.06)" : "none", transition:"background 0.2s" }}
                onMouseOver={e=>e.currentTarget.style.background="rgba(249,115,22,0.07)"}
                onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                <div style={{ width:34, height:34, background:"rgba(249,115,22,0.12)", border:"1px solid rgba(249,115,22,0.25)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>{icon}</div>
                <div>
                  <div style={{ fontSize:11.5, fontWeight:700, color:"rgba(255,255,255,0.85)" }}>{t}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:1 }}>{s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PRODUCTS SECTION */}
      <div style={{ padding: isMobile ? "28px 0 40px" : "48px 0 60px", background:"linear-gradient(180deg, #f0f2f8 0%, #e8ecf5 100%)" }}>
        <div style={{ maxWidth:1280, margin:"0 auto", padding: isMobile ? "0 12px" : "0 32px" }}>

          {/* Header row */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:24 }}>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ width:5, height:48, borderRadius:3, background:"linear-gradient(180deg, #f97316, #fbbf24)", boxShadow:"0 0 14px rgba(249,115,22,0.5)" }} />
              <div>
                <span style={{ fontSize:10, fontWeight:800, color:"#f97316", letterSpacing:3, textTransform:"uppercase", fontFamily:"'Plus Jakarta Sans'" }}>
                  {activeBrand === "All" ? "Handpicked For You" : `Brand — ${activeBrand}`}
                </span>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize: isMobile ? 22 : 28, fontWeight:900, color:C.navy, margin:"3px 0 0", letterSpacing:"-0.8px" }}>
                  {activeBrand === "All" ? "Featured Products" : `${activeBrand} Products`}
                </h2>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              {activeBrand !== "All" && (
                <button onClick={() => setActiveBrand("All")} style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, padding:"7px 14px", fontSize:11.5, fontWeight:600, color:C.textSub, cursor:"pointer", fontFamily:"'Plus Jakarta Sans'" }}>✕ Clear</button>
              )}
              <button onClick={() => globalNavigate("products")} style={{ padding:"9px 22px", fontSize:12, fontWeight:800, borderRadius:10, background:C.navy, color:"white", border:"none", cursor:"pointer", fontFamily:"'Plus Jakarta Sans'", boxShadow:"0 4px 14px rgba(13,36,97,0.25)", transition:"all 0.2s" }}
                onMouseOver={e=>e.currentTarget.style.background="#1535a0"}
                onMouseOut={e=>e.currentTarget.style.background=C.navy}>
                View All →
              </button>
            </div>
          </div>

          {/* Brand pills */}
          <div style={{ display:"flex", gap:8, marginBottom:28, flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : "visible", paddingBottom: isMobile ? 6 : 0, scrollbarWidth:"none" }}>
            {apiBrands.map(b => (
              <button key={b} onClick={() => setActiveBrand(b)} style={{
                padding: isMobile ? "10px 20px" : "8px 22px", borderRadius:24,
                border:`1.5px solid ${activeBrand===b ? "#f97316" : "rgba(13,36,97,0.15)"}`,
                background: activeBrand===b ? "linear-gradient(135deg, #f97316, #ea6c0a)" : "white",
                color: activeBrand===b ? "white" : C.navy,
                fontFamily:"'Plus Jakarta Sans'", fontWeight:700, fontSize: isMobile ? 14 : 12.5,
                cursor:"pointer", transition:"all 0.2s", flexShrink:0,
                boxShadow: activeBrand===b ? "0 6px 18px rgba(249,115,22,0.35)" : "0 2px 6px rgba(13,36,97,0.08)",
              }}
                onMouseOver={e=>{ if(activeBrand!==b){ e.currentTarget.style.borderColor="#f97316"; e.currentTarget.style.color="#f97316"; }}}
                onMouseOut={e=>{ if(activeBrand!==b){ e.currentTarget.style.borderColor="rgba(13,36,97,0.15)"; e.currentTarget.style.color=C.navy; }}}
              >{b}</button>
            ))}
          </div>

          {/* Products grid */}
          {filteredByBrand.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:C.textMuted }}>
              <div style={{ fontSize:36, marginBottom:10 }}>🔍</div>
              <p style={{ fontSize:14 }}>No products found for {activeBrand}</p>
            </div>
          ) : (
            <div className="products-grid" style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: isMobile ? 10 : 18 }}>
              {filteredByBrand.map(p => <ProductCard key={p.id} product={p} setPage={setPage} setSelectedProduct={setSelectedProduct} />)}
            </div>
          )}
        </div>
      </div>

      {/* BRANDS */}
      <div style={{ background:C.navy, padding:"32px 0" }}>
        <div style={{ maxWidth:1280, margin:"0 auto", padding:"0 32px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
          <div>
            <span className="section-label" style={{ color:"rgba(255,255,255,0.35)", marginBottom:4 }}>Authorized Dealers</span>
            <div style={{ fontSize:14, color:"rgba(255,255,255,0.6)", fontWeight:300 }}>Genuine products from top brands</div>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {apiBrands.filter(b => b !== "All").map(b => (
              <button key={b} onClick={() => { setActiveBrand(b); globalNavigate("products"); }}
                style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:6, padding:"8px 16px", cursor:"pointer", color:"rgba(255,255,255,0.8)", fontFamily:"'Plus Jakarta Sans'", fontWeight:600, fontSize:13, transition:"all 0.2s" }}
                onMouseOver={e=>{e.currentTarget.style.background="rgba(249,115,22,0.15)"; e.currentTarget.style.borderColor="rgba(249,115,22,0.35)"; e.currentTarget.style.color="white";}}
                onMouseOut={e=>{e.currentTarget.style.background="rgba(255,255,255,0.07)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.12)"; e.currentTarget.style.color="rgba(255,255,255,0.8)";}}>
                {b}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* WHY AXXESS */}
      <div style={{ padding:"60px 0", background:"linear-gradient(180deg, #e8ecf5 0%, #f0f2f8 100%)", position:"relative", overflow:"hidden" }}>
        {/* Background decoration */}
        <div style={{ position:"absolute", top:-60, right:-60, width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle, rgba(249,115,22,0.06) 0%, transparent 70%)", pointerEvents:"none" }} />
        <div style={{ position:"absolute", bottom:-60, left:-60, width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle, rgba(13,36,97,0.06) 0%, transparent 70%)", pointerEvents:"none" }} />
        <div style={{ maxWidth:1280, margin:"0 auto", padding:"0 32px", position:"relative", zIndex:1 }}>
          {/* Header */}
          <div style={{ textAlign:"center", marginBottom:44 }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"linear-gradient(135deg, rgba(249,115,22,0.12), rgba(249,115,22,0.06))", border:"1px solid rgba(249,115,22,0.3)", borderRadius:24, padding:"6px 18px", marginBottom:14 }}>
              <span style={{ color:"#f97316", fontSize:14, filter:"drop-shadow(0 0 4px rgba(249,115,22,0.6))" }}>⚡</span>
              <span style={{ color:"#f97316", fontSize:10, fontWeight:800, letterSpacing:2.5, textTransform:"uppercase", fontFamily:"'Plus Jakarta Sans'" }}>Our Promise</span>
            </div>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:34, fontWeight:900, color:C.navy, margin:0, letterSpacing:"-1px" }}>
              Why Choose <span style={{ color:"#f97316" }}>Axxess</span>
            </h2>
            <p style={{ color:C.textSub, fontSize:14, marginTop:10, fontWeight:400 }}>India's trusted Panasonic mixer accessories store</p>
          </div>
          {/* Cards */}
          <div className="why-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:24 }}>
            {[
              { icon:"🏆", title:"100% Genuine Products", desc:"Sourced directly from authorized distributors. No counterfeits, ever.", grad:"linear-gradient(135deg, #fff7ed, #fff)", border:"#f97316", glow:"rgba(249,115,22,0.15)" },
              { icon:"💳", title:"Flexible Payments", desc:"UPI, Net Banking, Credit/Debit Card, or Cash on Delivery — your choice.", grad:"linear-gradient(135deg, #eff6ff, #fff)", border:"#0d2461", glow:"rgba(13,36,97,0.12)" },
              { icon:"🔧", title:"After-Sales Support", desc:"Warranty help, returns, and guidance — we stay with you long after purchase.", grad:"linear-gradient(135deg, #fff7ed, #fff)", border:"#f97316", glow:"rgba(249,115,22,0.15)" },
            ].map(({icon,title,desc,grad,border,glow}) => (
              <div key={title}
                style={{ padding:"32px 28px", borderRadius:20, background:grad, boxShadow:`0 4px 24px ${glow}`, border:`1px solid rgba(13,36,97,0.08)`, borderTop:`3px solid ${border}`, transition:"all 0.3s", cursor:"default", position:"relative", overflow:"hidden" }}
                onMouseOver={e=>{e.currentTarget.style.transform="translateY(-8px)"; e.currentTarget.style.boxShadow=`0 20px 48px ${glow}`;}}
                onMouseOut={e=>{e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow=`0 4px 24px ${glow}`;}}>
                <div style={{ position:"absolute", top:-20, right:-20, width:80, height:80, borderRadius:"50%", background:`radial-gradient(circle, ${glow}, transparent)`, pointerEvents:"none" }} />
                <div style={{ width:56, height:56, borderRadius:16, background:`linear-gradient(135deg, ${glow}, rgba(255,255,255,0.5))`, border:`1px solid ${border}22`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, marginBottom:18, boxShadow:`0 4px 14px ${glow}` }}>{icon}</div>
                <h3 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:18, fontWeight:800, color:C.navy, marginBottom:10, letterSpacing:"-0.3px" }}>{title}</h3>
                <p style={{ fontSize:13.5, color:C.textSub, lineHeight:1.75, margin:0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* WHATSAPP CTA */}
      <div style={{ background:"linear-gradient(135deg, #040d24 0%, #0d2461 50%, #1535a0 100%)", padding:"52px 0", position:"relative", overflow:"hidden" }}>
        {/* Decorative dots */}
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize:"24px 24px", pointerEvents:"none" }} />
        {/* Glow */}
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:600, height:200, background:"radial-gradient(ellipse, rgba(249,115,22,0.08) 0%, transparent 70%)", pointerEvents:"none" }} />
        <div style={{ maxWidth:1280, margin:"0 auto", padding:"0 32px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:32, position:"relative", zIndex:1 }}>
          <div>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(249,115,22,0.15)", border:"1px solid rgba(249,115,22,0.3)", borderRadius:20, padding:"5px 14px", marginBottom:14 }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:"#25d366", display:"inline-block", boxShadow:"0 0 8px rgba(37,211,102,0.8)" }} />
              <span style={{ color:"#86efac", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Plus Jakarta Sans'" }}>We're Online</span>
            </div>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:30, fontWeight:900, color:"white", marginBottom:8, letterSpacing:"-0.8px" }}>Not sure which mixer to buy?</h2>
            <p style={{ color:"rgba(255,255,255,0.55)", fontSize:14, fontWeight:400, margin:0 }}>Our experts will help you find the perfect one for your kitchen and budget.</p>
          </div>
          <a href={`https://wa.me/${WHATSAPP}?text=Hi! I need help choosing a mixer accessory from Axxess.`} target="_blank" rel="noreferrer"
            style={{ display:"inline-flex", alignItems:"center", gap:10, background:"linear-gradient(135deg, #25d366, #1db954)", color:"white", padding:"15px 32px", borderRadius:14, fontFamily:"'Plus Jakarta Sans'", fontWeight:800, fontSize:15, textDecoration:"none", whiteSpace:"nowrap", boxShadow:"0 8px 28px rgba(37,211,102,0.35)", flexShrink:0, transition:"all 0.2s", letterSpacing:"0.2px" }}
            onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 12px 36px rgba(37,211,102,0.45)";}}
            onMouseOut={e=>{e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 8px 28px rgba(37,211,102,0.35)";}}>
            <span style={{ fontSize:20 }}>💬</span> Chat on WhatsApp
          </a>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ background:"#020814", color:"rgba(255,255,255,0.4)", padding:"52px 0 20px", position:"relative", overflow:"hidden" }}>
        {/* Top orange line */}
        <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:"linear-gradient(90deg, transparent, #f97316 30%, #fbbf24 50%, #f97316 70%, transparent)", boxShadow:"0 0 12px rgba(249,115,22,0.4)" }} />
        <div style={{ maxWidth:1280, margin:"0 auto", padding:"0 32px" }}>
          <div className="footer-grid" style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", gap:40, paddingBottom:40, borderBottom:"1px solid rgba(255,255,255,0.06)", marginBottom:20 }}>
            {/* Brand */}
            <div>
              <div style={{ fontFamily:"'Plus Jakarta Sans'", fontWeight:900, fontSize:28, color:"white", marginBottom:4, letterSpacing:"-1px" }}>A<span style={{ color:C.saffron }}>xx</span>ess</div>
              <div style={{ fontSize:10, color:"rgba(249,115,22,0.7)", letterSpacing:2.5, textTransform:"uppercase", marginBottom:14, fontWeight:700 }}>axxess.in</div>
              <p style={{ fontSize:13, lineHeight:1.8, maxWidth:220, color:"rgba(255,255,255,0.4)", marginBottom:20 }}>Your trusted destination for premium mixer accessories across India.</p>
              {/* Social/contact pills */}
              <div style={{ display:"flex", gap:8 }}>
                <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(37,211,102,0.1)", border:"1px solid rgba(37,211,102,0.2)", borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:700, color:"#4ade80", textDecoration:"none", transition:"all 0.2s" }}
                  onMouseOver={e=>e.currentTarget.style.background="rgba(37,211,102,0.18)"}
                  onMouseOut={e=>e.currentTarget.style.background="rgba(37,211,102,0.1)"}>
                  💬 WhatsApp
                </a>
                <a href="mailto:support@axxess.in" style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.2)", borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:700, color:"#fdba74", textDecoration:"none", transition:"all 0.2s" }}
                  onMouseOver={e=>e.currentTarget.style.background="rgba(249,115,22,0.18)"}
                  onMouseOut={e=>e.currentTarget.style.background="rgba(249,115,22,0.1)"}>
                  ✉️ Email
                </a>
              </div>
            </div>
            {/* Links */}
            {[
              {title:"Shop", links:[{l:"All Products",p:"products"},{l:"Best Sellers",p:"products"},{l:"New Arrivals",p:"products"},{l:"Offers",p:"products"}]},
              {title:"Brands", links:[{l:"Panasonic",p:"products"},{l:"Philips",p:"products"},{l:"Bajaj",p:"products"},{l:"Prestige",p:"products"}]},
              {title:"Help", links:[{l:"Contact Us",p:"contact"},{l:"About Us",p:"about"},{l:"Return Policy",p:"return-policy"},{l:"Warranty",p:"return-policy"},{l:"Track Order",p:"account"}]},
            ].map(({title,links}) => (
              <div key={title}>
                <h4 style={{ color:"rgba(255,255,255,0.9)", fontFamily:"'Plus Jakarta Sans'", fontWeight:800, fontSize:12, marginBottom:16, textTransform:"uppercase", letterSpacing:2 }}>{title}</h4>
                {links.map(({l,p}) => (
                  <div key={l} style={{ fontSize:13, marginBottom:10, cursor:"pointer", color:"rgba(255,255,255,0.4)", transition:"all 0.2s", display:"flex", alignItems:"center", gap:6 }}
                    onClick={() => globalNavigate(p)}
                    onMouseOver={e=>{e.currentTarget.style.color="#f97316"; e.currentTarget.style.paddingLeft="4px";}}
                    onMouseOut={e=>{e.currentTarget.style.color="rgba(255,255,255,0.4)"; e.currentTarget.style.paddingLeft="0";}}>{l}</div>
                ))}
              </div>
            ))}
          </div>
          {/* Bottom bar */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12 }}>
            <span style={{ color:"rgba(255,255,255,0.3)" }}>© 2025 Axxess (axxess.in). All rights reserved. | Chennai, Tamil Nadu</span>
            <span style={{ color:"rgba(255,255,255,0.3)" }}>Made with ❤️ for Indian kitchens</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── PRODUCTS PAGE ────────────────────────────────────────────────────────────
function ProductsPage({ setPage, setSelectedProduct, searchQuery, activeBrand, setActiveBrand }) {
  const { apiProducts, productsLoading, apiBrands } = useCart();
  const isMobile = useIsMobile();
  const allProducts = apiProducts;
  const [brand, setBrand] = useState(activeBrand || "All");
  const [sort, setSort] = useState("popular");
  const [compatModel, setCompatModel] = useState("");

  // Sync with navbar brand selection
  useEffect(() => {
    if (activeBrand) setBrand(activeBrand);
  }, [activeBrand]);

  // Brand-specific models for compatibility filter
  const BRAND_MODELS = {
    "Panasonic": [
      "MX-AC200","MX-AC210","MX-AC220","MX-AC250",
      "MX-AC300","MX-AC310","MX-AC350","MX-AC360",
      "MX-AC400","MX-AC460","MX-AC555","MX-AC560",
      "MX-AV325","MX-AV425","MX-AV625",
      "MX-AE375","MX-AE390","MX-AE475",
      "MX-SM11","MX-SB15",
    ],
    "Bajaj": [
      "GX-1","GX-3","GX-8","GX-11","GX-21",
      "Bravo Plus","Twister","Rex Plus","Neo",
      "Popular Plus","Pluto","Classic","Bolt",
    ],
    "Butterfly": [
      "Matchless","Jet Elite","Rhino","Smart",
      "Desire Plus","Bold","Turbo Star","Wave Plus",
      "Pebble","Spectra",
    ],
    "Philips": [
      "HL7699","HL7756","HL7777","HL7707",
      "HL7630","HL7505","HL7510","HL7576",
      "HL7570","HL7701",
    ],
    "Preethi": [
      "Blue Leaf Gold","Blue Leaf Platinum","Eco Plus",
      "Cosmos Plus","Turbo","Zodiac","Nitro",
      "Zion","Steel Plus","Iris",
    ],
    "Sujata": [
      "Dynamix","Supermix","Powermatic Plus",
      "Multimix","Classique","Smart",
    ],
  };

  // Get models for current brand filter or all brands
  const getCompatModels = () => {
    if (brand !== "All" && BRAND_MODELS[brand]) return BRAND_MODELS[brand];
    // If All — show all brands' models grouped
    return Object.entries(BRAND_MODELS).flatMap(([b, models]) => 
      models.map(m => `${b}: ${m}`)
    );
  };

  const compatModels = getCompatModels();

  let filtered = allProducts.filter(p => {
    if (brand !== "All" && p.brand !== brand) return false;
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase()) && !p.brand.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    // Compatibility filter — show products compatible with selected model
    if (compatModel) {
      const hasCompat = p.compatibleModels && p.compatibleModels.length > 0;
      if (hasCompat) {
        return p.compatibleModels.some(m => m.toUpperCase().includes(compatModel.replace("MX-", "").toUpperCase()));
      }
      // Fallback: check product name/description for model number
      const modelShort = compatModel.replace("MX-", "").toUpperCase();
      const inName = p.name.toUpperCase().includes(modelShort);
      const inDesc = (p.description || "").toUpperCase().includes(modelShort);
      return inName || inDesc;
    }
    return true;
  });

  if (sort === "price-asc") filtered = [...filtered].sort((a, b) => a.price - b.price);
  else if (sort === "price-desc") filtered = [...filtered].sort((a, b) => b.price - a.price);
  else if (sort === "rating") filtered = [...filtered].sort((a, b) => b.rating - a.rating);

  return (
    <div className="page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "172px 32px 80px" }}>
      <div style={{ marginBottom: 32 }}>
        <p style={{ color: C.primary, fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>Our Collection</p>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 42, fontWeight: 700, color: C.text, letterSpacing: '-0.5px' }}>
          All Products <span style={{ color: C.textLight, fontSize: 20, fontWeight: 400, fontFamily: "'Plus Jakarta Sans'" }}>({filtered.length} items)</span>
        </h1>
      </div>

      {/* ── Compatibility Filter — only show when specific brand selected ── */}
      {brand !== "All" && BRAND_MODELS[brand] && (
      <div style={{ background: `linear-gradient(135deg, #f0f4ff, #e8f0fe)`, border: `1.5px solid #c7d7fd`, borderRadius: 14, padding: "20px 24px", marginBottom: 24 }}>
        <div className="compat-filter" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 24 }}>🔍</span>
            <div>
              <div style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 800, fontSize: 14, color: C.navy }}>Find Compatible Products</div>
              <div style={{ fontSize: 12, color: C.textSub, marginTop: 1 }}>Select your {brand} mixer model</div>
            </div>
          </div>
          <select value={compatModel} onChange={e => { setCompatModel(e.target.value); trackEvent("compatibility_filter", { model: e.target.value }); }}
            style={{ padding: "10px 16px", border: `1.5px solid ${compatModel ? C.navy : C.border}`, borderRadius: 8, fontSize: 13, fontFamily: "'Plus Jakarta Sans'", fontWeight: compatModel ? 700 : 400, color: compatModel ? C.navy : C.textSub, background: "white", cursor: "pointer", minWidth: 220 }}>
            <option value="">-- Select your {brand} model --</option>
            {(BRAND_MODELS[brand] || []).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {compatModel && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                {filtered.length} product{filtered.length !== 1 ? "s" : ""} compatible with {compatModel}
              </span>
              <button onClick={() => setCompatModel("")} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer", color: C.textSub, fontFamily: "'Plus Jakarta Sans'" }}>
                ✕ Clear
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Brand filter + Sort */}
      <div className="sort-row" style={{ display: "flex", gap: 10, marginBottom: 32, flexWrap: "wrap", alignItems: "center", paddingBottom: 20, borderBottom: `1px solid ${C.border}` }}>
        <div className="brand-pills" style={{ display: "flex", gap: 7, flexWrap: "wrap", flex: 1, overflowX: "auto" }}>
          {apiBrands.map(b => (
            <button key={b} onClick={() => { setBrand(b); setCompatModel(""); if (setActiveBrand) setActiveBrand(b); }} style={{
              padding: "7px 16px", borderRadius: 6,
              border: `1.5px solid ${brand === b ? C.navy : C.border}`,
              background: brand === b ? C.bgElevated : C.bgCard,
              color: brand === b ? C.navy : C.textSub,
              cursor: "pointer", fontSize: 13, fontFamily: "'Plus Jakarta Sans'", fontWeight: brand === b ? 600 : 400, transition: "all 0.2s",
            }}>{b}</button>
          ))}
        </div>
        <select value={sort} onChange={e => setSort(e.target.value)} style={{ padding: "8px 14px", background: C.bgCard, border: `1.5px solid ${C.border}`, color: C.text, borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
          <option value="popular">Most Popular</option>
          <option value="rating">Top Rated</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", color: C.textLight }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔍</div>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: C.textMid, marginBottom: 8 }}>No products found</h2>
          <p>Try a different brand or search term.</p>
        </div>
      ) : (
        <div className="products-grid" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? 10 : 20 }}>
          {filtered.map(p => <ProductCard key={p.id} product={p} setPage={setPage} setSelectedProduct={setSelectedProduct} />)}
        </div>
      )}
    </div>
  );
}

// ─── PRODUCT DETAIL ───────────────────────────────────────────────────────────
// ─── WRITE REVIEW FORM ────────────────────────────────────────────────────────
function WriteReviewForm({ productId, onReviewSubmit }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!user) { globalNavigate("login"); return; }
    if (rating === 0) { setError("Please select a star rating"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/products/${productId}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: user.name || "Customer", phone: user.phone, rating, comment }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to submit review");
      onReviewSubmit(data.review, data.rating, data.reviews);
      setDone(true);
      trackEvent("review_submit", { product_id: productId, rating });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 12, padding: "20px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
      <div style={{ fontWeight: 700, color: "#16a34a", fontSize: 15 }}>Thank you for your review!</div>
      <div style={{ color: "#15803d", fontSize: 13, marginTop: 4 }}>Your review helps other customers make better decisions.</div>
    </div>
  );

  return (
    <div style={{ background: C.bgAlt, borderRadius: 12, padding: "24px 28px", border: `1px solid ${C.border}` }}>
      <h3 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 16, fontWeight: 800, color: C.navy, marginBottom: 20 }}>Write a Review</h3>

      {!user && (
        <div style={{ background: "#fef9ec", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#92400e" }}>
          ⚠️ Please <span style={{ fontWeight: 700, color: C.navy, cursor: "pointer", textDecoration: "underline" }} onClick={() => globalNavigate("login")}>login</span> to write a review.
        </div>
      )}

      {/* Star Rating */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Your Rating *</div>
        <div style={{ display: "flex", gap: 8 }}>
          {[1,2,3,4,5].map(star => (
            <span key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              style={{ fontSize: 36, cursor: "pointer", color: (hover || rating) >= star ? "#f59e0b" : "#d1d5db", transition: "color 0.1s", lineHeight: 1 }}>
              ★
            </span>
          ))}
        </div>
        {rating > 0 && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>
          {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
        </div>}
      </div>

      {/* Comment */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Your Review (optional)</label>
        <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Share your experience with this product..." maxLength={500}
          style={{ width: "100%", padding: "12px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13.5, fontFamily: "'Plus Jakarta Sans'", outline: "none", resize: "vertical", minHeight: 90, color: C.text, background: "white" }} />
        <div style={{ fontSize: 11, color: C.textMuted, textAlign: "right" }}>{comment.length}/500</div>
      </div>

      {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 14 }}>⚠️ {error}</div>}

      <button onClick={submit} disabled={loading || !user}
        style={{ padding: "12px 28px", background: user ? C.navy : "#94a3b8", color: "white", border: "none", borderRadius: 10, fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 14, cursor: user ? "pointer" : "not-allowed" }}>
        {loading ? "Submitting..." : "Submit Review →"}
      </button>
    </div>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
      <button onClick={() => setOpen(o => !o)} style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 18px", background:open?C.bgElevated:C.bgCard, border:"none", cursor:"pointer", textAlign:"left", gap:12 }}>
        <span style={{ fontSize:14, fontWeight:600, color:C.navy }}>{question}</span>
        <span style={{ fontSize:18, color:C.textSub, flexShrink:0, transform:open?"rotate(180deg)":"rotate(0deg)", transition:"transform 0.2s" }}>▾</span>
      </button>
      {open && (
        <div style={{ padding:"14px 18px", background:C.bgCard, borderTop:`1px solid ${C.border}`, fontSize:13.5, color:C.textMid, lineHeight:1.7 }}>
          {answer}
        </div>
      )}
    </div>
  );
}

function ProductDetail({ product: initialProduct, setPage }) {
  const { addToCart, toggleWishlist, isInWishlist } = useCart();
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("desc");
  const [product, setProduct] = useState(initialProduct);

  // Always fetch fresh data from API to get latest fields
  useEffect(() => {
    const id = initialProduct._id || initialProduct.id;
    if (!id) return;
    fetch(`${API}/products/${id}`)
      .then(r => r.json())
      .then(data => {
        if (data && data._id) setProduct({ ...data, id: data._id });
      })
      .catch(() => {}); // silently fallback to initialProduct
  }, [initialProduct._id || initialProduct.id]);

  // ── Product Schema for Google rich results ────────────────────────────────
  useEffect(() => {
    if (!product) return;
    // Track product view
    trackEvent("view_item", { currency: "INR", value: product.price, items: [{ item_id: product._id || product.id, item_name: product.name, item_brand: product.brand, price: product.price }] });
    const schema = {
      "@type": "Product",
      "name": product.name,
      "image": [product.image],
      "description": product.description || `${product.name} - Genuine mixer accessory with 1-year warranty`,
      "brand": { "@type": "Brand", "name": product.brand },
      "sku": product._id || product.id,
      "offers": {
        "@type": "Offer",
        "url": `https://axxess.in`,
        "priceCurrency": "INR",
        "price": product.price,
        "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": { "@type": "Organization", "name": "Axxess" }
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": product.rating || 4.0,
        "reviewCount": product.reviews || 1
      }
    };
    // Inject schema into page head
    let el = document.getElementById("product-schema");
    if (!el) { el = document.createElement("script"); el.id = "product-schema"; el.type = "application/ld+json"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(schema);

    // Inject FAQ schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": `Which Panasonic mixer models is ${product.name} compatible with?`, "acceptedAnswer": { "@type": "Answer", "text": "Compatible with MX-AC300, MX-AC310, MX-AC350, MX-AC400, MX-AC555, MX-AC560 and more. Use our compatibility filter to find the right jar for your model." } },
        { "@type": "Question", "name": `Is ${product.name} a genuine Panasonic product?`, "acceptedAnswer": { "@type": "Answer", "text": "Yes, 100% genuine Panasonic product. All accessories on Axxess are sourced from authorized distributors — not duplicates." } },
        { "@type": "Question", "name": `What is the warranty on ${product.name}?`, "acceptedAnswer": { "@type": "Answer", "text": "1-year manufacturer warranty against manufacturing defects. Contact support@axxess.in for warranty claims." } },
        { "@type": "Question", "name": "Is delivery free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! Free delivery across India on all orders. No minimum order value. Ships within 24 hours." } },
        { "@type": "Question", "name": "Can I return this product?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. 7-day easy return policy. Contact support@axxess.in or WhatsApp +91 93610 76530 within 7 days of delivery." } },
      ]
    };
    let faqEl = document.getElementById("faq-schema");
    if (!faqEl) { faqEl = document.createElement("script"); faqEl.id = "faq-schema"; faqEl.type = "application/ld+json"; document.head.appendChild(faqEl); }
    faqEl.textContent = JSON.stringify(faqSchema);
    // Update page title and description for this product
    document.title = `${product.name} | Axxess — Free Delivery Across India`;
    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) metaDesc.setAttribute("content", `Buy ${product.name} at ₹${product.price}. ${product.description?.split("\n")[0] || "Genuine mixer accessory with 1-year warranty"}. Free delivery across India. Shop at axxess.in`);
    return () => {
      // Reset on unmount
      document.title = "Axxess — Premium Mixer Accessories | Free Delivery Across India";
      if (metaDesc) metaDesc.setAttribute("content", "Buy genuine Panasonic mixer grinder accessories online at best prices. Free delivery across India. 1-year warranty. Shop at axxess.in");
      const schemaEl = document.getElementById("product-schema");
      if (schemaEl) schemaEl.remove();
      const faqSchemaEl = document.getElementById("faq-schema");
      if (faqSchemaEl) faqSchemaEl.remove();
    };
  }, [product]);

  const inW = isInWishlist(product.id);
  const reviewList = product.reviewList || [];
  const waMsg = encodeURIComponent(`Hi! I want to order:\n*${product.name}*\nPrice: ${fmt(product.price)}\nQty: ${qty}`);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingTop: 152 }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 72px" }}>
        <button onClick={() => globalNavigate("products")} style={{ background: "none", border: "none", color: C.navy, cursor: "pointer", fontFamily: "'Plus Jakarta Sans'", fontSize: 13, fontWeight: 600, marginBottom: 24, display: "flex", alignItems: "center", gap: 5 }}>
          ← Back to Products
        </button>

        <div className="detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, background: C.bgCard, borderRadius: 18, padding: 40, boxShadow: C.shadow, border: `1px solid ${C.border}` }}>
          <div>
            <div style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${C.border}`, height: 400, background: C.bgAlt }}>
              <img src={product.image} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "contain", padding: "12px", background: "white" }} />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <span style={{ fontSize: 10, color: C.sage, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5 }}>{product.brand}</span>
              {product.badge && <span style={{ marginLeft: 8, background: C.sage, color: "#0B0B0B", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, textTransform: "uppercase" }}>{product.badge}</span>}
            </div>
            <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 22, fontWeight: 700, color: C.navy, lineHeight: 1.35 }}>{product.name}</h1>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Stars rating={product.rating} size={16} />
              <span style={{ color: C.textLight, fontSize: 13 }}>{product.rating} · {product.reviews} reviews</span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 28, fontWeight: 900, color: "#111111" }}>{fmt(product.price)}</span>
              {product.originalPrice > product.price && <>
                <span style={{ fontSize: 15, color: C.textLight, textDecoration: "line-through" }}>{fmt(product.originalPrice)}</span>
                <span style={{ background: C.greenBg, color: C.green, padding: "3px 8px", borderRadius: 4, fontSize: 12, fontWeight: 700 }}>{pct(product.originalPrice, product.price)}% OFF</span>
              </>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[product.wattage && product.wattage > 0 && `⚡ ${product.wattage}W`, product.jars && product.jars > 0 && `🫙 ${product.jars} Jars`, product.warranty && `🛡️ ${product.warranty}`].filter(Boolean).map(tag => (
                <span key={tag} style={{ background: C.accentBg, color: C.primary, padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, border: `1px solid ${C.accentBorder}` }}>{tag}</span>
              ))}
            </div>
            {product.inStock
              ? <div style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>✅ In Stock · Ships within 24 hours</div>
              : <div style={{ color: C.red, fontSize: 13, fontWeight: 600 }}>❌ Out of Stock</div>}

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ color: C.textMid, fontSize: 13, fontWeight: 500 }}>Qty:</span>
              <div style={{ display: "flex", alignItems: "center", border: `1.5px solid ${C.border}`, borderRadius: 7, overflow: "hidden" }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ background: C.bgAlt, border: "none", cursor: "pointer", padding: "7px 14px", fontSize: 16, color: C.text, fontWeight: 600 }}>−</button>
                <span style={{ padding: "0 18px", fontWeight: 600, background: "white", fontSize: 15 }}>{qty}</span>
                <button onClick={() => setQty(q => q + 1)} style={{ background: C.bgAlt, border: "none", cursor: "pointer", padding: "7px 14px", fontSize: 16, color: C.text, fontWeight: 600 }}>+</button>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-primary" disabled={!product.inStock} onClick={() => product.inStock && addToCart(product, qty)} style={{ flex: 1, padding: "12px" }}>🛒 Add to Cart</button>
              <button onClick={() => toggleWishlist(product)} style={{ padding: "12px 16px", borderRadius: 8, border: `1.5px solid ${C.border}`, background: "white", cursor: "pointer", fontSize: 18 }}>{inW ? "❤️" : "🤍"}</button>
            </div>
            <a href={`https://wa.me/${WHATSAPP}?text=${waMsg}`} target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#25D366", color: "white", padding: "12px", borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: "none" }}>
              💬 Order via WhatsApp
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ marginTop: 32, background: C.bgCard, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", boxShadow: C.shadow }}>
          <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, background: C.bgAlt }}>
            {[{ k: "desc", l: "Description" }, { k: "specs", l: "Specifications" }, { k: "features", l: "Features" }, { k: "faq", l: "FAQ" }, { k: "reviews", l: `Reviews (${reviewList.length})` }].map(({ k, l }) => (
              <button key={k} onClick={() => setTab(k)} style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === k ? C.saffron : "transparent"}`, color: tab === k ? C.navy : C.textSub, padding: "14px 24px", cursor: "pointer", fontFamily: "'Plus Jakarta Sans'", fontWeight: tab === k ? 600 : 400, fontSize: 14, marginBottom: -1, transition: "all 0.2s" }}>{l}</button>
            ))}
          </div>
          <div style={{ padding: 28 }}>
            {tab === "desc" && (
              <ul style={{ listStyle:"none", margin:0, padding:0, display:"flex", flexDirection:"column", gap:10 }}>
                {(product.description || "").split("\n").filter(line => line.trim()).map((line, i) => (
                  <li key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, fontSize:14, color:C.textMid, lineHeight:1.75 }}>
                    <span style={{ color:"#f97316", fontWeight:800, fontSize:16, flexShrink:0, marginTop:1 }}>•</span>
                    <span>{line.trim()}</span>
                  </li>
                ))}
              </ul>
            )}
            {tab === "specs" && (
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <tbody>
                  {[
                    { l:"Brand",          v: product.brand },
                    { l:"Model Name",     v: product.modelName },
                    { l:"Colour",         v: product.colour },
                    { l:"Material",       v: product.material },
                    { l:"Special Feature",v: product.specialFeature },
                    { l:"Capacity",       v: product.capacity },
                    { l:"Wattage",        v: product.wattage ? `${product.wattage}W` : null },
                    { l:"No. of Jars",    v: product.jars ? `${product.jars} Jars` : null },
                    { l:"Item Weight",    v: product.itemWeight },
                    { l:"Warranty",       v: product.warranty },
                  ].filter(r => r.v).map(({ l, v }, i) => (
                    <tr key={l} style={{ background: i % 2 === 0 ? "#f8faff" : "white" }}>
                      <td style={{ padding:"12px 16px", fontSize:13.5, fontWeight:700, color:C.navy, width:"35%", borderBottom:`1px solid ${C.border}` }}>{l}</td>
                      <td style={{ padding:"12px 16px", fontSize:13.5, color:C.textMid, borderBottom:`1px solid ${C.border}` }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === "features" && (
              <ul style={{ listStyle: "none", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {product.features.map(f => <li key={f} style={{ display: "flex", alignItems: "center", gap: 10, color: C.text, fontSize: 14 }}><span style={{ color: C.green, fontWeight: 700, fontSize: 16 }}>✓</span>{f}</li>)}
              </ul>
            )}
            {tab === "faq" && (() => {
              const getFaqs = (p) => {
                const isChutney = p.name?.toLowerCase().includes("chutney") || p.name?.toLowerCase().includes("300");
                const isMulti = p.name?.toLowerCase().includes("multi") || p.name?.toLowerCase().includes("310");
                const isMill = p.name?.toLowerCase().includes("mill") || p.name?.toLowerCase().includes("sm11");
                const isBlender = p.name?.toLowerCase().includes("blender") || p.name?.toLowerCase().includes("sb15");
                const capacity = isChutney?"400ML":isMulti?"600ML":isMill?"1100ML":isBlender?"1500ML":"";
                const useCase = isChutney?"coconut chutney, tomato chutney, ginger-garlic paste and small spice batches":isMulti?"wet grinding, masalas, chutneys, curries and pastes":isMill?"dry grinding of rice flour, wheat flour, semolina, idli/dosa batter and spice powders":"smoothies, milkshakes, lassi, soups and juices";
                const models = isChutney?"MX-AC200, MX-AC210, MX-AC220, MX-AC300, MX-AC310, MX-AC350, MX-AC400, MX-AC555, MX-AC560":"MX-AC300, MX-AC310, MX-AC350, MX-AC400, MX-AC555, MX-AC560, MX-AV425, MX-AV625";
                return [
                  { q: `What is the capacity of this jar?`, a: `This is a ${capacity} jar — ${isChutney?"ideal for small quantity grinding":"perfect for "+(isMulti?"everyday cooking needs":isBlender?"large family servings":"bulk dry grinding")}.` },
                  { q: `Which Panasonic mixer models is this jar compatible with?`, a: `This jar is compatible with: ${models}. Use our compatibility filter on the products page to find the right jar for your specific model.` },
                  { q: `What is this jar best used for?`, a: `This jar is ideal for ${useCase}.` },
                  { q: `Is this a genuine Panasonic product?`, a: `Yes, 100%. All products on Axxess are genuine Panasonic accessories — not duplicates. We source directly from authorized distributors. Genuine parts protect your mixer motor and ensure consistent grinding results.` },
                  { q: `How long is the warranty?`, a: `All Panasonic mixer accessories on Axxess come with a 1-year manufacturer warranty against manufacturing defects. For warranty claims, contact us at support@axxess.in with your order details.` },
                  { q: `Is delivery free?`, a: `Yes! We offer FREE delivery across India on all orders — no minimum order value required. Orders are shipped within 24 hours and delivered in 3-5 business days.` },
                  { q: `Can I return this product?`, a: `Yes. We have a 7-day easy return policy. If you receive a damaged or incorrect product, contact us at support@axxess.in or WhatsApp +91 93610 76530 within 7 days of delivery.` },
                  { q: `How do I know if this jar fits my Panasonic mixer?`, a: `Check your mixer model number (usually on the bottom of the mixer). Then use our Compatibility Filter on the Products page — select your model and see all compatible jars instantly.` },
                  { q: `Why should I choose original Panasonic jars over duplicate ones?`, a: `Original Panasonic jars use food-grade stainless steel, have proper blade alignment and safety lock mechanisms. Duplicate jars have poor blade quality, leak easily, and can damage your mixer motor — costing you more in the long run.` },
                  { q: `How do I place an order?`, a: `Simply add the product to cart, click Checkout, enter your phone number for OTP login, fill in your delivery address and choose payment method (UPI, Card, Net Banking or Cash on Delivery). Your order will be confirmed instantly!` },
                ];
              };
              const faqs = getFaqs(product);
              return (
                <div>
                  <h3 style={{ fontSize:17, fontWeight:700, color:C.navy, marginBottom:20 }}>Frequently Asked Questions</h3>
                  <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                    {faqs.map((faq, i) => (
                      <FaqItem key={i} question={faq.q} answer={faq.a} />
                    ))}
                  </div>
                  <div style={{ marginTop:24, padding:"16px 20px", background:C.accentBg, borderRadius:12, border:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:4 }}>Still have questions?</div>
                    <div style={{ fontSize:13, color:C.textSub }}>
                      📞 WhatsApp us at <strong>+91 93610 76530</strong><br/>
                      ✉️ Email: <strong>support@axxess.in</strong>
                    </div>
                  </div>
                </div>
              );
            })()}
            {tab === "reviews" && (
              <div>
                {/* Review Summary */}
                {reviewList.length > 0 && (
                  <div style={{ background: C.bgAlt, borderRadius: 12, padding: "20px 24px", marginBottom: 24, display: "flex", alignItems: "center", gap: 32, border: `1px solid ${C.border}` }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 48, fontWeight: 900, color: C.navy, lineHeight: 1 }}>{product.rating}</div>
                      <Stars rating={product.rating} size={18} />
                      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{reviewList.length} review{reviewList.length !== 1 ? "s" : ""}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      {[5,4,3,2,1].map(star => {
                        const count = reviewList.filter(r => Math.round(r.rating) === star).length;
                        const pct = reviewList.length ? Math.round((count / reviewList.length) * 100) : 0;
                        return (
                          <div key={star} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: C.textMuted, width: 8 }}>{star}</span>
                            <span style={{ color: "#f59e0b", fontSize: 11 }}>★</span>
                            <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: "#f59e0b", borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 11, color: C.textMuted, width: 24 }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Review List */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 28 }}>
                  {reviewList.length === 0
                    ? <p style={{ color: C.textLight, fontSize: 14 }}>No reviews yet. Be the first to review!</p>
                    : reviewList.map((r, i) => (
                      <div key={i} style={{ background: C.bgAlt, padding: 18, borderRadius: 10, border: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>{r.user}</span>
                            {r.verified && <span style={{ fontSize: 10, background: "#dcfce7", color: "#16a34a", padding: "2px 7px", borderRadius: 10, fontWeight: 700 }}>✓ Verified Buyer</span>}
                          </div>
                          <span style={{ color: C.textLight, fontSize: 12 }}>{new Date(r.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                        </div>
                        <Stars rating={r.rating} />
                        {r.comment && <p style={{ color: C.textMid, marginTop: 8, fontSize: 13.5, lineHeight: 1.65 }}>{r.comment}</p>}
                      </div>
                    ))}
                </div>

                {/* Write Review Form */}
                <WriteReviewForm productId={product._id || product.id} onReviewSubmit={(newReview, newRating, newCount) => {
                  setProduct(p => ({ ...p, reviewList: [...(p.reviewList || []), newReview], rating: newRating, reviews: newCount }));
                }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CART CHECKOUT BUTTON (checks login) ─────────────────────────────────────
function CartCheckoutButton() {
  const { user } = useAuth();
  return (
    <button className="btn-primary"
      onClick={() => {
        if (!user) {
          localStorage.setItem("axxess_return", "checkout");
          globalNavigate("login");
        } else {
          globalNavigate("checkout");
        }
      }}
      style={{ width: "100%", marginTop: 20, padding: "13px" }}>
      {user ? "Proceed to Checkout →" : "Login to Checkout →"}
    </button>
  );
}

// ─── CART ─────────────────────────────────────────────────────────────────────
function CartPage({ setPage }) {
  const { cart, removeFromCart, updateQty, cartTotal } = useCart();
  const delivery = 0; // Free delivery on all orders

  if (cart.length === 0) return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "172px 32px", textAlign: "center" }}>
      <div style={{ fontSize: 72, marginBottom: 20 }}>🛒</div>
      <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 34, fontWeight: 600, marginBottom: 10, color: C.text, letterSpacing:"-0.3px" }}>Your cart is empty</h2>
      <p style={{ color: C.textMid, marginBottom: 28 }}>Add some products to get started.</p>
      <button className="btn-primary" onClick={() => globalNavigate("products")}>Browse Products</button>
    </div>
  );

  return (
    <div className="page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "172px 32px 80px" }}>
      <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 40, fontWeight: 700, color: C.text, marginBottom: 32, letterSpacing:"-0.3px" }}>Shopping Cart</h1>
      <div className="cart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 28 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {cart.map(item => (
            <div key={item.id} style={{ background: C.bgCard, borderRadius: 12, padding: 18, border: `1px solid ${C.border}`, display: "flex", gap: 16, alignItems: "center", boxShadow: C.shadow }}>
              <img src={item.image} alt={item.name} style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 9, border: `1px solid ${C.border}` }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: C.primary, fontWeight: 700, marginBottom: 3 }}>{item.brand}</div>
                <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4, color: C.text }}>{item.name}</div>
                <div style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 800, fontSize: 16 }}>{fmt(item.price)}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", border: `1.5px solid ${C.border}`, borderRadius: 7 }}>
                  <button onClick={() => updateQty(item.id, item.qty - 1)} style={{ background: C.bgAlt, border: "none", cursor: "pointer", padding: "5px 12px", fontWeight: 600, color: C.text }}>−</button>
                  <span style={{ padding: "0 14px", fontWeight: 600, fontSize: 14 }}>{item.qty}</span>
                  <button onClick={() => updateQty(item.id, item.qty + 1)} style={{ background: C.bgAlt, border: "none", cursor: "pointer", padding: "5px 12px", fontWeight: 600, color: C.text }}>+</button>
                </div>
                <button onClick={() => removeFromCart(item.id)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 16, padding: 4 }}>✕</button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: C.bgCard, borderRadius: 14, padding: 24, border: `1px solid ${C.border}`, height: "fit-content", boxShadow: C.shadow }}>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 20, fontWeight: 700, marginBottom: 20, color: C.text }}>Order Summary</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", color: C.textMid, fontSize: 13.5 }}><span>Subtotal</span><span style={{ color: C.text, fontWeight: 500 }}>{fmt(cartTotal)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", color: C.textMid, fontSize: 13.5 }}>
              <span>Delivery</span><span style={{ color: delivery === 0 ? C.green : C.text, fontWeight: 500 }}>{delivery === 0 ? "FREE" : fmt(delivery)}</span>
            </div>
            {delivery > 0 && <p style={{ fontSize: 11, color: C.textLight, background: C.accentBg, padding: "6px 10px", borderRadius: 5 }}>Add {fmt(999 - cartTotal)} more for free delivery!</p>}
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Total</span>
              <span style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 900, fontSize: 22, color: C.navy }}>{fmt(cartTotal)}</span>
            </div>
          </div>
          <button className="btn-primary" onClick={() => globalNavigate("checkout")} style={{ width: "100%", marginTop: 20, padding: "13px" }}>Proceed to Checkout →</button>
          <a href={`https://wa.me/${WHATSAPP}?text=Hi! Order: ${cart.map(i => `${i.name} x${i.qty}`).join(", ")}. Total: ${fmt(cartTotal)}`}
            target="_blank" rel="noreferrer"
            style={{ display: "block", textAlign: "center", marginTop: 10, background: "#25D366", color: "white", padding: "11px", borderRadius: 8, fontWeight: 600, textDecoration: "none", fontSize: 13.5 }}>
            💬 Order via WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── CHECKOUT ─────────────────────────────────────────────────────────────────
// ─── AVAILABLE OFFERS WIDGET ──────────────────────────────────────────────────
function AvailableOffers({ onSelect, cartTotal }) {
  const [offers, setOffers] = useState([]);
  const [show, setShow] = useState(false);

  useEffect(() => {
    fetch(`${API}/coupons/offers`)
      .then(r => r.json())
      .then(data => setOffers(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  if (offers.length === 0) return null;

  return (
    <div style={{ marginTop: 10 }}>
      <button onClick={() => setShow(s => !s)}
        style={{ background: "none", border: "none", color: C.navy, fontFamily: "'Plus Jakarta Sans'", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 4 }}>
        🏷️ View available offers ({offers.length}) {show ? "▲" : "▼"}
      </button>
      {show && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          {offers.map(o => {
            const eligible = o.minOrder === 0 || cartTotal >= o.minOrder;
            return (
              <div key={o.code} style={{ background: eligible ? "#f0f4ff" : "#f8f9fc", border: `1px dashed ${eligible ? C.navy : C.border}`, borderRadius: 8, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", opacity: eligible ? 1 : 0.6 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 800, fontSize: 13, color: C.navy, letterSpacing: 1 }}>{o.code}</span>
                    {!eligible && <span style={{ fontSize: 10, color: "#dc2626" }}>Min ₹{o.minOrder} required</span>}
                  </div>
                  <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
                    {o.type === "percent" ? `${o.value}% off` : `₹${o.value} off`}
                    {o.maxDiscount > 0 ? ` (max ₹${o.maxDiscount})` : ""}
                    {o.minOrder > 0 ? ` on orders above ₹${o.minOrder}` : ""}
                  </div>
                </div>
                {eligible && (
                  <button onClick={() => onSelect(o.code)}
                    style={{ padding: "5px 12px", background: C.navy, color: "white", border: "none", borderRadius: 6, fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 11, cursor: "pointer" }}>
                    Apply
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CheckoutPage({ setPage }) {
  const { cart, cartTotal, removeFromCart } = useCart();
  const { user, updateUser } = useAuth();

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user) globalNavigate("login");
  }, [user]);

  // Pre-fill phone from logged in user
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "", address: "", city: "", state: "Tamil Nadu", pincode: "", payment: "upi" });
  const [done, setDone] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [paidTotal, setPaidTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const delivery = 0;
  const discount = coupon ? coupon.discount : 0;
  const finalTotal = cartTotal + delivery - discount;
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const fieldStyle = { width: "100%", padding: "11px 14px", background: "white", border: `1.5px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13.5, fontFamily: "'Plus Jakarta Sans'", outline: "none", transition: "border 0.2s" };

  const applyCoupon = async () => {
    if (!couponCode.trim()) { setCouponError("Please enter a coupon code"); return; }
    setCouponLoading(true); setCouponError("");
    try {
      const res = await fetch(`${API}/coupons/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: couponCode, orderTotal: cartTotal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCoupon(data);
      trackEvent("coupon_applied", { coupon_code: data.code, discount: data.discount });
    } catch (e) {
      setCouponError(e.message);
      setCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => { setCoupon(null); setCouponCode(""); setCouponError(""); };

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const saveOrderToDb = async (paymentInfo = {}) => {
    const orderData = {
      customer: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      payment: form.payment,
      items: cart.map(i => ({
        productId: i._id || i.id,
        name: i.name,
        brand: i.brand,
        price: i.price,
        qty: i.qty,
        image: i.image,
      })),
      total: finalTotal,
      delivery,
      discount,
      couponCode: coupon ? coupon.code : "",
      status: form.payment === "cod" ? "Pending" : "Confirmed",
      razorpayOrderId: paymentInfo.razorpayOrderId || "",
      razorpayPaymentId: paymentInfo.razorpayPaymentId || "",
    };
    const res = await fetch(`${API}/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(orderData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || "Failed to place order: " + res.status);
    return data;
  };

  const placeOrder = async () => {
    if (!form.name || !form.phone || !form.address || !form.pincode) {
      setError("Please fill all required fields marked with *"); return;
    }
    if (form.phone.length !== 10 || !"6789".includes(form.phone[0])) {
      setError("Please enter a valid 10-digit mobile number"); return;
    }
    if (form.pincode.length !== 6) {
      setError("Please enter a valid 6-digit pincode"); return;
    }
    setLoading(true); setError("");

  // ── Save address to account if checkbox checked ───────────────────────────
  const saveAddressIfNeeded = async () => {
    if (!form.saveAddress || !user || !form.address) return;
    try {
      const newAddr = {
        label: "Home",
        name: form.name,
        phone: form.phone,
        address: form.address,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
      };
      // Don't duplicate if same address already saved
      const exists = user.addresses?.some(a => a.address === form.address && a.pincode === form.pincode);
      if (exists) return;
      const newAddresses = [...(user.addresses || []), newAddr];
      await fetch(`${API}/auth/update-profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: user.phone, addresses: newAddresses }),
      });
      updateUser({ addresses: newAddresses });
    } catch (e) {
      console.log("Address save failed:", e.message);
    }
  };

    // COD — save directly without Razorpay
    if (form.payment === "cod") {
      try {
        const data = await saveOrderToDb();
        await saveAddressIfNeeded();
        if (coupon) await fetch(`${API}/coupons/use`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code: coupon.code }) });
        setPaidTotal(finalTotal);
        setOrderId("ORD-" + data._id.slice(-6).toUpperCase());
        trackEvent("purchase", { transaction_id: data._id, value: finalTotal, currency: "INR", payment_type: "cod", items: cart.map(i => ({ item_id: i.id, item_name: i.name, price: i.price, quantity: i.qty })) });
        cart.forEach(i => removeFromCart(i.id));
        setDone(true);
      } catch (e) {
        setError(e.message || "Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Online payment — use Razorpay
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { setError("Failed to load payment gateway. Please try again."); setLoading(false); return; }

      // Create Razorpay order from backend
      const rzpRes = await fetch(`${API}/payment/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: finalTotal }),
      });
      const rzpData = await rzpRes.json();
      if (!rzpRes.ok) throw new Error(rzpData.error || "Failed to create payment");

      const options = {
        key: RAZORPAY_KEY,
        amount: rzpData.amount,
        currency: "INR",
        name: "Axxess",
        description: `Order of ${cart.length} item(s)`,
        order_id: rzpData.id,
        prefill: {
          name: form.name,
          contact: form.phone,
          email: form.email || "",
        },
        theme: { color: "#0d2461" },
        handler: async (response) => {
          try {
            // Verify payment on backend
            const verifyRes = await fetch(`${API}/payment/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const verifyData = await verifyRes.json();
            if (!verifyData.success) throw new Error("Payment verification failed");

            // Save order to DB
            const order = await saveOrderToDb({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
            });
            await saveAddressIfNeeded();
            setPaidTotal(finalTotal);
            setOrderId("ORD-" + order._id.slice(-6).toUpperCase());
            trackEvent("purchase", { transaction_id: order._id, value: finalTotal, currency: "INR", payment_type: form.payment, items: cart.map(i => ({ item_id: i.id, item_name: i.name, price: i.price, quantity: i.qty })) });
            cart.forEach(i => removeFromCart(i.id));
            setDone(true);
          } catch (e) {
            setError("Payment successful but order saving failed. Please contact support.");
          }
          setLoading(false);
        },
        modal: {
          ondismiss: () => { setError("Payment cancelled. Please try again."); setLoading(false); }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      setError(e.message || "Payment failed. Please try again.");
      setLoading(false);
    }
  };

  if (done) return (
    <div style={{ minHeight: "100vh", background: C.bg, paddingTop: 152, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ maxWidth: 520, width: "100%", padding: "0 24px", textAlign: "center" }}>
        <div style={{ width: 88, height: 88, background: C.greenBg, border: `3px solid ${C.green}`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 42, margin: "0 auto 24px" }}>✓</div>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 32, fontWeight: 800, color: C.navy, marginBottom: 8 }}>Order Placed!</h1>
        <div style={{ display: "inline-block", background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 16px", marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: C.textSub, fontWeight: 500 }}>Order ID: </span>
          <span style={{ fontSize: 13, color: C.navy, fontWeight: 800 }}>{orderId}</span>
        </div>
        <p style={{ color: C.textSub, marginBottom: 4, fontSize: 14 }}>Thank you, <strong>{form.name}</strong>! Your order is confirmed.</p>
        <div style={{ background: "white", padding: "20px 22px", borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 24, textAlign: "left", boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Order Summary</div>
          {form.address && <div style={{ fontSize: 12.5, color: C.textSub, marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>📍 {form.address}, {form.city}, {form.state} - {form.pincode}</div>}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 16, color: C.navy }}>
            <span>Total Paid</span><span>{fmt(paidTotal)}</span>
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Payment: {form.payment.toUpperCase()}</div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button className="btn-primary" onClick={() => setPage("home")} style={{ padding: "12px 28px" }}>Continue Shopping</button>
          <a href={`https://wa.me/${WHATSAPP}?text=Hi! My order ${orderId} has been placed. Please confirm.`} target="_blank" rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#25D366", color: "white", padding: "12px 20px", borderRadius: 10, fontWeight: 700, textDecoration: "none", fontSize: 13 }}>
            💬 WhatsApp
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingTop: 152 }}>
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 32px 80px" }}>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 28, fontWeight: 900, color: C.navy, marginBottom: 8 }}>Checkout</h1>
        <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 28 }}>{cart.length} item{cart.length !== 1 ? "s" : ""} in your order</p>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 9, padding: "12px 16px", marginBottom: 20, color: "#dc2626", fontSize: 13, fontWeight: 600 }}>⚠️ {error}</div>}

        <div className="checkout-grid" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* Delivery Address */}
            <div style={{ background: "white", padding: 28, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
              <h2 style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: C.navy, display: "flex", alignItems: "center", gap: 8 }}>📦 Delivery Address</h2>
              {/* Saved addresses quick select */}
              {user?.addresses && user.addresses.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Saved Addresses</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {user.addresses.map((addr, idx) => (
                      <div key={idx} onClick={() => setForm(f => ({ ...f, name: addr.name, phone: addr.phone, address: addr.address, city: addr.city, state: addr.state, pincode: addr.pincode }))}
                        style={{ padding: "10px 14px", borderRadius: 8, border: `1.5px solid ${form.address === addr.address ? C.navy : C.border}`, background: form.address === addr.address ? "#f0f4ff" : "white", cursor: "pointer", transition: "all 0.15s" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <span style={{ fontSize: 12 }}>{addr.label === "Home" ? "🏠" : addr.label === "Work" ? "💼" : "📍"}</span>
                          <span style={{ fontWeight: 700, fontSize: 12, color: C.navy }}>{addr.label}</span>
                          {idx === 0 && <span style={{ fontSize: 9, background: C.navy, color: "white", padding: "1px 6px", borderRadius: 8, fontWeight: 700 }}>DEFAULT</span>}
                        </div>
                        <div style={{ fontSize: 12, color: C.textSub }}>{addr.name} · {addr.address}, {addr.city} - {addr.pincode}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>Or fill in a new address below</div>
                </div>
              )}
              <div className="address-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {[
                  { k: "name",    l: "Full Name *",     s: 1, ph: "Enter your full name" },
                  { k: "phone",   l: "Phone Number *",  s: 1, ph: "10-digit mobile number" },
                  { k: "email",   l: "Email Address",   s: 2, ph: "yourname@email.com (optional)" },
                  { k: "address", l: "Full Address *",  s: 2, ph: "House no, Street, Area" },
                  { k: "city",    l: "City",            s: 1, ph: "Chennai" },
                  { k: "state",   l: "State",           s: 1, ph: "Tamil Nadu" },
                  { k: "pincode", l: "Pincode *",       s: 1, ph: "6-digit pincode" },
                ].map(({ k, l, s, ph }) => (
                  <div key={k} style={{ gridColumn: `span ${s}` }}>
                    <label style={{ display: "block", fontSize: 11.5, color: C.textSub, marginBottom: 5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{l}</label>
                    <input value={form[k]} onChange={e => upd(k, e.target.value)} placeholder={ph}
                      type={k === "phone" || k === "pincode" ? "tel" : "text"}
                      autoComplete="off"
                      style={fieldStyle}
                      onFocus={e => e.target.style.borderColor = C.navy}
                      onBlur={e => e.target.style.borderColor = C.border} />
                  </div>
                ))}
              </div>
              {/* Save address checkbox */}
              {user && (
                <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
                  <input type="checkbox" id="saveAddr" checked={form.saveAddress || false}
                    onChange={e => upd("saveAddress", e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: C.navy, cursor: "pointer" }} />
                  <label htmlFor="saveAddr" style={{ fontSize: 13, color: C.textSub, cursor: "pointer", fontWeight: 500 }}>
                    Save this address to my account for future orders
                  </label>
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div style={{ background: "white", padding: 28, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
              <h2 style={{ fontWeight: 700, fontSize: 16, marginBottom: 18, color: C.navy }}>💳 Payment Method</h2>
              <div className="payment-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[
                  { k: "upi",        l: "UPI / GPay",      i: "📱", s: "Google Pay, PhonePe" },
                  { k: "card",       l: "Card",            i: "💳", s: "Credit / Debit Card" },
                  { k: "netbanking", l: "Net Banking",     i: "🏦", s: "All major banks" },
                  { k: "cod",        l: "Cash on Delivery",i: "💵", s: "Pay when delivered" },
                ].map(({ k, l, i, s }) => (
                  <label key={k} onClick={() => upd("payment", k)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: form.payment === k ? "#eef2ff" : "white", border: `1.5px solid ${form.payment === k ? C.navy : C.border}`, borderRadius: 10, cursor: "pointer", transition: "all 0.2s" }}>
                    <input type="radio" name="pay" value={k} checked={form.payment === k} onChange={() => upd("payment", k)} style={{ accentColor: C.navy }} />
                    <span style={{ fontSize: 20 }}>{i}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: form.payment === k ? C.navy : C.text }}>{l}</div>
                      <div style={{ fontSize: 10.5, color: C.textMuted }}>{s}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div style={{ background: "white", padding: 22, borderRadius: 14, border: `1px solid ${C.border}`, height: "fit-content", boxShadow: C.shadow, position: "sticky", top: 170 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: C.navy }}>Order Summary</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
              {cart.map(i => (
                <div key={i.id} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <img src={i.image} alt={i.name} style={{ width: 46, height: 46, objectFit: "contain", borderRadius: 8, border: `1px solid ${C.border}`, background: "#f8f9fc", padding: 3 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.name}</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>Qty: {i.qty}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, flexShrink: 0 }}>{fmt(i.price * i.qty)}</div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", color: C.textSub, fontSize: 13 }}><span>Subtotal</span><span>{fmt(cartTotal)}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", color: C.textSub, fontSize: 13 }}>
                <span>Delivery</span>
                <span style={{ color: delivery === 0 ? C.green : C.text, fontWeight: delivery === 0 ? 600 : 400 }}>{delivery === 0 ? "FREE" : fmt(delivery)}</span>
              </div>
              {coupon && (
                <div style={{ display: "flex", justifyContent: "space-between", color: "#16a34a", fontSize: 13, fontWeight: 600 }}>
                  <span>🎉 Discount ({coupon.code})</span>
                  <span>- {fmt(discount)}</span>
                </div>
              )}
              {delivery > 0 && <div style={{ fontSize: 11, color: C.saffron, background: C.saffronBg, padding: "5px 8px", borderRadius: 5 }}>Add {fmt(999 - cartTotal)} more for free delivery!</div>}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 17, marginTop: 4, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <span style={{ color: C.text }}>Total</span>
                <span style={{ color: C.navy }}>{fmt(finalTotal)}</span>
              </div>
            </div>

            {/* Coupon Code */}
            <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              {!coupon ? (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>🎟️ Have a coupon?</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code" maxLength={20}
                      style={{ flex: 1, padding: "9px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: "'Plus Jakarta Sans'", outline: "none", letterSpacing: 1, fontWeight: 600 }}
                      onKeyDown={e => e.key === "Enter" && applyCoupon()} />
                    <button onClick={applyCoupon} disabled={couponLoading}
                      style={{ padding: "9px 14px", background: C.navy, color: "white", border: "none", borderRadius: 8, fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                      {couponLoading ? "..." : "Apply"}
                    </button>
                  </div>
                  {couponError && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>⚠️ {couponError}</div>}
                  <AvailableOffers onSelect={(code) => { setCouponCode(code); }} cartTotal={cartTotal} />
                </div>
              ) : (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#16a34a" }}>✅ {coupon.code} applied!</div>
                    <div style={{ fontSize: 11, color: "#15803d" }}>{coupon.message}</div>
                  </div>
                  <button onClick={removeCoupon} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 18, padding: 0 }}>✕</button>
                </div>
              )}
            </div>
            <button onClick={placeOrder} disabled={loading || cart.length === 0}
              style={{ width: "100%", marginTop: 16, padding: "13px", background: loading ? "#94a3b8" : C.navy, color: "white", border: "none", borderRadius: 10, fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 15, cursor: loading ? "not-allowed" : "pointer", transition: "all 0.2s" }}>
              {loading ? "Placing Order..." : "Place Order →"}
            </button>
            <p style={{ fontSize: 11, color: C.textMuted, textAlign: "center", marginTop: 10 }}>🔒 Your details are safe and secure</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── WISHLIST ─────────────────────────────────────────────────────────────────
function WishlistPage({ setPage, setSelectedProduct }) {
  const { wishlist } = useCart();
  const isMobile = useIsMobile();
  if (wishlist.length === 0) return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "172px 32px", textAlign: "center" }}>
      <div style={{ fontSize: 72, marginBottom: 20 }}>🤍</div>
      <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 32, fontWeight: 600, color: C.text, marginBottom: 10, letterSpacing:"-0.3px" }}>Your wishlist is empty</h2>
      <p style={{ color: C.textMid, marginBottom: 28 }}>Save products you love for later.</p>
      <button className="btn-primary" onClick={() => globalNavigate("products")}>Browse Products</button>
    </div>
  );
  return (
    <div className="page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "172px 32px 80px" }}>
      <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 40, fontWeight: 700, color: C.text, marginBottom: 32, letterSpacing:"-0.3px" }}>My Wishlist</h1>
      <div className="products-grid" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? 10 : 20 }}>
        {wishlist.map(p => <ProductCard key={p.id} product={p} setPage={setPage} setSelectedProduct={setSelectedProduct} />)}
      </div>
    </div>
  );
}

// ─── CONTACT ──────────────────────────────────────────────────────────────────
function AboutPage() {
  return (
    <div className="page-container" style={{ maxWidth:860, margin:"0 auto", padding:"172px 32px 80px" }}>
      <div style={{ marginBottom:40 }}>
        <p style={{ color:C.primary, fontWeight:600, fontSize:12, textTransform:"uppercase", letterSpacing:1.5, marginBottom:6 }}>Who We Are</p>
        <h1 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:40, fontWeight:800, color:C.text, letterSpacing:"-0.5px", marginBottom:16 }}>About Axxess</h1>
        <p style={{ fontSize:16, color:C.textMid, lineHeight:1.8, maxWidth:680 }}>
          Axxess is India's trusted online destination for genuine Panasonic mixer grinder accessories. We specialize in original Panasonic mixer jars, blades, couplers and other accessories — delivered free across India.
        </p>
      </div>

      {/* Mission */}
      <div style={{ background:C.bgCard, borderRadius:16, padding:32, border:`1px solid ${C.border}`, marginBottom:24, boxShadow:C.shadow }}>
        <h2 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:22, fontWeight:700, color:C.navy, marginBottom:12 }}>Our Mission</h2>
        <p style={{ fontSize:15, color:C.textMid, lineHeight:1.8 }}>
          To make genuine Panasonic mixer accessories accessible to every household in India at the best prices, with free doorstep delivery and assured quality. We believe every Indian kitchen deserves original, safe and long-lasting mixer accessories.
        </p>
      </div>

      {/* Why Choose Us */}
      <div style={{ background:C.bgCard, borderRadius:16, padding:32, border:`1px solid ${C.border}`, marginBottom:24, boxShadow:C.shadow }}>
        <h2 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:22, fontWeight:700, color:C.navy, marginBottom:20 }}>Why Choose Axxess?</h2>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          {[
            { icon:"✅", title:"100% Genuine Products", desc:"All accessories are sourced from authorized Panasonic distributors — never duplicates." },
            { icon:"🚚", title:"Free Delivery Across India", desc:"We deliver to every corner of India at no extra charge, on all orders." },
            { icon:"🛡️", title:"1-Year Warranty", desc:"All products come with 1-year manufacturer warranty against defects." },
            { icon:"📦", title:"Ships Within 24 Hours", desc:"Orders are processed and shipped within 24 hours of placement." },
            { icon:"↩️", title:"7-Day Easy Returns", desc:"Not satisfied? Return within 7 days for a full refund, no questions asked." },
            { icon:"📞", title:"Dedicated Support", desc:"Our team is available via WhatsApp and email for all your queries." },
          ].map(item => (
            <div key={item.title} style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
              <span style={{ fontSize:24, flexShrink:0 }}>{item.icon}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:C.text, marginBottom:4 }}>{item.title}</div>
                <div style={{ fontSize:13, color:C.textMid, lineHeight:1.6 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Business Info */}
      <div style={{ background:C.bgCard, borderRadius:16, padding:32, border:`1px solid ${C.border}`, marginBottom:24, boxShadow:C.shadow }}>
        <h2 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:22, fontWeight:700, color:C.navy, marginBottom:20 }}>Business Information</h2>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {[
            { label:"Business Name", value:"Golden Shoppee (Brand: Axxess)" },
            { label:"Location", value:"Chennai, Tamil Nadu, India" },
            { label:"Email", value:"support@axxess.in" },
            { label:"Phone / WhatsApp", value:"+91 93610 76530" },
            { label:"Website", value:"https://axxess.in" },
            { label:"Business Hours", value:"Monday to Saturday, 9 AM to 9 PM IST" },
          ].map(item => (
            <div key={item.label} style={{ display:"flex", gap:16, paddingBottom:12, borderBottom:`1px solid ${C.border}` }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.textLight, width:180, flexShrink:0, textTransform:"uppercase", letterSpacing:0.5 }}>{item.label}</div>
              <div style={{ fontSize:14, color:C.text }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Products */}
      <div style={{ background:C.bgCard, borderRadius:16, padding:32, border:`1px solid ${C.border}`, marginBottom:24, boxShadow:C.shadow }}>
        <h2 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:22, fontWeight:700, color:C.navy, marginBottom:12 }}>What We Sell</h2>
        <p style={{ fontSize:15, color:C.textMid, lineHeight:1.8, marginBottom:16 }}>
          We specialize exclusively in genuine Panasonic mixer grinder accessories:
        </p>
        <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
          {["Chutney Jars (400ML)","Multi Jars (600ML)","Mill Jars (1100ML)","Blender Jars (1500ML)","Mixer Blades","Couplers","Jar Lids"].map(item => (
            <span key={item} style={{ background:C.accentBg, color:C.navy, padding:"6px 14px", borderRadius:20, fontSize:13, fontWeight:600, border:`1px solid ${C.border}` }}>{item}</span>
          ))}
        </div>
      </div>

      {/* Contact CTA */}
      <div style={{ background:`linear-gradient(135deg,${C.navy},#1535a0)`, borderRadius:16, padding:32, textAlign:"center" }}>
        <h2 style={{ fontFamily:"'Plus Jakarta Sans'", fontSize:22, fontWeight:700, color:"white", marginBottom:8 }}>Have Questions?</h2>
        <p style={{ color:"rgba(255,255,255,0.8)", fontSize:14, marginBottom:20 }}>Our team is here to help you find the right accessories for your mixer.</p>
        <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
          <a href="mailto:support@axxess.in" style={{ background:"white", color:C.navy, padding:"11px 24px", borderRadius:10, fontWeight:700, fontSize:14, textDecoration:"none" }}>✉️ Email Us</a>
          <a href="https://wa.me/919361076530" target="_blank" rel="noreferrer" style={{ background:"#25D366", color:"white", padding:"11px 24px", borderRadius:10, fontWeight:700, fontSize:14, textDecoration:"none" }}>💬 WhatsApp</a>
        </div>
      </div>
    </div>
  );
}

function ReturnPolicyPage() {
  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingTop: 152 }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 80px" }}>
        <div style={{ background: "white", borderRadius: 16, padding: "48px 52px", boxShadow: C.shadow, border: `1px solid ${C.border}` }}>

          {/* Header */}
          <div style={{ borderBottom: `3px solid ${C.orange}`, paddingBottom: 24, marginBottom: 36 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.orange, textTransform: "uppercase", letterSpacing: 2, marginBottom: 10 }}>Axxess — axxess.in</div>
            <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 32, fontWeight: 900, color: C.navy, margin: 0 }}>Return & Refund Policy</h1>
            <p style={{ color: C.textMuted, fontSize: 13, marginTop: 8 }}>Last updated: March 2026</p>
          </div>

          {[
            {
              title: "7-Day Easy Returns",
              content: "We offer a hassle-free 7-day return policy from the date of delivery. If you are not completely satisfied with your purchase, you may return the product within 7 days for a full refund or exchange."
            },
            {
              title: "Return Eligibility",
              content: "Products are eligible for return if:\n• The product is defective or damaged on arrival\n• The wrong product was delivered\n• The product does not match the description\n• The product is unused and in original packaging\n\nProducts are NOT eligible for return if:\n• The product has been used or installed\n• The original packaging is damaged\n• The return request is made after 7 days of delivery"
            },
            {
              title: "How to Initiate a Return",
              content: "To initiate a return:\n1. Contact us via WhatsApp at +91 93610 76530 or email support@axxess.in\n2. Provide your Order ID and reason for return\n3. Our team will respond within 24 hours\n4. Pack the product securely in original packaging\n5. Ship the product to the address provided by our team"
            },
            {
              title: "Refund Process",
              content: "Once we receive and inspect the returned product:\n• Refunds are processed within 5-7 business days\n• For online payments (UPI/Card/Net Banking) — refund is credited to the original payment method\n• For Cash on Delivery orders — refund is processed via bank transfer\n• You will receive an email confirmation once the refund is initiated"
            },
            {
              title: "Exchange Policy",
              content: "We accept product exchanges within 7 days of delivery. If you need a different model or colour, contact us and we will arrange an exchange subject to availability. Shipping charges for exchange are borne by Axxess."
            },
            {
              title: "Shipping Costs for Returns",
              content: "• If the return is due to our error (wrong/defective product) — we bear the return shipping cost\n• If the return is for any other reason — the customer bears the return shipping cost\n• We recommend using a trackable shipping service for returns"
            },
            {
              title: "Warranty Claims",
              content: "All products come with a 1-year manufacturer warranty against manufacturing defects. Warranty claims can be initiated by contacting us at support@axxess.in with your order details and a description of the defect."
            },
            {
              title: "Contact Us",
              content: "For any return, refund or exchange queries:\n• WhatsApp: +91 93610 76530\n• Email: support@axxess.in\n• Response time: Within 24 hours on business days"
            }
          ].map(({ title, content }) => (
            <div key={title} style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 17, fontWeight: 800, color: C.navy, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 4, height: 20, background: C.orange, borderRadius: 2, display: "inline-block" }} />
                {title}
              </h2>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.8, whiteSpace: "pre-line", paddingLeft: 12 }}>
                {content}
              </div>
            </div>
          ))}

          {/* Footer CTA */}
          <div style={{ background: `linear-gradient(135deg, ${C.navy}, #1535a0)`, borderRadius: 12, padding: "24px 28px", marginTop: 40, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div>
              <div style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 16, fontWeight: 700, color: "white" }}>Need help with a return?</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>Our team responds within 24 hours</div>
            </div>
            <a href={`https://wa.me/919361076530?text=Hi! I need help with a return.`} target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#25D366", color: "white", padding: "11px 22px", borderRadius: 10, fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
              💬 WhatsApp Us
            </a>
          </div>

        </div>
      </div>
    </div>
  );
}

function ContactPage() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const fieldStyle = { width: "100%", padding: "11px 14px", background: "white", border: `1.5px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13.5, fontFamily: "'Plus Jakarta Sans'", outline: "none" };

  const sendMessage = async () => {
    if (!form.name.trim() || !form.message.trim()) {
      setError("Please fill in your name and message."); return;
    }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      setSent(true);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingTop: 152 }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 72px" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ color: C.primary, fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>Get in Touch</p>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 42, fontWeight: 700, color: C.text, letterSpacing: '-0.5px' }}>We're Here to Help</h1>
        </div>

        <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 32 }}>
          {[{ icon: "📞", t: "Call Us", i: "+91 93610 76530", s: "Mon–Sat, 9am–7pm" }, { icon: "💬", t: "WhatsApp", i: "+91 93610 76530", s: "Quick replies guaranteed" }, { icon: "📧", t: "Email", i: "support@axxess.in", s: "Response within 24 hrs" }, { icon: "📍", t: "We're based in", i: "Chennai, Tamil Nadu", s: "Ships pan-India" }].map(({ icon, t, i, s }) => (
            <div key={t} style={{ background: C.bgCard, padding: 22, borderRadius: 12, border: `1px solid ${C.border}`, display: "flex", gap: 14, alignItems: "flex-start", boxShadow: C.shadow }}>
              <div style={{ fontSize: 28 }}>{icon}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: C.text, marginBottom: 3 }}>{t}</div>
                <div style={{ color: C.primary, fontSize: 13.5, fontWeight: 600 }}>{i}</div>
                <div style={{ color: C.textLight, fontSize: 12, marginTop: 2 }}>{s}</div>
              </div>
            </div>
          ))}
        </div>

        {!sent ? (
          <div style={{ background: C.bgCard, padding: 36, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 22, fontWeight: 700, marginBottom: 22, color: C.text }}>Send a Message</h2>
            {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, fontWeight: 600 }}>⚠️ {error}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[{ k: "name", l: "Your Name" }, { k: "phone", l: "Phone Number" }].map(({ k, l }) => (
                <div key={k}>
                  <label style={{ display: "block", fontSize: 12, color: C.textMid, marginBottom: 5, fontWeight: 500 }}>{l}</label>
                  <input value={form[k]} onChange={e => upd(k, e.target.value)} style={fieldStyle} />
                </div>
              ))}
              <div>
                <label style={{ display: "block", fontSize: 12, color: C.textMid, marginBottom: 5, fontWeight: 500 }}>Message</label>
                <textarea value={form.message} onChange={e => upd("message", e.target.value)} rows={4} style={{ ...fieldStyle, resize: "vertical" }} />
              </div>
              <button className="btn-primary" onClick={sendMessage} disabled={loading} style={{ padding: "13px", opacity: loading ? 0.7 : 1 }}>{loading ? "Sending..." : "Send Message →"}</button>
            </div>
          </div>
        ) : (
          <div style={{ background: C.greenBg, padding: 48, borderRadius: 14, border: `1px solid #d1fae5`, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>✅</div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 24, fontWeight: 700, color: C.text, marginBottom: 8 }}>Message Sent!</h2>
            <p style={{ color: C.textMid }}>We'll get back to you within 24 hours. Thank you, {form.name}!</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
function LoginPage({ setPage }) {
  const { login } = useAuth();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer(t => {
        if (t <= 1) { clearInterval(interval); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  const sendOtp = async () => {
    if (phone.length !== 10) { setError("Enter a valid 10-digit phone number"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send OTP");
      setStep("otp");
      startResendTimer();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendTimer > 0) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to resend OTP");
      setOtp("");
      startResendTimer();
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { setError("Enter the 6-digit OTP"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Invalid OTP");
      login(data.user, data.token);
      // If came from cart, go to checkout, else go to account
      const returnTo = localStorage.getItem("axxess_return") || "account";
      localStorage.removeItem("axxess_return");
      globalNavigate(returnTo);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = { width: "100%", padding: "13px 16px", background: "white", border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: 16, fontFamily: "'Plus Jakarta Sans'", outline: "none", letterSpacing: step === "otp" ? 8 : 0, textAlign: step === "otp" ? "center" : "left" };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", paddingTop: 80 }}>
      <div style={{ background: "white", borderRadius: 20, padding: "40px 36px", width: "100%", maxWidth: 420, boxShadow: C.shadowLg, border: `1px solid ${C.border}`, margin: "0 16px" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, background: `linear-gradient(135deg, #060f2e, #0d2461)`, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 24 }}>⚡</div>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 22, fontWeight: 800, color: C.navy }}>Welcome to Axxess</h1>
          <p style={{ color: C.textMuted, fontSize: 13, marginTop: 4 }}>
            {step === "phone" ? "Enter your phone number to continue" : `OTP sent to +91 ${phone}`}
          </p>
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, fontWeight: 600, marginBottom: 16 }}>⚠️ {error}</div>}

        {step === "phone" ? (
          <div>
            <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Phone Number</label>
            <div style={{ position: "relative", marginBottom: 16 }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: C.textSub, fontWeight: 600 }}>+91</span>
              <input value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="Enter 10-digit number" type="tel" maxLength={10}
                style={{ ...fieldStyle, paddingLeft: 50 }}
                onKeyDown={e => e.key === "Enter" && sendOtp()} />
            </div>
            <button onClick={sendOtp} disabled={loading || phone.length !== 10}
              style={{ width: "100%", padding: "13px", background: phone.length === 10 ? C.navy : "#94a3b8", color: "white", border: "none", borderRadius: 10, fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 15, cursor: phone.length === 10 ? "pointer" : "not-allowed" }}>
              {loading ? "Sending OTP..." : "Send OTP →"}
            </button>
          </div>
        ) : (
          <div>
            <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Enter OTP</label>
            <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="● ● ● ● ● ●" type="tel" maxLength={6}
              style={{ ...fieldStyle, marginBottom: 16 }}
              onKeyDown={e => e.key === "Enter" && verifyOtp()} />
            <button onClick={verifyOtp} disabled={loading || otp.length !== 6}
              style={{ width: "100%", padding: "13px", background: otp.length === 6 ? C.navy : "#94a3b8", color: "white", border: "none", borderRadius: 10, fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 15, cursor: otp.length === 6 ? "pointer" : "not-allowed", marginBottom: 10 }}>
              {loading ? "Verifying..." : "Verify & Login ✓"}
            </button>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <button onClick={resendOtp} disabled={resendTimer > 0 || loading}
                style={{ flex: 1, padding: "10px", background: resendTimer > 0 ? "#f1f5f9" : "white", border: `1px solid ${C.border}`, borderRadius: 10, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, fontSize: 13, color: resendTimer > 0 ? C.textMuted : C.navy, cursor: resendTimer > 0 ? "not-allowed" : "pointer" }}>
                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "🔄 Resend OTP"}
              </button>
              <button onClick={() => { setStep("phone"); setOtp(""); setError(""); }}
                style={{ flex: 1, padding: "10px", background: "none", border: `1px solid ${C.border}`, borderRadius: 10, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, fontSize: 13, color: C.textSub, cursor: "pointer" }}>
                ← Change Number
              </button>
            </div>
          </div>
        )}

        <p style={{ textAlign: "center", fontSize: 12, color: C.textMuted, marginTop: 20 }}>
          By continuing you agree to our Terms & Privacy Policy
        </p>
      </div>
    </div>
  );
}

// ─── PROFILE TAB ─────────────────────────────────────────────────────────────
function ProfileTab() {
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const iStyle = { width: "100%", padding: "11px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13.5, fontFamily: "'Plus Jakarta Sans'", outline: "none", background: "white" };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/auth/update-profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: user.phone, name: form.name.trim(), email: form.email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      updateUser({ name: form.name.trim(), email: form.email.trim() });
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert(e.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: "white", borderRadius: 14, padding: 28, border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
        <h3 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 16, fontWeight: 700, color: C.navy }}>Personal Details</h3>
        {!editing ? (
          <button onClick={() => setEditing(true)}
            style={{ padding: "7px 16px", borderRadius: 8, border: `1.5px solid ${C.navy}`, background: "white", color: C.navy, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
            ✏️ Edit
          </button>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setEditing(false); setForm({ name: user?.name || "", email: user?.email || "" }); }}
              style={{ padding: "7px 14px", borderRadius: 8, border: `1.5px solid ${C.border}`, background: "white", color: C.textSub, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={saveProfile} disabled={saving}
              style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: C.navy, color: "white", fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              {saving ? "Saving..." : "Save ✓"}
            </button>
          </div>
        )}
      </div>

      {saved && <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px", marginBottom: 16, color: "#166534", fontSize: 13, fontWeight: 600 }}>✅ Profile updated successfully!</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Phone — always read only */}
        <div>
          <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Phone Number</label>
          <div style={{ ...iStyle, background: "#f8fafc", color: C.textMuted, display: "flex", alignItems: "center", gap: 8 }}>
            📞 +91 {user.phone} <span style={{ fontSize: 11, color: C.textMuted, marginLeft: "auto" }}>Cannot be changed</span>
          </div>
        </div>
        {/* Name */}
        <div>
          <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Full Name</label>
          {editing ? (
            <input value={form.name} onChange={e => upd("name", e.target.value)} placeholder="Enter your full name" style={iStyle} />
          ) : (
            <div style={{ ...iStyle, background: "#f8fafc", color: form.name ? C.text : C.textMuted }}>{form.name || "Not set — click Edit to add"}</div>
          )}
        </div>
        {/* Email */}
        <div>
          <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Email Address</label>
          {editing ? (
            <input value={form.email} onChange={e => upd("email", e.target.value)} placeholder="yourname@email.com" type="email" style={iStyle} />
          ) : (
            <div style={{ ...iStyle, background: "#f8fafc", color: form.email ? C.text : C.textMuted }}>{form.email || "Not set — click Edit to add"}</div>
          )}
        </div>
        {/* Member Since */}
        <div>
          <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Member Since</label>
          <div style={{ ...iStyle, background: "#f8fafc", color: C.textMuted }}>
            📅 {new Date(user.createdAt || Date.now()).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ADDRESS TAB ──────────────────────────────────────────────────────────────
function AddressTab() {
  const { user, updateUser } = useAuth();
  const [addresses, setAddresses] = useState(() => user?.addresses || []);
  const [showForm, setShowForm] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const blank = { label: "Home", name: "", phone: "", address: "", city: "", state: "Tamil Nadu", pincode: "" };
  const [form, setForm] = useState(blank);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const iStyle = { width: "100%", padding: "10px 13px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: "'Plus Jakarta Sans'", outline: "none", background: "white" };

  const saveAddress = async () => {
    if (!form.name || !form.phone || !form.address || !form.pincode) { alert("Please fill all required fields"); return; }
    setSaving(true);
    const newAddresses = editIdx !== null
      ? addresses.map((a, i) => i === editIdx ? form : a)
      : [...addresses, form];
    try {
      const res = await fetch(`${API}/auth/update-profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: user.phone, addresses: newAddresses }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setAddresses(newAddresses);
      updateUser({ addresses: newAddresses });
      setShowForm(false);
      setEditIdx(null);
      setForm(blank);
    } catch (e) {
      alert(e.message || "Failed to save address.");
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (idx) => {
    if (!window.confirm("Delete this address?")) return;
    const newAddresses = addresses.filter((_, i) => i !== idx);
    await fetch(`${API}/auth/update-profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone: user.phone, addresses: newAddresses }),
    });
    setAddresses(newAddresses);
    updateUser({ addresses: newAddresses });
  };

  const setDefault = async (idx) => {
    const reordered = [addresses[idx], ...addresses.filter((_, i) => i !== idx)];
    await fetch(`${API}/auth/update-profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone: user.phone, addresses: reordered }),
    });
    setAddresses(reordered);
    updateUser({ addresses: reordered });
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 16, fontWeight: 700, color: C.navy }}>Saved Addresses</h3>
          <p style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{addresses.length} address{addresses.length !== 1 ? "es" : ""} saved</p>
        </div>
        {!showForm && (
          <button onClick={() => { setShowForm(true); setEditIdx(null); setForm(blank); }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: C.navy, color: "white", fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            + Add New Address
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div style={{ background:A.bgCard, borderRadius:14, padding: 22, border: `1.5px solid ${C.navy}`, marginBottom: 16 }}>
          <h4 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 16 }}>
            {editIdx !== null ? "Edit Address" : "Add New Address"}
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div style={{ gridColumn: "span 2" }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: "uppercase", marginBottom: 4 }}>Label</label>
              <div style={{ display: "flex", gap: 8 }}>
                {["Home", "Work", "Other"].map(l => (
                  <button key={l} onClick={() => upd("label", l)}
                    style={{ padding: "6px 16px", borderRadius: 6, border: `1.5px solid ${form.label === l ? C.navy : C.border}`, background: form.label === l ? C.navy : "white", color: form.label === l ? "white" : C.textSub, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                    {l === "Home" ? "🏠" : l === "Work" ? "💼" : "📍"} {l}
                  </button>
                ))}
              </div>
            </div>
            {[{k:"name",l:"Full Name *",s:1},{k:"phone",l:"Phone *",s:1},{k:"address",l:"Full Address *",s:2},{k:"city",l:"City",s:1},{k:"state",l:"State",s:1},{k:"pincode",l:"Pincode *",s:1}].map(({k,l,s}) => (
              <div key={k} style={{ gridColumn: `span ${s}` }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: "uppercase", marginBottom: 4 }}>{l}</label>
                <input value={form[k]} onChange={e => upd(k, e.target.value)} style={iStyle} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={saveAddress} disabled={saving}
              style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: C.navy, color: "white", fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              {saving ? "Saving..." : "Save Address ✓"}
            </button>
            <button onClick={() => { setShowForm(false); setEditIdx(null); setForm(blank); }}
              style={{ padding: "10px 16px", borderRadius: 8, border: `1.5px solid ${C.border}`, background: "white", color: C.textSub, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Address Cards */}
      {addresses.length === 0 && !showForm ? (
        <div style={{ background:A.bgCard, borderRadius:14, padding: 40, border: `1px solid ${C.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📍</div>
          <p style={{ color: C.textMuted, fontSize: 13 }}>No saved addresses yet. Add one for faster checkout!</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {addresses.map((addr, idx) => (
            <div key={idx} style={{ background:A.bgCard, borderRadius:14, padding: "18px 20px", border: `1.5px solid ${idx === 0 ? C.navy : C.border}`, position: "relative" }}>
              {idx === 0 && <span style={{ position: "absolute", top: 12, right: 12, background: C.navy, color: "white", fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>DEFAULT</span>}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 16 }}>{addr.label === "Home" ? "🏠" : addr.label === "Work" ? "💼" : "📍"}</span>
                <span style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>{addr.label}</span>
              </div>
              <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 2 }}>{addr.name}</div>
              <div style={{ fontSize: 12, color: C.textSub, marginBottom: 2 }}>📞 {addr.phone}</div>
              <div style={{ fontSize: 12, color: C.textSub }}>{addr.address}, {addr.city}, {addr.state} - {addr.pincode}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={() => { setEditIdx(idx); setForm(addr); setShowForm(true); }}
                  style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: "white", color: C.textSub, fontSize: 12, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, cursor: "pointer" }}>
                  ✏️ Edit
                </button>
                {idx !== 0 && (
                  <button onClick={() => setDefault(idx)}
                    style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.navy}`, background: "white", color: C.navy, fontSize: 12, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, cursor: "pointer" }}>
                    ★ Set Default
                  </button>
                )}
                <button onClick={() => deleteAddress(idx)}
                  style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #fecaca", background: "#fff5f5", color: "#dc2626", fontSize: 12, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, cursor: "pointer" }}>
                  🗑 Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MY ACCOUNT PAGE ──────────────────────────────────────────────────────────
function AccountPage({ setPage }) {
  const { user, logout, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");
  const [cancelling, setCancelling] = useState(null);

  const fetchOrders = () => {
    if (!user || !token) return;
    fetch(`${API}/auth/my-orders`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { setOrders(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) { globalNavigate("login"); return; }
    fetchOrders();
  }, [user]);

  const cancelOrder = async (orderId, paymentMethod) => {
    const isPaid = paymentMethod && paymentMethod !== "cod";
    const msg = isPaid
      ? "Cancel this order? Your payment will be refunded in 5-7 business days."
      : "Are you sure you want to cancel this order?";
    if (!window.confirm(msg)) return;
    setCancelling(orderId);
    try {
      const res = await fetch(`${API}/orders/${orderId}/cancel`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to cancel order");
      alert(data.message || "Order cancelled successfully.");
      fetchOrders();
    } catch (e) {
      alert(e.message || "Failed to cancel order. Please try again.");
    } finally {
      setCancelling(null);
    }
  };

  if (!user) return null;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingTop: 152 }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 60px" }}>
        {/* Profile header */}
        <div style={{ background: `linear-gradient(135deg, #060f2e, #0d2461)`, borderRadius: 16, padding: "28px 28px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 52, height: 52, background: C.saffron, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: "white" }}>
              {user.name ? user.name.charAt(0).toUpperCase() : user.phone.slice(-2)}
            </div>
            <div>
              <div style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 800, fontSize: 18, color: "white" }}>{user.name || "Customer"}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>+91 {user.phone}</div>
            </div>
          </div>
          <button onClick={() => { logout(); globalNavigate("home"); }}
            style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white", padding: "8px 16px", borderRadius: 8, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {[{k:"orders",l:"My Orders"},{k:"profile",l:"Profile"},{k:"addresses",l:"Addresses"}].map(({k,l}) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ padding: "9px 20px", borderRadius: 8, border: `1.5px solid ${tab===k ? C.navy : C.border}`, background: tab===k ? C.navy : "white", color: tab===k ? "white" : C.textSub, fontFamily: "'Plus Jakarta Sans'", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              {l}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div>
            {loading ? (
              <div style={{ textAlign: "center", padding: 40, color: C.textMuted }}>Loading orders...</div>
            ) : orders.length === 0 ? (
              <div style={{ textAlign: "center", padding: 60, background: "white", borderRadius: 14, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 8 }}>No orders yet</h3>
                <p style={{ color: C.textMuted, marginBottom: 20 }}>Start shopping to see your orders here</p>
                <button className="btn-primary" onClick={() => globalNavigate("products")}>Shop Now →</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {orders.map(order => (
                  <div key={order._id} style={{ background:A.bgCard, borderRadius:14, padding: "18px 20px", border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 14, color: C.navy }}>ORD-{order._id.slice(-6).toUpperCase()}</div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{new Date(order.createdAt).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</div>
                      </div>
                      <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: order.status==="Delivered"?"#dcfce7":order.status==="Shipped"?"#dbeafe":order.status==="Cancelled"?"#fee2e2":"#fef9c3",
                        color: order.status==="Delivered"?"#166534":order.status==="Shipped"?"#1e40af":order.status==="Cancelled"?"#991b1b":"#854d0e" }}>
                        {order.status}
                      </span>
                    </div>

                    {/* Order Status Timeline */}
                    {order.status !== "Cancelled" && (() => {
                      const steps = [
                        { key: "Pending", label: "Order Placed", icon: "🧾" },
                        { key: "Confirmed", label: "Confirmed", icon: "✅" },
                        { key: "Shipped", label: "Shipped", icon: "🚚" },
                        { key: "Delivered", label: "Delivered", icon: "🎉" },
                      ];
                      const statusIndex = steps.findIndex(s => s.key === order.status);
                      return (
                        <div style={{ display: "flex", alignItems: "center", marginBottom: 16, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                          {steps.map((step, idx) => {
                            const done = idx <= statusIndex;
                            const active = idx === statusIndex;
                            return (
                              <div key={step.key} style={{ display: "flex", alignItems: "center", flex: idx < steps.length - 1 ? 1 : "none" }}>
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                  <div style={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                                    background: done ? (active ? C.navy : "#dcfce7") : "#f1f5f9",
                                    border: `2px solid ${done ? (active ? C.navy : "#16a34a") : C.border}`,
                                    boxShadow: active ? `0 0 0 3px rgba(13,36,97,0.15)` : "none" }}>
                                    {done ? (active ? step.icon : "✓") : step.icon}
                                  </div>
                                  <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, color: done ? (active ? C.navy : "#16a34a") : C.textMuted, whiteSpace: "nowrap" }}>{step.label}</span>
                                </div>
                                {idx < steps.length - 1 && (
                                  <div style={{ flex: 1, height: 2, background: idx < statusIndex ? "#16a34a" : C.border, margin: "0 4px", marginBottom: 18 }} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                      {order.items && order.items.map((item, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          {item.image && <img src={item.image} alt={item.name} style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 6, border: `1px solid ${C.border}`, background: "#f8f9fc", padding: 2 }} />}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{item.name}</div>
                            <div style={{ fontSize: 11, color: C.textMuted }}>Qty: {item.qty} × {fmt(item.price)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: 12, color: C.textSub }}>Total paid</div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: C.navy }}>{fmt(order.total)}</div>
                      </div>
                      {/* Cancel button — only for Pending or Confirmed */}
                      {(order.status === "Pending" || order.status === "Confirmed") && (
                        <button
                          onClick={() => cancelOrder(order._id, order.payment)}
                          disabled={cancelling === order._id}
                          style={{ padding: "7px 16px", borderRadius: 8, border: "1.5px solid #fecaca", background: cancelling === order._id ? "#f1f5f9" : "#fff5f5", color: "#dc2626", fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 12, cursor: cancelling === order._id ? "not-allowed" : "pointer", transition: "all 0.2s" }}
                          onMouseOver={e => { e.currentTarget.style.background = "#fee2e2"; }}
                          onMouseOut={e => { e.currentTarget.style.background = cancelling === order._id ? "#f1f5f9" : "#fff5f5"; }}>
                          {cancelling === order._id ? "Cancelling..." : "✕ Cancel Order"}
                        </button>
                      )}
                      {order.status === "Cancelled" && (
                        <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Order Cancelled</span>
                      )}
                      {(order.status === "Shipped" || order.status === "Delivered") && (
                        <div>
                          {order.trackingNumber ? (
                            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 14px", marginTop: 8 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#1e40af", marginBottom: 4 }}>🚚 Shipment Tracking</div>
                              <div style={{ fontSize: 12, color: "#1e40af" }}>Courier: <strong>{order.courierName}</strong></div>
                              <div style={{ fontSize: 12, color: "#1e40af" }}>AWB: <strong>{order.trackingNumber}</strong></div>
                              {order.trackingUrl && (
                                <a href={order.trackingUrl} target="_blank" rel="noreferrer"
                                  style={{ display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 700, color: "white", background: "#1d4ed8", padding: "5px 12px", borderRadius: 6, textDecoration: "none" }}>
                                  Track Package →
                                </a>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: 12, color: C.textMuted }}>Tracking details will be added soon</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "profile" && <ProfileTab />}
        {tab === "addresses" && <AddressTab />}
      </div>
    </div>
  );
}

// ─── WHATSAPP FLOAT ───────────────────────────────────────────────────────────
function MobileBottomNav({ page, setPage, activeBrand, setActiveBrand }) {
  const { cartCount, wishlist } = useCart();
  const isMobile = useIsMobile();
  if (!isMobile) return null;
  const tabs = [
    { icon: "🏠", label: "Home", key: "home" },
    { icon: "🛍️", label: "Products", key: "products" },
    { icon: "🔍", label: "Search", key: "search" },
    { icon: "🤍", label: "Wishlist", key: "wishlist", badge: wishlist.length },
    { icon: "🛒", label: "Cart", key: "cart", badge: cartCount },
  ];
  return (
    <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:1000, background:"white", borderTop:"1px solid #e2e8f4", display:"flex", boxShadow:"0 -4px 20px rgba(13,36,97,0.12)", paddingBottom:"env(safe-area-inset-bottom)" }}>
      {tabs.map(tab => {
        const isActive = page === tab.key || (tab.key === "home" && page === "home");
        return (
          <button key={tab.key} onClick={() => {
            if (tab.key === "search") { setPage("products"); }
            else { setPage(tab.key); }
          }} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"8px 0 6px", background:"none", border:"none", cursor:"pointer", position:"relative", gap:2 }}>
            <span style={{ fontSize:22, lineHeight:1 }}>{tab.icon}</span>
            <span style={{ fontSize:10, fontWeight: isActive ? 700 : 500, color: isActive ? "#f97316" : "#94a3b8", fontFamily:"'Plus Jakarta Sans'" }}>{tab.label}</span>
            {isActive && <div style={{ position:"absolute", top:0, left:"20%", right:"20%", height:2, background:"#f97316", borderRadius:"0 0 3px 3px" }} />}
            {tab.badge > 0 && <span style={{ position:"absolute", top:4, right:"18%", background:"#f97316", color:"white", fontSize:9, fontWeight:800, borderRadius:"50%", width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center" }}>{tab.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}

function WAFloat() {
  const isMobile = useIsMobile();
  if (isMobile) return null;
  return (
    <a href={`https://wa.me/${WHATSAPP}?text=Hi! I want to enquire about mixer accessories from Axxess.`} target="_blank" rel="noreferrer"
      style={{ position: "fixed", bottom: 26, right: 26, zIndex: 999, width: 54, height: 54, background: "#25D366", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, boxShadow: "0 4px 18px rgba(37,211,102,0.45)", textDecoration: "none", transition: "transform 0.2s" }}
      onMouseOver={e => e.currentTarget.style.transform = "scale(1.1)"}
      onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}>💬</a>
  );
}

// ─── ADMIN PANEL (inline) ─────────────────────────────────────────────────────
const ADMIN_PRODUCTS_INIT = []; // loaded from MongoDB
const ADMIN_ORDERS_INIT = [];
const STATUS_COLORS = {
  Pending:   { bg:"rgba(245,158,11,0.12)", color:"#fbbf24", border:"rgba(245,158,11,0.3)" },
  Confirmed: { bg:"rgba(124,111,247,0.12)", color:"#a78bfa", border:"rgba(124,111,247,0.3)" },
  Shipped:   { bg:"rgba(59,130,246,0.12)",  color:"#60a5fa", border:"rgba(59,130,246,0.3)" },
  Delivered: { bg:"rgba(34,197,94,0.12)",   color:"#4ade80", border:"rgba(34,197,94,0.3)" },
  Cancelled: { bg:"rgba(239,68,68,0.12)",   color:"#f87171", border:"rgba(239,68,68,0.3)" },
};

const A = {
  bg:         "#0f1117",
  bgCard:     "#1a1d2e",
  bgElevated: "#222536",
  bgHover:    "#252840",
  sidebar:    "#13151f",
  border:     "rgba(255,255,255,0.08)",
  text:       "#e8eaf6",
  textMid:    "#9399b2",
  textLight:  "#565b78",
  accent:     "#7c6ff7",
  accentDark: "#6c5ff0",
  accentBg:   "rgba(124,111,247,0.15)",
  green:      "#22c55e",
  greenBg:    "rgba(34,197,94,0.12)",
  red:        "#ef4444",
  redBg:      "rgba(239,68,68,0.12)",
  amber:      "#f59e0b",
  amberBg:    "rgba(245,158,11,0.12)",
  blue:       "#3b82f6",
  blueBg:     "rgba(59,130,246,0.12)",
  teal:       "#14b8a6",
  tealBg:     "rgba(20,184,166,0.12)",
  shadow:     "0 2px 12px rgba(0,0,0,0.3)",
  shadowMd:   "0 8px 24px rgba(0,0,0,0.4)",
  shadowLg:   "0 16px 48px rgba(0,0,0,0.5)",
};

const acss = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.aj * { box-sizing:border-box; margin:0; padding:0; }
.aj { font-family:'DM Sans',sans-serif; background:${A.bg}; color:${A.text}; min-height:100vh; }
.aj .rh:hover { background:${A.bgHover}!important; transition:background 0.15s; }
.aj input,.aj select,.aj textarea { background:${A.bgElevated}!important; color:${A.text}!important; border:1px solid ${A.border}!important; border-radius:8px!important; transition:all 0.2s; font-family:'DM Sans',sans-serif!important; }
.aj input::placeholder,.aj textarea::placeholder { color:${A.textLight}!important; }
.aj input:focus,.aj select:focus,.aj textarea:focus { border-color:${A.accent}!important; outline:none!important; box-shadow:0 0 0 3px rgba(124,111,247,0.2)!important; }
@keyframes afu { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
.afu { animation:afu 0.3s ease both; }
@keyframes spin { to{transform:rotate(360deg)} }
.aj ::-webkit-scrollbar { width:5px; height:5px; }
.aj ::-webkit-scrollbar-track { background:${A.bg}; }
.aj ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.1); border-radius:3px; }
.aj ::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,0.2); }
`;

function AdminLogin({ onLogin }) {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const attempt = () => {
    setLoading(true);
    setTimeout(() => {
      if (u === "admin" && p === "axxess@2025") { onLogin(); }
      else { setErr("Incorrect username or password."); setLoading(false); }
    }, 600);
  };
  return (
    <div className="aj" style={{ display:"flex", minHeight:"100vh", background:"linear-gradient(135deg,#667eea 0%,#764ba2 100%)" }}>
      <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", flex:1 }}>
        <div className="afu" style={{ background:A.bgCard, borderRadius:20, padding:"44px 40px", width:400, boxShadow:A.shadowLg, border:`1px solid ${A.border}` }}>
          <div style={{ textAlign:"center", marginBottom:32 }}>
            <div style={{ width:56, height:56, background:"linear-gradient(135deg,#667eea,#764ba2)", borderRadius:16, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", boxShadow:"0 8px 24px rgba(102,126,234,0.4)" }}>
              <span style={{ fontSize:24 }}>⚡</span>
            </div>
            <div style={{ fontSize:22, fontWeight:800, color:A.text, marginBottom:6 }}>Welcome back</div>
            <div style={{ fontSize:13, color:A.textLight }}>Sign in to Axxess Admin Panel</div>
          </div>
          {err && <div style={{ background:A.redBg, border:`1px solid #fca5a5`, borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:13, color:A.red }}>⚠ {err}</div>}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:A.textMid, marginBottom:6, textTransform:"uppercase", letterSpacing:0.5 }}>Username</label>
            <input type="text" value={u} onChange={e=>setU(e.target.value)} placeholder="Enter username" onKeyDown={e=>e.key==="Enter"&&attempt()}
              style={{ width:"100%", padding:"11px 14px", fontSize:14 }}/>
          </div>
          <div style={{ marginBottom:24 }}>
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:A.textMid, marginBottom:6, textTransform:"uppercase", letterSpacing:0.5 }}>Password</label>
            <input type="password" value={p} onChange={e=>setP(e.target.value)} placeholder="Enter password" onKeyDown={e=>e.key==="Enter"&&attempt()}
              style={{ width:"100%", padding:"11px 14px", fontSize:14 }}/>
          </div>
          <button onClick={attempt} disabled={loading} style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#667eea,#764ba2)", color:"white", border:"none", borderRadius:12, fontSize:15, fontWeight:700, cursor:loading?"not-allowed":"pointer", boxShadow:"0 4px 16px rgba(102,126,234,0.4)", opacity:loading?0.8:1 }}>
            {loading ? "Signing in..." : "Sign In →"}
          </button>
          <div style={{ marginTop:20, textAlign:"center", fontSize:12, color:A.textLight }}>🔒 Authorized personnel only</div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, gradient, icon }) {
  return (
    <div className="afu" style={{ background:gradient, borderRadius:16, padding:"24px", color:"white", position:"relative", overflow:"hidden", boxShadow:"0 8px 24px rgba(0,0,0,0.12)" }}>
      <div style={{ position:"absolute", top:-20, right:-20, width:100, height:100, borderRadius:"50%", background:"rgba(255,255,255,0.1)" }}/>
      <div style={{ position:"absolute", bottom:-30, right:20, width:80, height:80, borderRadius:"50%", background:"rgba(255,255,255,0.08)" }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, position:"relative" }}>
        <div style={{ fontSize:13, fontWeight:500, opacity:0.9 }}>{label}</div>
        <div style={{ width:40, height:40, background:"rgba(255,255,255,0.2)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{icon}</div>
      </div>
      <div style={{ fontSize:32, fontWeight:800, letterSpacing:-1, marginBottom:6, position:"relative" }}>{value}</div>
      <div style={{ fontSize:12, opacity:0.8, position:"relative" }}>{sub}</div>
    </div>
  );
}

function AdminDashboard({ products, orders, setTab }) {
  const fmta = n => `₹${Number(n).toLocaleString("en-IN")}`;
  const revenue = orders.filter(o=>o.status==="Delivered").reduce((s,o)=>s+o.total,0);
  const pending = orders.filter(o=>o.status==="Pending").length;
  const shipped = orders.filter(o=>o.status==="Shipped").length;
  const outOfStock = products.filter(p=>p.stock===0);
  const lowStock = products.filter(p=>p.stock>0&&p.stock<=5);

  const statusCounts = ["Pending","Confirmed","Shipped","Delivered","Cancelled"].map(s=>({
    label:s, count:orders.filter(o=>o.status===s).length,
    col:(STATUS_COLORS[s]||{}).color||"#999",
    bg:(STATUS_COLORS[s]||{}).bg||"#f5f5f5",
  }));

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:24, fontWeight:800, color:A.text, marginBottom:4 }}>Dashboard</h1>
        <p style={{ fontSize:13, color:A.textLight }}>Welcome back! Here's what's happening with your store.</p>
      </div>

      {/* Stat Cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:20, marginBottom:28 }}>
        <StatCard label="Total Revenue" value={fmta(revenue)} sub="From delivered orders" icon="💰" gradient="linear-gradient(135deg,#667eea,#764ba2)"/>
        <StatCard label="Total Orders" value={orders.length} sub={`${pending} pending · ${shipped} shipped`} icon="📦" gradient="linear-gradient(135deg,#f093fb,#f5576c)"/>
        <StatCard label="Products" value={products.length} sub={`${outOfStock.length} out of stock`} icon="🛍️" gradient="linear-gradient(135deg,#4facfe,#00f2fe)"/>
        <StatCard label="Low Stock" value={lowStock.length} sub="Need restocking soon" icon="⚠️" gradient="linear-gradient(135deg,#43e97b,#38f9d7)"/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }}>
        {/* Order Status Breakdown */}
        <div style={{ background:A.bgCard, borderRadius:16, padding:"24px", boxShadow:A.shadow, border:`1px solid ${A.border}` }}>
          <div style={{ fontWeight:700, fontSize:15, color:A.text, marginBottom:20 }}>Order Status</div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {statusCounts.map(({label,count,col,bg})=>(
              <div key={label} style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:10, height:10, borderRadius:"50%", background:col, flexShrink:0 }}/>
                <div style={{ flex:1, fontSize:13, color:A.textMid }}>{label}</div>
                <div style={{ background:bg, color:col, padding:"2px 10px", borderRadius:20, fontSize:12, fontWeight:600 }}>{count}</div>
                <div style={{ width:80, height:6, background:A.bg, borderRadius:3, overflow:"hidden" }}>
                  <div style={{ width:`${orders.length?Math.round(count/orders.length*100):0}%`, height:"100%", background:col, borderRadius:3 }}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ background:A.bgCard, borderRadius:16, padding:"24px", boxShadow:A.shadow, border:`1px solid ${A.border}` }}>
          <div style={{ fontWeight:700, fontSize:15, color:A.text, marginBottom:20 }}>Quick Actions</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              { label:"Add Product", icon:"➕", color:"#667eea", action:"addproduct" },
              { label:"View Orders", icon:"📋", color:"#f5576c", action:"orders" },
              { label:"Coupons", icon:"🎟️", color:"#4facfe", action:"coupons" },
              { label:"Messages", icon:"💬", color:"#43e97b", action:"messages" },
            ].map(({label,icon,color,action})=>(
              <button key={label} onClick={()=>setTab(action)} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, padding:"16px 10px", background:A.bgElevated, borderRadius:12, border:`1px solid ${A.border}`, cursor:"pointer", transition:"all 0.2s" }}
                onMouseOver={e=>{e.currentTarget.style.background=A.accentBg;e.currentTarget.style.borderColor=A.accent;}}
                onMouseOut={e=>{e.currentTarget.style.background=A.bgElevated;e.currentTarget.style.borderColor=A.border;}}>
                <div style={{ width:40, height:40, borderRadius:10, background:color+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{icon}</div>
                <div style={{ fontSize:12, fontWeight:600, color:A.textMid }}>{label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div style={{ background:A.bgCard, borderRadius:16, boxShadow:A.shadow, border:`1px solid ${A.border}`, overflow:"hidden" }}>
        <div style={{ padding:"18px 24px", borderBottom:`1px solid ${A.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontWeight:700, fontSize:15, color:A.text }}>Recent Orders</div>
          <button onClick={()=>setTab("orders")} style={{ background:A.accentBg, color:A.accent, border:"none", padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer" }}>View All →</button>
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:A.bgElevated }}>
              {["Order","Customer","Total","Status","Date"].map(h=>(
                <th key={h} style={{ padding:"12px 16px", textAlign:"left", fontSize:11, fontWeight:600, color:A.textLight, textTransform:"uppercase", letterSpacing:0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.slice(0,6).map((o,i)=>(
              <tr key={o.id} className="rh" style={{ borderTop:`1px solid ${A.border}` }}>
                <td style={{ padding:"13px 16px", fontSize:13, fontWeight:600, color:A.accent }}>#{o.id?.slice(-6)?.toUpperCase()}</td>
                <td style={{ padding:"13px 16px", fontSize:13, color:A.text }}>{o.customer}</td>
                <td style={{ padding:"13px 16px", fontSize:13, fontWeight:600, color:A.text }}>₹{Number(o.total).toLocaleString("en-IN")}</td>
                <td style={{ padding:"13px 16px" }}>
                  <span style={{ background:(STATUS_COLORS[o.status]||{}).bg||A.bgElevated, color:(STATUS_COLORS[o.status]||{}).color||A.textMid, padding:"4px 10px", borderRadius:20, fontSize:11, fontWeight:600 }}>{o.status}</span>
                </td>
                <td style={{ padding:"13px 16px", fontSize:12, color:A.textLight }}>{o.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminProducts({ products, setProducts, setTab, setEditProd, onDelete }) {
  const [search, setSearch] = useState("");
  const fmta = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.brand.toLowerCase().includes(search.toLowerCase()));
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22 }}>
        <div><h1 style={{fontSize:22,fontWeight:800,color:A.text}}>Products</h1><p style={{color:A.textLight,fontSize:12,marginTop:2}}>{products.length} products</p></div>
        <button onClick={() => { setEditProd(null); setTab("addproduct"); }} style={{background:"linear-gradient(135deg,#667eea,#764ba2)",color:"white",border:"none",padding:"10px 20px",borderRadius:10,fontWeight:600,fontSize:13,cursor:"pointer",boxShadow:"0 4px 12px rgba(102,126,234,0.3)"}}>+ Add Product</button>
      </div>
      <div style={{position:"relative",marginBottom:16}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search products..." style={{padding:"8px 13px 8px 36px",border:`1.5px solid ${A.border}`,borderRadius:8,fontSize:13,background:A.bgCard,width:300,fontFamily:"'DM Sans',sans-serif"}}/>
        <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",color:A.textLight,fontSize:13}}>🔍</span>
      </div>
      <div style={{background:A.bgCard,borderRadius:16,border:`1px solid ${A.border}`,boxShadow:A.shadow,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:A.bgElevated}}>{["Product","Brand","Price","Stock","Status","Actions"].map(h=><th key={h} style={{padding:"11px 14px",textAlign:"left",fontSize:10,fontWeight:700,color:A.textLight,textTransform:"uppercase",letterSpacing:0.6}}>{h}</th>)}</tr></thead>
          <tbody>{filtered.map((p,i)=>(
            <tr key={p.id} className="rh" style={{borderTop:`1px solid ${A.border}`,background:A.bgCard}}>
              <td style={{padding:"11px 14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <img src={p.image} alt={p.name} style={{width:42,height:42,objectFit:"cover",borderRadius:7,border:`1px solid ${A.border}`}}/>
                  <div><div style={{fontSize:12,fontWeight:600,maxWidth:200,color:A.text}}>{p.name}</div>{p.badge&&<span style={{fontSize:9,background:A.accentBg,color:A.accent,padding:"1px 6px",borderRadius:3,fontWeight:700}}>{p.badge}</span>}</div>
                </div>
              </td>
              <td style={{padding:"11px 14px",fontSize:12,color:A.textMid}}>{p.brand}</td>
              <td style={{padding:"11px 14px"}}><div style={{fontSize:12,fontWeight:700}}>{fmta(p.price)}</div><div style={{fontSize:10,color:A.textLight,textDecoration:"line-through"}}>{fmta(p.originalPrice)}</div></td>
              <td style={{padding:"11px 14px",fontSize:12,fontWeight:600,color:p.stock===0?A.red:p.stock<=5?"#d97706":A.green}}>{p.stock===0?"Out of Stock":`${p.stock} units`}</td>
              <td style={{padding:"11px 14px"}}><button onClick={()=>setProducts(prev=>prev.map(q=>q.id===p.id?{...q,inStock:!q.inStock}:q))} style={{padding:"3px 11px",borderRadius:50,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,background:p.inStock?A.greenBg:A.redBg,color:p.inStock?A.green:A.red,fontFamily:"'DM Sans',sans-serif"}}>{p.inStock?"● Active":"● Inactive"}</button></td>
              <td style={{padding:"11px 14px"}}>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>{setEditProd(p);setTab("addproduct");}} style={{padding:"5px 11px",borderRadius:6,border:`1px solid ${A.border}`,background:A.bgElevated,cursor:"pointer",fontSize:11,fontWeight:600,color:"#4a5568",fontFamily:"'DM Sans',sans-serif"}}>✏️ Edit</button>
                  <button onClick={() => { const pid = p._id || p.id; onDelete(pid); }} style={{padding:"5px 11px",borderRadius:6,border:`1px solid ${A.border}`,background:A.redBg,cursor:"pointer",fontSize:11,fontWeight:600,color:A.red,fontFamily:"'DM Sans',sans-serif"}}>🗑️</button>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function AdminAddProduct({ products, setProducts, setTab, editProd, setEditProd, onSave }) {
  const isEdit = !!editProd;
  const blank = { name:"",brand:"Panasonic",price:"",originalPrice:"",stock:"",wattage:"",jars:"",warranty:"2 Years",badge:"",inStock:true,image:"",description:"",features:"",modelName:"",colour:"",material:"",specialFeature:"",capacity:"",itemWeight:"",compatibleModels:"" };
  const [form, setForm] = useState(isEdit ? {...editProd, features: (editProd.features||[]).join(", "), modelName:editProd.modelName||"", colour:editProd.colour||"", material:editProd.material||"", specialFeature:editProd.specialFeature||"", capacity:editProd.capacity||"", itemWeight:editProd.itemWeight||"", compatibleModels:(editProd.compatibleModels||[]).join(", ")} : blank);
  const [saved, setSaved] = useState(false);
  const [brandList, setBrandList] = useState(["Panasonic"]);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const fStyle = { width:"100%",padding:"9px 12px",border:`1.5px solid ${A.border}`,borderRadius:7,fontSize:13,color:A.text,background:A.bgElevated,fontFamily:"'DM Sans',sans-serif" };
  const lStyle = { fontSize:11,fontWeight:700,color:A.textMid,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:0.5 };

  useEffect(() => {
    fetch(`${API}/brands`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setBrandList(data.map(b => b.name)); })
      .catch(() => {});
  }, []);
  const save = async () => {
    if(!form.name||!form.price){alert("Name and price required.");return;}
    const prod = {
      ...form,
      _id: isEdit ? (editProd._id || editProd.id) : undefined,
      price: Number(form.price),
      originalPrice: Number(form.originalPrice)||Number(form.price),
      stock: Number(form.stock)||0,
      wattage: form.wattage !== "" ? Number(form.wattage) : undefined,
      jars: form.jars !== "" ? Number(form.jars) : undefined,
      modelName: form.modelName||"",
      colour: form.colour||"",
      material: form.material||"",
      specialFeature: form.specialFeature||"",
      capacity: form.capacity||"",
      itemWeight: form.itemWeight||"",
      features: form.features ? form.features.split(",").map(f=>f.trim()).filter(Boolean) : [],
      compatibleModels: form.compatibleModels ? form.compatibleModels.split(",").map(m=>m.trim().toUpperCase()).filter(Boolean) : []
    };
    await onSave(prod, isEdit);
    setSaved(true);
    setTimeout(()=>{setEditProd(null);setTab("products");},1200);
  };
  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:24}}>
        <button onClick={()=>{setEditProd(null);setTab("products");}} style={{background:"none",border:"none",color:A.accent,cursor:"pointer",fontSize:13,fontWeight:500}}>← Back</button>
        <h1 style={{fontSize:22,fontWeight:800,color:A.text}}>{isEdit?"Edit Product":"Add New Product"}</h1>
      </div>
      {saved&&<div style={{background:A.greenBg,border:"1px solid rgba(34,197,94,0.3)",borderRadius:9,padding:"11px 16px",marginBottom:18,color:A.green,fontWeight:600,fontSize:13}}>✅ Product {isEdit?"updated":"added"} successfully!</div>}
      <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:20}}>
        <div style={{display:"flex",flexDirection:"column",gap:18}}>
          <div style={{background:A.bgCard,borderRadius:16,padding:24,border:`1px solid ${A.border}`,boxShadow:A.shadow}}>
            <h2 style={{fontSize:14,fontWeight:700,marginBottom:18}}>Basic Information</h2>
            <div style={{display:"flex",flexDirection:"column",gap:13}}>
              <div><label style={lStyle}>Product Name *</label><input value={form.name} onChange={e=>upd("name",e.target.value)} placeholder="e.g. Panasonic MX-AC300" style={fStyle}/></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div><label style={lStyle}>Brand</label><select value={form.brand} onChange={e=>upd("brand",e.target.value)} style={fStyle}>{brandList.map(b=><option key={b} value={b}>{b}</option>)}</select></div>
                <div><label style={lStyle}>Badge</label><select value={form.badge} onChange={e=>upd("badge",e.target.value)} style={fStyle}>{["","Best Seller","Top Rated","Budget Pick","Premium","South India Fav","Durable"].map(b=><option key={b} value={b}>{b||"None"}</option>)}</select></div>
              </div>
              <div>
                <label style={lStyle}>Description</label>
                <div style={{fontSize:10, color:A.textLight, marginBottom:5}}>Enter each point on a new line — will show as bullet points on product page</div>
                <textarea value={form.description} onChange={e=>upd("description",e.target.value)} rows={6}
                  placeholder={"Product Specifications: Panasonic MX-310MA 550W\nPremium Material: Made with SS304 Stainless Steel\nEfficient Grinding Blade for smooth grinding"}
                  style={{...fStyle,resize:"vertical", lineHeight:1.7}}/>
              </div>
              <div><label style={lStyle}>Features (comma separated)</label><input value={form.features} onChange={e=>upd("features",e.target.value)} placeholder="550W Motor, 3 Jars, 2 Year Warranty" style={fStyle}/></div>
            </div>
          </div>
          <div style={{background:A.bgCard,borderRadius:16,padding:24,border:`1px solid ${A.border}`,boxShadow:A.shadow}}>
            <h2 style={{fontSize:14,fontWeight:700,marginBottom:18}}>Specifications</h2>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div><label style={lStyle}>Model Name</label><input value={form.modelName} onChange={e=>upd("modelName",e.target.value)} placeholder="e.g. MX-AC300" style={fStyle}/></div>
              <div><label style={lStyle}>Colour</label><input value={form.colour} onChange={e=>upd("colour",e.target.value)} placeholder="e.g. White, Black" style={fStyle}/></div>
              <div><label style={lStyle}>Material</label><input value={form.material} onChange={e=>upd("material",e.target.value)} placeholder="e.g. Stainless Steel" style={fStyle}/></div>
              <div><label style={lStyle}>Capacity</label><input value={form.capacity} onChange={e=>upd("capacity",e.target.value)} placeholder="e.g. 1.5 Litres" style={fStyle}/></div>
              <div><label style={lStyle}>Wattage (W)</label><input value={form.wattage} onChange={e=>upd("wattage",e.target.value)} placeholder="e.g. 750" style={fStyle}/></div>
              <div><label style={lStyle}>No. of Jars</label><input value={form.jars} onChange={e=>upd("jars",e.target.value)} placeholder="e.g. 3" style={fStyle}/></div>
              <div><label style={lStyle}>Item Weight</label><input value={form.itemWeight} onChange={e=>upd("itemWeight",e.target.value)} placeholder="e.g. 3.2 kg" style={fStyle}/></div>
              <div><label style={lStyle}>Warranty</label><input value={form.warranty} onChange={e=>upd("warranty",e.target.value)} placeholder="e.g. 2 Years" style={fStyle}/></div>
            </div>
            <div style={{marginTop:12}}>
              <label style={lStyle}>Special Feature</label>
              <input value={form.specialFeature} onChange={e=>upd("specialFeature",e.target.value)} placeholder="e.g. Anti-skid feet, Overload protection" style={fStyle}/>
            </div>
            <div style={{marginTop:12}}>
              <label style={lStyle}>Compatible Mixer Models</label>
              <input value={form.compatibleModels} onChange={e=>upd("compatibleModels",e.target.value)} placeholder="e.g. MX-AC310, MX-AC400, MX-AC555, MX-AC560" style={fStyle}/>
              <div style={{fontSize:11,color:A.textLight,marginTop:4}}>Enter model numbers separated by commas. This powers the compatibility filter.</div>
            </div>
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:18}}>
          <div style={{background:A.bgCard,borderRadius:16,padding:22,border:`1px solid ${A.border}`,boxShadow:A.shadow}}>
            <h2 style={{fontSize:14,fontWeight:700,marginBottom:16}}>Pricing & Stock</h2>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {[{k:"price",l:"Selling Price (₹) *"},{k:"originalPrice",l:"Original Price (₹)"},{k:"stock",l:"Stock Quantity"}].map(({k,l})=>(
                <div key={k}><label style={lStyle}>{l}</label><input type="number" value={form[k]} onChange={e=>upd(k,e.target.value)} style={fStyle}/></div>
              ))}
              <div><label style={lStyle}>Availability</label>
                <div style={{display:"flex",gap:8}}>
                  {[{v:true,l:"In Stock"},{v:false,l:"Out of Stock"}].map(({v,l})=>(
                    <label key={l} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",borderRadius:7,border:`1.5px solid ${form.inStock===v?A.accent:A.border}`,background:form.inStock===v?A.accentBg:A.bgElevated,cursor:"pointer",flex:1,justifyContent:"center",fontSize:12,fontWeight:form.inStock===v?600:400,color:form.inStock===v?A.accent:A.textMid}}>
                      <input type="radio" name="is" checked={form.inStock===v} onChange={()=>upd("inStock",v)} style={{accentColor:A.accent}}/>{l}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div style={{background:A.bgCard,borderRadius:16,padding:22,border:`1px solid ${A.border}`,boxShadow:A.shadow}}>
            <h2 style={{fontSize:14,fontWeight:700,marginBottom:16}}>Product Image</h2>
            <label style={lStyle}>Product Image</label>
            <div style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
              <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 14px",background:A.accent,color:"white",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600}}>
                📷 Upload Image
                <input type="file" accept="image/*" style={{display:"none"}} onChange={async(e)=>{
                  const file = e.target.files[0];
                  if(!file) return;
                  upd("imageUploading", true);
                  const fd = new FormData();
                  fd.append("image", file);
                  try {
                    const res = await fetch(`${API}/upload`, {method:"POST",headers:{"x-admin-key":"axxess_admin_2025_secure"},body:fd});
                    const data = await res.json();
                    if(data.url){upd("image",data.url);upd("imageUploading",false);}
                    else{alert("Upload failed: "+(data.error||"Unknown error"));upd("imageUploading",false);}
                  } catch(err){alert("Upload error: "+err.message);upd("imageUploading",false);}
                }}/>
              </label>
              {form.imageUploading && <span style={{fontSize:12,color:A.textLight}}>⏳ Uploading...</span>}
              {form.image && !form.imageUploading && <span style={{fontSize:11,color:"#16a34a",fontWeight:600}}>✅ Image ready</span>}
            </div>
            <input value={form.image} onChange={e=>upd("image",e.target.value)} placeholder="Or paste image URL here..." style={{...fStyle,fontSize:12}}/>
            {form.image&&<img src={form.image} alt="preview" style={{width:"100%",height:130,objectFit:"cover",borderRadius:7,marginTop:10,border:`1px solid ${A.border}`}} onError={e=>e.target.style.display="none"}/>}
          </div>
          <button onClick={save} style={{background:A.accent,color:"white",border:"none",padding:"10px",borderRadius:8,fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:14,cursor:"pointer"}}>
            {isEdit?"💾 Update Product":"✅ Save Product"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TrackingForm({ orderId, onSave }) {
  const ADMIN_KEY = "axxess_admin_2025_secure";
  const [form, setForm] = useState({ courierName:"", trackingNumber:"", trackingUrl:"" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const fStyle = { width:"100%", padding:"7px 10px", border:`1.5px solid ${A.border}`, borderRadius:6, fontSize:12, fontFamily:"'DM Sans',sans-serif", background:A.bgElevated, marginBottom:6 };

  const COURIERS = ["Delhivery","Shiprocket","DTDC","BlueDart","Ekart","India Post","Xpressbees","Shadow Fax"];

  const save = async () => {
    if (!form.courierName || !form.trackingNumber) { alert("Courier name and tracking number required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "x-admin-key": ADMIN_KEY },
        body: JSON.stringify({ ...form, status: "Shipped" }),
      });
      if (!res.ok) throw new Error("Failed to save");
      onSave(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <select value={form.courierName} onChange={e=>setForm(f=>({...f,courierName:e.target.value}))} style={fStyle}>
        <option value="">Select courier...</option>
        {COURIERS.map(c => <option key={c} value={c}>{c}</option>)}
        <option value="Other">Other</option>
      </select>
      <input value={form.trackingNumber} onChange={e=>setForm(f=>({...f,trackingNumber:e.target.value}))}
        placeholder="AWB / Tracking number" style={fStyle}/>
      <input value={form.trackingUrl} onChange={e=>setForm(f=>({...f,trackingUrl:e.target.value}))}
        placeholder="Tracking URL (optional)" style={{...fStyle,marginBottom:8}}/>
      <button onClick={save} disabled={saving}
        style={{padding:"7px 14px",background:"linear-gradient(135deg,#667eea,#764ba2)",color:"white",border:"none",borderRadius:8,boxShadow:"0 4px 12px rgba(102,126,234,0.3)",fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:12,cursor:"pointer"}}>
        {saving?"Saving...":saved?"✅ Saved!":"🚚 Add Tracking & Mark Shipped"}
      </button>
    </div>
  );
}

function AdminOrders({ orders, setOrders, onStatusChange }) {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const fmta = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  const filtered = orders.filter(o => (filter === "All" || o.status === filter) && (!search || o.customer?.toLowerCase().includes(search.toLowerCase()) || o.id?.toLowerCase().includes(search.toLowerCase())));
  return (
    <div>
      <div style={{marginBottom:22}}><h1 style={{fontSize:22,fontWeight:800,color:A.text}}>Orders</h1><p style={{color:A.textLight,fontSize:12,marginTop:2}}>{orders.length} total orders</p></div>
      <div style={{display:"flex",gap:8,marginBottom:18,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{display:"flex",gap:6}}>
          {["All","Pending","Shipped","Delivered","Cancelled"].map(s=>(
            <button key={s} onClick={()=>setFilter(s)} style={{padding:"6px 14px",borderRadius:7,border:`1.5px solid ${filter===s?A.accent:A.border}`,background:filter===s?A.accentBg:A.bgCard,color:filter===s?A.accent:A.textMid,cursor:"pointer",fontSize:12,fontFamily:"'DM Sans',sans-serif",fontWeight:filter===s?600:400}}>
              {s}
            </button>
          ))}
        </div>
        <div style={{position:"relative",marginLeft:"auto"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..." style={{padding:"7px 12px 7px 33px",border:`1.5px solid ${A.border}`,borderRadius:7,fontSize:12,background:A.bgCard,width:200,fontFamily:"'DM Sans',sans-serif"}}/>
          <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:A.textLight,fontSize:12}}>🔍</span>
        </div>
      </div>
      <div style={{background:A.bgCard,borderRadius:16,border:`1px solid ${A.border}`,boxShadow:A.shadow,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:A.bgElevated}}>{["Order","Customer & Address","Items","Total","Payment","Status","Date","Action"].map(h=><th key={h} style={{padding:"10px 13px",textAlign:"left",fontSize:10,fontWeight:700,color:A.textLight,textTransform:"uppercase",letterSpacing:0.6,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.length===0?<tr><td colSpan={8} style={{padding:"36px",textAlign:"center",color:A.textLight}}>No orders found.</td></tr>:
            filtered.map((o,i)=>(
              <>
                <tr key={o.id} className="rh" style={{borderTop:`1px solid ${A.border}`,background:A.bgCard,cursor:"pointer"}} onClick={()=>setExpanded(expanded===o.id?null:o.id)}>
                  <td style={{padding:"11px 13px",fontSize:12,fontWeight:700,color:A.accent}}>{o.id?.slice(-8) || "—"}</td>
                  <td style={{padding:"11px 13px"}}>
                    <div style={{fontSize:12,fontWeight:600}}>{o.customer}</div>
                    <div style={{fontSize:10,color:A.textLight}}>📞 {o.phone}</div>
                    <div style={{fontSize:10,color:A.textLight}}>📍 {o.city}, {o.state} - {o.pincode}</div>
                  </td>
                  <td style={{padding:"11px 13px",fontSize:11,color:A.textMid,maxWidth:130}}>{o.items}</td>
                  <td style={{padding:"11px 13px",fontSize:12,fontWeight:700}}>{fmta(o.total)}</td>
                  <td style={{padding:"11px 13px",fontSize:11,color:A.textMid,textTransform:"uppercase"}}>{o.payment}</td>
                  <td style={{padding:"11px 13px"}}><span style={{...STATUS_COLORS[o.status],padding:"2px 9px",borderRadius:50,fontSize:10,fontWeight:700,whiteSpace:"nowrap"}}>{o.status}</span></td>
                  <td style={{padding:"11px 13px",fontSize:10,color:A.textLight,whiteSpace:"nowrap"}}>{o.date}</td>
                  <td style={{padding:"11px 13px"}}>
                    <select value={o.status} onChange={e=>{e.stopPropagation();onStatusChange(o.id,e.target.value);setOrders(prev=>prev.map(q=>q.id===o.id?{...q,status:e.target.value}:q));}} onClick={e=>e.stopPropagation()} style={{padding:"4px 7px",border:`1px solid ${A.border}`,borderRadius:5,fontSize:11,color:A.text,background:A.bgElevated,fontFamily:"'DM Sans',sans-serif",cursor:"pointer"}}>
                      {["Pending","Confirmed","Shipped","Delivered","Cancelled"].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
                {expanded===o.id && (
                  <tr key={o.id+"_exp"} style={{background:"#f0f4ff",borderTop:`1px solid ${A.border}`}}>
                    <td colSpan={8} style={{padding:"14px 20px"}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
                        <div>
                          <div style={{fontSize:10,fontWeight:700,color:A.textLight,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>📦 Shipping Address</div>
                          <div style={{fontSize:13,fontWeight:600,color:A.text}}>{o.customer}</div>
                          <div style={{fontSize:12,color:A.textMid}}>{o.address}</div>
                          <div style={{fontSize:12,color:A.textMid}}>{o.city}, {o.state} - {o.pincode}</div>
                          <div style={{fontSize:12,color:A.textMid,marginTop:2}}>📞 {o.phone}</div>
                          {o.email && <div style={{fontSize:12,color:A.textMid}}>✉️ {o.email}</div>}
                        </div>
                        <div>
                          <div style={{fontSize:10,fontWeight:700,color:A.textLight,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>🛒 Order Items</div>
                          {o.items?.split(",").map((item,idx)=>(
                            <div key={idx} style={{fontSize:12,color:A.textMid,marginBottom:2}}>• {item.trim()}</div>
                          ))}
                          <div style={{fontSize:12,color:A.textMid,marginTop:8}}>Method: <strong>{o.payment?.toUpperCase()}</strong></div>
                          <div style={{fontSize:12,color:A.textMid}}>Total: <strong>{fmta(o.total)}</strong></div>
                          {o.notes && <div style={{fontSize:12,color:A.textMid,marginTop:4}}>Notes: {o.notes}</div>}
                        </div>
                        <div>
                          <div style={{fontSize:10,fontWeight:700,color:A.textLight,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>🚚 Tracking Info</div>
                          {o.trackingNumber ? (
                            <div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"10px 12px",marginBottom:8}}>
                              <div style={{fontSize:12,fontWeight:700,color:"#16a34a"}}>✅ Tracking Added</div>
                              <div style={{fontSize:12,color:"#15803d"}}>Courier: {o.courierName}</div>
                              <div style={{fontSize:12,color:"#15803d"}}>AWB: {o.trackingNumber}</div>
                              {o.trackingUrl && <a href={o.trackingUrl} target="_blank" rel="noreferrer" style={{fontSize:11,color:"#1d4ed8"}}>Track Package →</a>}
                            </div>
                          ) : (
                            <div style={{fontSize:11,color:A.textLight,marginBottom:8}}>No tracking added yet</div>
                          )}
                          <TrackingForm orderId={o.id} onSave={(tracking) => {
                            setOrders(prev => prev.map(q => q.id === o.id ? {...q, ...tracking} : q));
                          }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ADMIN BRAND MANAGER ──────────────────────────────────────────────────────
function AdminBrands() {
  const ADMIN_KEY = "axxess_admin_2025_secure";
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState({ name: "", country: "🇮🇳 India", active: true });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const countries = ["🇮🇳 India", "🇯🇵 Japan", "🇳🇱 Netherlands", "🇩🇪 Germany", "🇺🇸 USA", "🇨🇳 China", "🇰🇷 Korea"];
  const fStyle = { width:"100%", padding:"9px 12px", border:`1.5px solid ${A.border}`, borderRadius:7, fontSize:13, color:A.text, background:A.bgElevated, fontFamily:"'DM Sans',sans-serif" };

  useEffect(() => {
    fetch(`${API}/brands`, { headers: { "x-admin-key": ADMIN_KEY } })
      .then(r => r.json())
      .then(data => { setBrands(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const addBrand = async () => {
    if (!newBrand.name.trim()) { alert("Brand name is required."); return; }
    try {
      const res = await fetch(`${API}/brands`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-key": ADMIN_KEY },
        body: JSON.stringify(newBrand),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setBrands(prev => [...prev, data]);
      setNewBrand({ name: "", country: "🇮🇳 India", active: true });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch(e) { alert(e.message); }
  };

  const toggleBrand = async (id, active) => {
    await fetch(`${API}/brands/${id}`, { method:"PUT", headers:{"Content-Type":"application/json","x-admin-key":ADMIN_KEY}, body:JSON.stringify({ active }) });
    setBrands(prev => prev.map(b => b._id === id ? { ...b, active } : b));
  };

  const deleteBrand = async (id) => {
    if (!window.confirm("Remove this brand?")) return;
    await fetch(`${API}/brands/${id}`, { method:"DELETE", headers:{"x-admin-key":ADMIN_KEY} });
    setBrands(prev => prev.filter(b => b._id !== id));
  };

  return (
    <div>
      <div style={{ marginBottom:22 }}>
        <h1 style={{ fontSize:22, fontWeight:800, color:A.text }}>Brand Management</h1>
        <p style={{ color:A.textLight, fontSize:13, marginTop:3 }}>Add or manage brands. Active brands appear on the website.</p>
      </div>
      {saved && <div style={{ background:"#dcfce7", border:"1px solid #86efac", borderRadius:8, padding:"10px 16px", marginBottom:16, color:A.green, fontWeight:600, fontSize:13 }}>✅ Brand added!</div>}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:20 }}>
        {/* Brand list */}
        <div style={{ background:A.bgCard, borderRadius:16, border:`1px solid ${A.border}`, boxShadow:A.shadow, overflow:"hidden" }}>
          <div style={{ padding:"14px 18px", borderBottom:`1px solid ${A.border}`, display:"flex", justifyContent:"space-between" }}>
            <span style={{ fontWeight:700, fontSize:14, color:A.text }}>Your Brands ({brands.length})</span>
            <span style={{ fontSize:12, color:A.textLight }}>{brands.filter(b=>b.active).length} active</span>
          </div>
          {loading ? <div style={{ padding:32, textAlign:"center", color:A.textLight }}>Loading...</div> :
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{background:A.bgElevated}}>{["Brand Name","Country","Status","Action"].map(h=><th key={h} style={{ padding:"10px 16px", textAlign:"left", fontSize:10, fontWeight:700, color:A.textLight, textTransform:"uppercase", letterSpacing:0.6 }}>{h}</th>)}</tr></thead>
            <tbody>
              {brands.map((b) => (
                <tr key={b._id} style={{ borderTop:`1px solid ${A.border}`, background:A.bgCard }}>
                  <td style={{ padding:"12px 16px", fontWeight:700, fontSize:13, color:A.text }}>{b.name}</td>
                  <td style={{ padding:"12px 16px", fontSize:13, color:A.textMid }}>{b.country}</td>
                  <td style={{ padding:"12px 16px" }}>
                    <button onClick={() => toggleBrand(b._id, !b.active)} style={{ padding:"3px 10px", borderRadius:50, border:"none", cursor:"pointer", fontSize:10, fontWeight:700, background:b.active?A.greenBg:"#fee2e2", color:b.active?A.green:A.red, fontFamily:"'DM Sans',sans-serif" }}>
                      {b.active ? "● Active" : "● Inactive"}
                    </button>
                  </td>
                  <td style={{ padding:"12px 16px" }}>
                    <button onClick={() => deleteBrand(b._id)} style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${A.border}`, background:A.bgCard, cursor:"pointer", fontSize:11, fontWeight:600, color:A.red, fontFamily:"'DM Sans',sans-serif" }}>🗑️ Remove</button>
                  </td>
                </tr>
              ))}
              {brands.length === 0 && <tr><td colSpan={4} style={{ padding:32, textAlign:"center", color:A.textLight }}>No brands yet. Add one!</td></tr>}
            </tbody>
          </table>}
        </div>
        {/* Add brand form */}
        <div style={{ background:A.bgCard, borderRadius:16, padding:22, border:`1px solid ${A.border}`, boxShadow:A.shadow, height:"fit-content" }}>
          <h2 style={{ fontSize:15, fontWeight:700, color:A.text, marginBottom:18 }}>➕ Add New Brand</h2>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:A.textMid, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:0.5 }}>Brand Name *</label>
              <input value={newBrand.name} onChange={e=>setNewBrand(p=>({...p,name:e.target.value}))} placeholder="e.g. Philips" style={fStyle} />
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:A.textMid, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:0.5 }}>Country of Origin</label>
              <select value={newBrand.country} onChange={e=>setNewBrand(p=>({...p,country:e.target.value}))} style={fStyle}>
                {countries.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button onClick={addBrand} style={{ background:"linear-gradient(135deg,#667eea,#764ba2)", color:"white", border:"none", padding:"11px", borderRadius:10, fontWeight:700, fontSize:14, cursor:"pointer", boxShadow:"0 4px 12px rgba(102,126,234,0.3)" }}>
              ✅ Add Brand
            </button>
          </div>
          <div style={{ marginTop:16, padding:"11px 13px", background:"#f0f9ff", borderRadius:8, fontSize:12, color:"#0369a1" }}>
            💡 Brands are saved to database and persist across sessions.
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminCoupons() {
  const ADMIN_KEY = "axxess_admin_2025_secure";
  const fmta = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code:"", type:"percent", value:"", minOrder:"", maxDiscount:"", usageLimit:"", expiresAt:"" });
  const [saved, setSaved] = useState(false);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const fStyle = { width:"100%", padding:"9px 12px", border:`1.5px solid ${A.border}`, borderRadius:7, fontSize:13, color:A.text, background:A.bgElevated, fontFamily:"'DM Sans',sans-serif" };
  const lStyle = { fontSize:11, fontWeight:700, color:A.textMid, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:0.5 };

  useEffect(() => {
    fetch(`${API}/coupons`, { headers: { "x-admin-key": ADMIN_KEY } })
      .then(r => r.json()).then(setCoupons).catch(() => {});
  }, []);

  const createCoupon = async () => {
    if (!form.code || !form.value) { alert("Code and value required"); return; }
    try {
      const res = await fetch(`${API}/coupons`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-key": ADMIN_KEY },
        body: JSON.stringify({ ...form, value: Number(form.value), minOrder: Number(form.minOrder)||0, maxDiscount: Number(form.maxDiscount)||0, usageLimit: Number(form.usageLimit)||0 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCoupons(prev => [data, ...prev]);
      setForm({ code:"", type:"percent", value:"", minOrder:"", maxDiscount:"", usageLimit:"", expiresAt:"" });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch(e) { alert(e.message); }
  };

  const toggleCoupon = async (id, active) => {
    await fetch(`${API}/coupons/${id}`, { method:"PUT", headers:{"Content-Type":"application/json","x-admin-key":ADMIN_KEY}, body:JSON.stringify({ active }) });
    setCoupons(prev => prev.map(c => c._id === id ? { ...c, active } : c));
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await fetch(`${API}/coupons/${id}`, { method:"DELETE", headers:{"x-admin-key":ADMIN_KEY} });
    setCoupons(prev => prev.filter(c => c._id !== id));
  };

  return (
    <div>
      <div style={{marginBottom:22}}><h1 style={{fontSize:22,fontWeight:800,color:A.text}}>Coupons</h1><p style={{color:A.textLight,fontSize:12,marginTop:2}}>Create and manage discount codes</p></div>
      {saved && <div style={{background:A.greenBg,border:"1px solid rgba(34,197,94,0.3)",borderRadius:9,padding:"11px 16px",marginBottom:18,color:A.green,fontWeight:600,fontSize:13}}>✅ Coupon created!</div>}

      {/* Create Coupon Form */}
      <div style={{background:A.bgCard,borderRadius:16,padding:24,border:`1px solid ${A.border}`,boxShadow:A.shadow,marginBottom:24}}>
        <h2 style={{fontSize:15,fontWeight:800,marginBottom:18}}>Create New Coupon</h2>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
          <div><label style={lStyle}>Coupon Code *</label><input value={form.code} onChange={e=>upd("code",e.target.value.toUpperCase())} placeholder="e.g. AXXESS10" style={fStyle}/></div>
          <div><label style={lStyle}>Type</label>
            <select value={form.type} onChange={e=>upd("type",e.target.value)} style={fStyle}>
              <option value="percent">Percentage (%)</option>
              <option value="fixed">Fixed Amount (₹)</option>
            </select>
          </div>
          <div><label style={lStyle}>Value * {form.type==="percent"?"(%)":"(₹)"}</label><input value={form.value} onChange={e=>upd("value",e.target.value)} placeholder={form.type==="percent"?"e.g. 10":"e.g. 100"} type="number" style={fStyle}/></div>
          <div><label style={lStyle}>Min Order (₹)</label><input value={form.minOrder} onChange={e=>upd("minOrder",e.target.value)} placeholder="e.g. 500 (0 = no min)" type="number" style={fStyle}/></div>
          <div><label style={lStyle}>Max Discount (₹)</label><input value={form.maxDiscount} onChange={e=>upd("maxDiscount",e.target.value)} placeholder="e.g. 200 (0 = no cap)" type="number" style={fStyle}/></div>
          <div><label style={lStyle}>Usage Limit</label><input value={form.usageLimit} onChange={e=>upd("usageLimit",e.target.value)} placeholder="e.g. 100 (0 = unlimited)" type="number" style={fStyle}/></div>
          <div><label style={lStyle}>Expires On</label><input value={form.expiresAt} onChange={e=>upd("expiresAt",e.target.value)} type="date" style={fStyle}/></div>
        </div>
        <button onClick={createCoupon} style={{marginTop:16,padding:"10px 24px",background:"linear-gradient(135deg,#667eea,#764ba2)",color:"white",border:"none",borderRadius:10,fontWeight:700,fontSize:14,cursor:"pointer",boxShadow:"0 4px 12px rgba(102,126,234,0.3)"}}>
          🎟️ Create Coupon
        </button>
      </div>

      {/* Coupons List */}
      <div style={{background:A.bgCard,borderRadius:16,border:`1px solid ${A.border}`,boxShadow:A.shadow,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:A.bgElevated}}>{["Code","Type","Value","Min Order","Usage","Expires","Status","Actions"].map(h=><th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,fontWeight:700,color:A.textLight,textTransform:"uppercase",letterSpacing:0.6}}>{h}</th>)}</tr></thead>
          <tbody>
            {coupons.length===0 ? <tr><td colSpan={8} style={{padding:36,textAlign:"center",color:A.textLight}}>No coupons yet. Create one above!</td></tr> :
            coupons.map((c,i) => (
              <tr key={c._id} style={{borderTop:`1px solid ${A.border}`,background:A.bgCard}}>
                <td style={{padding:"11px 14px",fontWeight:800,fontSize:13,color:A.accent,letterSpacing:1}}>{c.code}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:A.textMid}}>{c.type==="percent"?"Percentage":"Fixed"}</td>
                <td style={{padding:"11px 14px",fontSize:13,fontWeight:700}}>{c.type==="percent"?`${c.value}%`:fmta(c.value)}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:A.textMid}}>{c.minOrder>0?fmta(c.minOrder):"None"}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:A.textMid}}>{c.usedCount}{c.usageLimit>0?`/${c.usageLimit}`:""}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:A.textMid}}>{c.expiresAt?new Date(c.expiresAt).toLocaleDateString("en-IN"):"Never"}</td>
                <td style={{padding:"11px 14px"}}>
                  <button onClick={()=>toggleCoupon(c._id,!c.active)} style={{padding:"3px 10px",borderRadius:50,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,background:c.active?A.greenBg:A.redBg,color:c.active?A.green:A.red}}>
                    {c.active?"● Active":"● Inactive"}
                  </button>
                </td>
                <td style={{padding:"11px 14px"}}>
                  <button onClick={()=>deleteCoupon(c._id)} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${A.border}`,background:A.redBg,cursor:"pointer",fontSize:11,fontWeight:600,color:A.red,fontFamily:"'DM Sans',sans-serif"}}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch(`${API}/contact`)
      .then(r => r.json())
      .then(data => { setMessages(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    try {
      await fetch(`${API}/contact/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ read: true }) });
      setMessages(prev => prev.map(m => m._id === id ? { ...m, read: true } : m));
    } catch (e) {}
  };

  const filtered = messages.filter(m => {
    if (filter === "unread" && m.read) return false;
    if (filter === "read" && !m.read) return false;
    if (search && !m.name.toLowerCase().includes(search.toLowerCase()) && !m.message.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ fontSize: 23, fontWeight: 800, color: A.text }}>Customer Messages</h1>
            <p style={{ color: A.textLight, fontSize: 12, marginTop: 2 }}>
              {messages.length} total &nbsp;·&nbsp;
              <span style={{ color: unreadCount > 0 ? "#f97316" : A.green, fontWeight: 700 }}>{unreadCount} unread</span>
            </p>
          </div>
          {unreadCount > 0 && (
            <button onClick={() => messages.filter(m => !m.read).forEach(m => markRead(m._id))}
              style={{ background: A.accent, color: A.bgCard, border: "none", padding: "8px 16px", borderRadius: 8, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
              Mark All Read
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        {["all", "unread", "read"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ padding: "6px 14px", borderRadius: 7, border: `1.5px solid ${filter === f ? A.accent : A.border}`, background: filter === f ? A.accentBg : A.bgCard, color: filter === f ? A.accent : A.textMid, cursor: "pointer", fontSize: 12, fontFamily: "'DM Sans',sans-serif", fontWeight: filter === f ? 600 : 400, textTransform: "capitalize" }}>
            {f}
          </button>
        ))}
        <div style={{ position: "relative", marginLeft: "auto" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search messages..."
            style={{ padding: "7px 12px 7px 32px", border: `1.5px solid ${A.border}`, borderRadius: 7, fontSize: 12, background: A.bgCard, width: 200, fontFamily: "'DM Sans',sans-serif" }} />
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: A.textLight, fontSize: 12 }}>🔍</span>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: A.textLight }}>Loading messages...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: A.textLight }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>💬</div>
          <p style={{ fontSize: 14 }}>{search || filter !== "all" ? "No messages found" : "No messages yet"}</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((m) => (
            <div key={m._id} style={{ background:A.bgCard,borderRadius:16, padding: "18px 20px", border: `1px solid ${m.read ? A.border : "#f97316"}`, boxShadow: A.shadow, position: "relative", borderLeft: `4px solid ${m.read ? A.border : "#f97316"}` }}>
              {!m.read && (
                <span style={{ position: "absolute", top: 14, right: 14, background: "#f97316", color: A.bgCard, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>NEW</span>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: m.read ? A.bg : "#fff7ed", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                    {m.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: A.text }}>{m.name}</div>
                    {m.phone && <div style={{ fontSize: 12, color: A.textMid, marginTop: 1 }}>📞 {m.phone}</div>}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: A.textLight, textAlign: "right" }}>
                  {new Date(m.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  <br/>
                  {new Date(m.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              <p style={{ fontSize: 13.5, color: A.text, lineHeight: 1.65, background:A.bgElevated, padding:"12px 14px", borderRadius: 8, margin: "0 0 12px" }}>{m.message}</p>
              <div style={{ display: "flex", gap: 8 }}>
                {m.phone && (
                  <a href={`https://wa.me/91${m.phone.replace(/\D/g,"")}?text=Hi ${m.name}! Thank you for contacting Axxess. `} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#25D366", color: A.bgCard, padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: "none", fontFamily: "'DM Sans',sans-serif" }}>
                    💬 Reply on WhatsApp
                  </a>
                )}
                {m.phone && (
                  <a href={`tel:${m.phone}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, background: A.accentBg, color: A.accent, padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: "none", fontFamily: "'DM Sans',sans-serif" }}>
                    📞 Call
                  </a>
                )}
                {!m.read && (
                  <button onClick={() => markRead(m._id)}
                    style={{ background:A.bgElevated, color:A.textMid, border: `1px solid ${A.border}`, padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                    ✓ Mark Read
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminSidebar({ tab, setTab, onLogout, onRefresh, refreshing }) {
  const nav = [
    { key:"dashboard", label:"Dashboard",   icon:"🏠" },
    { key:"orders",    label:"Orders",       icon:"📋" },
    { key:"products",  label:"Products",     icon:"🛍️" },
    { key:"addproduct",label:"Add Product",  icon:"➕" },
    { key:"coupons",   label:"Coupons",      icon:"🎟️" },
    { key:"brands",    label:"Brands",       icon:"🏷️" },
    { key:"messages",  label:"Messages",     icon:"💬" },
  ];
  return (
    <div style={{ width:220, minHeight:"100vh", background:A.sidebar, display:"flex", flexDirection:"column", position:"fixed", left:0, top:0, bottom:0, zIndex:100, borderRight:`1px solid ${A.border}`, boxShadow:"2px 0 20px rgba(0,0,0,0.3)" }}>
      {/* Brand */}
      <div style={{ padding:"22px 20px 18px", borderBottom:`1px solid ${A.border}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:40, height:40, background:"linear-gradient(135deg,#667eea,#764ba2)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, boxShadow:"0 4px 12px rgba(102,126,234,0.4)" }}>
            <span style={{ fontSize:20 }}>⚡</span>
          </div>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:A.text, letterSpacing:-0.3 }}>Axxess</div>
            <div style={{ fontSize:11, color:A.textLight, fontWeight:500 }}>Admin Panel</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex:1, padding:"14px 10px", display:"flex", flexDirection:"column", gap:2, overflowY:"auto" }}>
        <div style={{ fontSize:10, fontWeight:600, color:A.textLight, textTransform:"uppercase", letterSpacing:1.2, padding:"4px 10px 10px" }}>Main Menu</div>
        {nav.map(({ key, label, icon }) => {
          const active = tab === key;
          return (
            <button key={key} onClick={() => setTab(key)} style={{
              display:"flex", alignItems:"center", gap:12, padding:"10px 12px",
              borderRadius:10, border:"none", cursor:"pointer", width:"100%", textAlign:"left",
              background: active ? A.accentBg : "transparent",
              color: active ? A.accent : A.textMid,
              fontSize:13, fontWeight: active ? 600 : 400,
              transition:"all 0.15s",
            }}
            onMouseOver={e => { if (!active) { e.currentTarget.style.background = A.bgHover; e.currentTarget.style.color = A.text; }}}
            onMouseOut={e => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = A.textMid; }}}>
              <span style={{ fontSize:16, width:24, textAlign:"center", flexShrink:0 }}>{icon}</span>
              <span>{label}</span>
              {active && <div style={{ marginLeft:"auto", width:6, height:6, borderRadius:"50%", background:A.accent }}/>}
            </button>
          );
        })}
      </nav>

      {/* Bottom */}
      <div style={{ padding:"12px 10px", borderTop:`1px solid ${A.border}`, display:"flex", flexDirection:"column", gap:4 }}>
        <button onClick={onRefresh} disabled={refreshing} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:10, border:"none", background:"transparent", color:A.textMid, fontSize:12, fontWeight:500, cursor:"pointer", width:"100%", transition:"all 0.15s" }}
          onMouseOver={e => { e.currentTarget.style.background = A.bgHover; e.currentTarget.style.color = A.text; }}
          onMouseOut={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = A.textMid; }}>
          <span style={{ display:"inline-block", animation:refreshing?"spin 1s linear infinite":"none", fontSize:14 }}>🔄</span>
          {refreshing ? "Refreshing..." : "Refresh Data"}
        </button>
        <button onClick={onLogout} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:10, border:"none", background:"transparent", color:A.red, fontSize:12, fontWeight:500, cursor:"pointer", width:"100%", transition:"all 0.15s" }}
          onMouseOver={e => { e.currentTarget.style.background = A.redBg; }}
          onMouseOut={e => { e.currentTarget.style.background = "transparent"; }}>
          <span style={{ fontSize:14 }}>🚪</span> Sign Out
        </button>
      </div>
    </div>
  );
}

function AdminApp() {
  const [loggedIn, setLoggedIn] = useState(() => sessionStorage.getItem("axxess_admin") === "true");
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editProd, setEditProd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([{ id:1, name:"Panasonic", country:"🇯🇵 Japan", active:true }]);
  const [refreshing, setRefreshing] = useState(false);

  const fetchProducts = () => {
    fetch(`${API}/products`)
      .then(r => r.json())
      .then(data => { setProducts(data.map(p => ({ ...p, id: p._id }))); setLoading(false); })
      .catch(() => { setProducts(ADMIN_PRODUCTS_INIT); setLoading(false); });
  };

  const ADMIN_KEY = "axxess_admin_2025_secure";

  const fetchOrders = () => {
    fetch(`${API}/orders`, { headers: { "x-admin-key": ADMIN_KEY } })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.orders || []);
        setOrders(list.map(o => ({ 
          ...o, 
          id: o._id, 
          date: new Date(o.createdAt).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }), 
          itemsDisplay: o.items?.map(i => `${i.name} x${i.qty}`).join(", ") || "—",
          items: o.items?.map(i => `${i.name} x${i.qty}`).join(", ") || "—",
        })));
      })
      .catch(() => setOrders(ADMIN_ORDERS_INIT));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchProducts(), fetchOrders()]);
    setTimeout(() => setRefreshing(false), 800);
  };

  const handleLogin = () => {
    sessionStorage.setItem("axxess_admin", "true");
    setLoggedIn(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("axxess_admin");
    setLoggedIn(false);
  };

  useEffect(() => { if (loggedIn) { fetchProducts(); fetchOrders(); } }, [loggedIn]);

  const handleSaveProduct = async (product, isEdit) => {
    try {
      const url = isEdit ? `${API}/products/${product._id || product.id}` : `${API}/products`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json", "x-admin-key": ADMIN_KEY }, body: JSON.stringify(product) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert("Failed to save: " + (err.error || res.status));
        return;
      }
      const saved = await res.json();
      console.log("Saved product:", saved);
      fetchProducts();
    } catch (e) { alert("Failed to save product. Is the backend running?"); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      const res = await fetch(`${API}/products/${id}`, { method: "DELETE", headers: { "x-admin-key": ADMIN_KEY } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert("Failed to delete: " + (err.error || res.status));
        return;
      }
      fetchProducts();
    } catch (e) {
      alert("Cannot connect to backend. Make sure your backend server is running on port 5001.");
    }
  };

  const handleUpdateOrderStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/orders/${id}`, { 
        method: "PUT", 
        headers: { "Content-Type": "application/json", "x-admin-key": ADMIN_KEY }, 
        body: JSON.stringify({ status }) 
      });
      if (!res.ok) {
        const err = await res.json();
        alert("Failed to update: " + (err.error || res.status));
        return;
      }
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
    } catch (e) { 
      alert("Failed to update order: " + e.message); 
    }
  };

  if (!loggedIn) return <><style>{acss}</style><div className="aj"><AdminLogin onLogin={handleLogin} /></div></>;
  if (loading) return <div className="aj" style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",fontSize:16,color:"#64748b"}}>Loading data from MongoDB...</div>;

  return (
    <div className="aj">
      <AdminSidebar tab={tab} setTab={setTab} onLogout={handleLogout} onRefresh={handleRefresh} refreshing={refreshing} />
      <div className="admin-content" style={{ marginLeft: 220, padding: "28px 32px", minHeight: "100vh", background: A.bg }}>
        {tab === "dashboard" && <AdminDashboard products={products} orders={orders} setTab={setTab} />}
        {tab === "products" && <AdminProducts products={products} setProducts={setProducts} setTab={setTab} setEditProd={setEditProd} onDelete={handleDeleteProduct} />}
        {tab === "orders" && <AdminOrders orders={orders} setOrders={setOrders} onStatusChange={handleUpdateOrderStatus} />}
        {tab === "addproduct" && <AdminAddProduct products={products} setProducts={setProducts} setTab={setTab} editProd={editProd} setEditProd={setEditProd} onSave={handleSaveProduct} />}
        {tab === "brands" && <AdminBrands />}
        {tab === "coupons" && <AdminCoupons />}
        {tab === "messages" && <AdminMessages />}
      </div>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────

export default function App() {
  const getInitialPage = () => {
    if (typeof window === "undefined") return "home";
    if (window.location.pathname === "/admin") return "admin";
    const path = window.location.pathname.replace(/^\//, "") || "home";
    const validPages = ["home", "products", "cart", "checkout", "wishlist", "contact", "about", "detail", "login", "account", "return-policy"];
    if (validPages.includes(path)) return path;
    // Handle product URLs like /products/panasonic-ac-300ca-abc123
    if (path.startsWith("products/")) return "detail";
    return "home";
  };

  const [page, setPage] = useState(getInitialPage);
  const [selected, setSelected] = useState(() => {
    // Restore selected product from sessionStorage on refresh
    try {
      const saved = sessionStorage.getItem("axxess_selected");
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [search, setSearch] = useState("");
  const [activeBrand, setActiveBrand] = useState("All");

  // ── Browser back/forward button support ──────────────────────────
  const navigate = (newPage, opts = {}) => {
    let url;
    if (newPage === "home") url = "/";
    else if (newPage === "detail" && opts.selected) url = productPath(opts.selected);
    else url = "/" + newPage;
    window.history.pushState({ page: newPage }, "", url);
    setPage(newPage);
    if (opts.selected !== undefined) {
      setSelected(opts.selected);
      try { sessionStorage.setItem("axxess_selected", JSON.stringify(opts.selected)); } catch {}
    }
    window.scrollTo(0, 0);
  };

  // Register navigate globally so all components can use it
  _navigateFn = navigate;

  useEffect(() => {
    const handlePop = (e) => {
      const state = e.state;
      if (state && state.page) {
        setPage(state.page);
        window.scrollTo(0, 0);
      } else {
        setPage("home");
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener("popstate", handlePop);

    const path = window.location.pathname.replace(/^\//, "") || "home";
    const validPages = ["home", "products", "cart", "checkout", "wishlist", "contact", "about", "detail", "login", "account", "return-policy"];
    const initialPage = validPages.includes(path) ? path : (path.startsWith("products/") ? "detail" : "home");

    // If refreshed on detail page, restore selected from sessionStorage
    if (initialPage === "detail") {
      try {
        const saved = sessionStorage.getItem("axxess_selected");
        if (saved) {
          setSelected(JSON.parse(saved));
          setPage("detail");
        } else {
          setPage("home"); // No product saved, go home
        }
      } catch {
        setPage("home");
      }
    } else {
      setPage(initialPage);
    }

    window.history.replaceState({ page: initialPage }, "", window.location.pathname);

    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  if (window.location.pathname === "/admin") return <AdminApp />;
  if (page === "admin") return <AdminApp />;

  const setPageWithHistory = (newPage, opts = {}) => navigate(newPage, opts);

  return (
    <AuthProvider>
    <CartProvider>
      <style>{css}</style>
      <Navbar page={page} setPage={setPageWithHistory} searchQuery={search} setSearchQuery={setSearch} activeBrand={activeBrand} setActiveBrand={setActiveBrand} />
      <Toast />
      <WAFloat />
      <MobileBottomNav page={page} setPage={setPageWithHistory} activeBrand={activeBrand} setActiveBrand={setActiveBrand} />
      {page === "home" && <HomePage setPage={setPageWithHistory} setSelectedProduct={(p) => { setSelected(p); try { sessionStorage.setItem("axxess_selected", JSON.stringify(p)); } catch {} }} activeBrand={activeBrand} setActiveBrand={setActiveBrand} />}
      {page === "products" && <ProductsPage setPage={setPageWithHistory} setSelectedProduct={(p) => { setSelected(p); try { sessionStorage.setItem("axxess_selected", JSON.stringify(p)); } catch {} }} searchQuery={search} activeBrand={activeBrand} setActiveBrand={setActiveBrand} />}
      {page === "detail" && selected && <ProductDetail product={selected} setPage={setPageWithHistory} />}
      {page === "cart" && <CartPage setPage={setPageWithHistory} />}
      {page === "checkout" && <CheckoutPage setPage={setPageWithHistory} />}
      {page === "wishlist" && <WishlistPage setPage={setPageWithHistory} setSelectedProduct={(p) => { setSelected(p); try { sessionStorage.setItem("axxess_selected", JSON.stringify(p)); } catch {} }} />}
      {page === "contact" && <ContactPage />}
      {page === "about" && <AboutPage />}
      {page === "return-policy" && <ReturnPolicyPage />}
      {page === "login" && <LoginPage setPage={setPageWithHistory} />}
      {page === "account" && <AccountPage setPage={setPageWithHistory} />}
      {typeof window !== "undefined" && (window.__admin = () => setPage("admin"))}
    </CartProvider>
    </AuthProvider>
  );
}